import type { JsonApiError } from './types';
import { moduleName } from '@/shared/lib/tenantModules';
import { systemName } from '@/shared/lib/externalSystemName';
import { GROUP_SCOPE_REQUISITI } from '@/shared/lib/groupScope';
import { taskFieldLabel } from '@/shared/lib/taskFieldLabels';

/**
 * Codici applicativi noti del BE (presenti in `error.meta.code` o `error.code`).
 * v1.8.0 ha introdotto la famiglia LICENSE_* e le transizioni subscription/holding.
 */
export type ApiErrorCode =
  | 'LICENSE_NOT_IMPLEMENTED_YET'
  | 'LICENSE_REQUIRED'
  | 'LICENSE_EXCLUDED'
  | 'LICENSE_NOT_AVAILABLE_AS_ADDON'
  | 'MISSING_PREREQUISITE'
  | 'SUBSCRIPTION_ALREADY_ACTIVE'
  | 'INVALID_TRANSITION'
  | 'CYCLE_DETECTED'
  | 'CROSS_TENANT_DENIED_CLAIM'
  | 'CROSS_TENANT_DENIED_NOT_DESCENDANT'
  | 'MASTER_TENANT_READ_ONLY'
  // Integrazione calendario (PLANNING v2 §10.2)
  | 'INVALID_CALENDAR_PROVIDER'
  | 'CALENDAR_INTEGRATION_DISABLED'
  | 'CALENDAR_PROVIDER_NOT_CONFIGURED'
  | 'CALENDAR_PROVIDER_NOT_IMPLEMENTED'
  // Regole della pianificazione (PLANNING v2 §6)
  | 'PLANNING_POLICY_NOT_RESOLVED'
  | 'PLANNING_POLICY_OVERRIDE_NOT_ALLOWED'
  | 'TENANT_ENTRY_DENIED'
  | 'QUOTA_EXCEEDED'
  | 'USER_NOT_FOUND'
  | 'EMPLOYEE_TENANT_NOT_HOSTED_IN_SITE'
  | 'FLAG_ALREADY_ASSIGNED_TO_OTHER_SITE'
  | 'MAPPING_ALREADY_EXISTS'
  | 'LAST_OWNER_CANNOT_BE_REMOVED'
  // ERP v2 (29 giu 2026): catalogo meta.code su 409/422 applicativi
  | 'DUPLICATE_RESOURCE'
  | 'DUPLICATE_SHIFT_ASSIGNMENT'
  | 'DUPLICATE_VAT_NUMBER'
  | 'DUPLICATE_SITE_WORKING_TIME'
  | 'INVALID_EMPLOYEE_TYPE'
  | 'INVALID_RELATIONSHIP_STATE'
  // Allineamento v1.20 (Sprint 1 / working-times / mapping sedi)
  | 'INVALID_ATTRIBUTE_VALUE'
  | 'DUPLICATE_SITE_TENANT_MAPPING'
  | 'INVALID_DAILY_WORKING_MINUTES'
  | 'CROSS_GROUP_LINK_DENIED'
  | 'NOT_AN_INVITATION_OTP'
  // CRM Client (BE v1.20.37) e tenant-attributes (BE v1.20.38)
  | 'CLIENT_DUPLICATE_IN_PORTFOLIO'
  | 'DUPLICATE_ATTRIBUTE_KEY'
  | 'INVALID_VALUE_FOR_TYPE'
  | 'INVALID_VALUE_TYPE'
  | 'MISSING_ATTRIBUTE_KEY'
  // Modulo Progetti (BE v1.21/v1.22)
  | 'INVALID_STATUS_TRANSITION'
  | 'INVALID_STATUS_VALUE'
  | 'CHANGE_ORDER_REQUIRED'
  | 'DUPLICATE_PROJECT_CODE'
  | 'HAS_DEPENDENCIES'
  | 'DUPLICATE_TEAM_MEMBER'
  | 'HOUR_CAP_EXCEEDED'
  | 'SCOPE_NOT_ENFORCEABLE'
  | 'ALREADY_PROCESSED'
  | 'USE_APPROVE_ENDPOINT'
  | 'EXTERNALLY_MANAGED'
  // Politica di sincronizzazione `read-only` (EXTERNAL-SYNC-POLICY-v2 §1.6): 422 su PATCH/DELETE di un
  // campo governato dal gestionale esterno — dal 16-10-2026 anche dal connettore MaeWay v8.
  | 'EXTERNAL_SYNC_LOCKED'
  // Comandi dei connettori (BE-TO-FE-CONNETTORE-MAEWAY-V8-v1 §3.5)
  | 'CONNECTOR_OPERATION_UNKNOWN'
  | 'CONNECTOR_PARAMS_INVALID'
  | 'CONNECTOR_COMMAND_SUPERADMIN_ONLY'
  | 'CONNECTOR_DISABLED'
  | 'CONNECTOR_COMMAND_PENDING'
  | 'CONNECTOR_COMMAND_IMMUTABLE'
  | 'CONNECTOR_COMMAND_STARTED'
  | 'CONNECTOR_SYSTEM_UNKNOWN'
  | 'EMAIL_ROUTING_INVALID'
  | 'RETAINER_AMOUNT_REQUIRED'
  | 'ACTIVITY_REASON_REQUIRED'
  // Group Resource Pool (BE v1.23/v1.24/v1.25)
  | 'CROSS_TENANT_DENIED_GROUP'
  // Gerarchia HR (`HierarchyValidator`, `CrossTenantAssignmentResourceDefinitions`)
  | 'HIERARCHY_CYCLE'
  | 'SUBORDINATE_ALREADY_HAS_MANAGER'
  | 'EMPLOYEE_IS_HIERARCHY_ROOT'
  | 'EMPLOYEE_IS_SUBORDINATE'
  // Unicità per azienda (BE-RESPONSE-TO-FE-UNICITA-DIPENDENTI-v1 §3, task 1981)
  | 'EMPLOYEE_TAX_CODE_TAKEN'
  | 'EMPLOYEE_EMAIL_TAKEN'
  | 'EMPLOYEE_IDENTITY_DUPLICATES_PRESENT'
  | 'INVALID_COST_SPLIT'
  | 'INVALID_ALLOCATION_PERCENT'
  // Estensioni configurazione per-tenant (BE v1.23.0 — tenant-config extensions)
  | 'DUPLICATE_PROJECT_TYPE_PRESET'
  | 'TARDINESS_POLICY_SCOPE_INVALID'
  | 'TARDINESS_POLICY_TENANT_MISMATCH'
  | 'DUPLICATE_TARDINESS_DEFAULT'
  | 'TARDINESS_POLICY_NOT_RESOLVED'
  | 'EMPLOYEE_NOT_FOUND'
  | 'EMPLOYEE_ID_REQUIRED'
  | 'DUPLICATE_PROJECT_ATTRIBUTE_DEFINITION'
  | 'PROJECT_ATTRIBUTE_ENUM_INVALID'
  | 'DUPLICATE_PROJECT_ATTRIBUTE_VALUE'
  | 'PROJECT_ATTRIBUTE_TENANT_MISMATCH'
  // Visibilità di gruppo holding sulle config entities (BE v1.24.0 — holding-inheritance)
  | 'INVALID_SCOPE'
  | 'GROUP_SCOPE_DENIED'
  | 'GROUP_RECORD_READONLY'
  | 'PROJECT_ATTRIBUTE_HAS_GROUP_VALUES'
  | 'PROJECT_CATEGORY_HAS_GROUP_REFERENCES'
  // Workflow changelog + notifiche (BE-DONE-CHANGELOG-WORKFLOW-v1)
  | 'INVALID_CHANGELOG_TRANSITION'
  | 'INVALID_CHANGELOG_STATUS'
  | 'INVALID_CHANGELOG_VERSION'
  | 'DUPLICATE_CHANGELOG_VERSION'
  | 'CHANGELOG_SCOPE_DENIED'
  | 'INVALID_NOTIFICATION_CHANNEL'
  | 'INVALID_NOTIFICATION_TARGET'
  // Gestionale Sprint (BE-DONE-GESTIONALE-SPRINT-v1)
  | 'INVALID_PRODUCT_TYPE'
  | 'INVALID_SPRINT_PRIORITY'
  | 'INVALID_SPRINT_QUARTER'
  | 'INVALID_SPRINT_STATE'
  | 'DUPLICATE_SPRINT_MONTH'
  | 'DUPLICATE_SPRINT_TASK'
  | 'DUPLICATE_SPRINT_PROGRESS'
  | 'DUPLICATE_SPRINT_MEMBER'
  | 'PRODUCT_HAS_TASKS'
  | 'SPRINT_HAS_PROGRESS'
  | 'SPRINT_LOCKED'
  | 'SPRINT_ADMIN_REQUIRED'
  | 'PRODUCT_SCOPE_DENIED'
  | 'PROJECT_TENANT_MISMATCH'
  | 'SPRINT_TASK_NOT_FOUND'
  | 'SPRINT_NOT_FOUND'
  // Pianificazione attività (PLANNING v2): l'immutabilità del piano consumato è un invariante
  // di integrità, non un permesso — vale anche per il superadmin.
  | 'PLANNING_ENTRY_CONSUMED'
  | 'PLANNING_ENTRY_TASK_MISMATCH'
  | 'PLANNING_WRITE_NOT_ALLOWED'
  | 'PLANNING_INVALID_PLANNED_MINUTES'
  | 'PLANNING_SERIES_NOT_FOUND'
  // Pacchetto HR — modulo LEAVE (assenze/ferie v1)
  | 'LEAVE_INVALID_STATUS_TRANSITION'
  | 'LEAVE_INVALID_DATE_RANGE'
  | 'LEAVE_INVALID_UNIT_OF_MEASURE'
  | 'LEAVE_INVALID_PART_OF_DAY'
  | 'LEAVE_INVALID_PRIORITY'
  | 'LEAVE_TYPE_REQUIRED'
  | 'LEAVE_TYPE_INACTIVE'
  | 'LEAVE_REASON_REQUIRED'
  | 'LEAVE_REASON_TYPE_MISMATCH'
  | 'LEAVE_REASON_INACTIVE'
  | 'LEAVE_REASON_NOT_REJECTABLE'
  | 'LEAVE_INVALID_ACCRUAL_POLICY'
  | 'LEAVE_INVALID_CARRY_OVER_POLICY'
  | 'LEAVE_INVALID_DOCUMENT_TYPE'
  | 'LEAVE_INVALID_DOCUMENT_SUBMISSION_MODE'
  | 'LEAVE_INVALID_AUTO_APPROVE_POLICY'
  | 'LEAVE_TYPE_CODE_DUPLICATE'
  | 'LEAVE_REASON_CODE_DUPLICATE'
  | 'LEAVE_BALANCE_READ_ONLY'
  | 'LEAVE_EVENT_READ_ONLY'
  | 'LEAVE_ATTACHMENT_TYPE_MISMATCH'
  | 'LEAVE_ATTACHMENT_LOCKED'
  | 'LEAVE_ATTACHMENT_DELETE_REASON_REQUIRED'
  | 'LEAVE_INVALID_ELIGIBILITY'
  | 'LEAVE_CROSS_YEAR_HOURS_FALLBACK'
  | 'LEAVE_DAY_CANCELLATION_REASON_REQUIRED'
  | 'LEAVE_PARTIAL_CANCELLATION_NOT_ALLOWED'
  | 'LEAVE_DAY_NOT_FOUND'
  | 'LEAVE_DAY_ALREADY_CANCELLED'
  | 'LEAVE_CANNOT_CANCEL_PAST_DAY'
  | 'LEAVE_DAY_INVALID_DATE_RANGE'
  | 'LEAVE_INVALID_ELIGIBILITY_MODE'
  | 'LEAVE_INVALID_REQUIRED_PROTECTED_STATUS_TYPE'
  | 'LEAVE_INVALID_REQUIRED_PROTECTED_STATUS_SUBTYPE'
  | 'LEAVE_ELIGIBILITY_MODE_REQUIRES_STATUS_TYPES'
  | 'JOB_UNKNOWN'
  | 'JOB_PARAM_MISSING'
  | 'JOB_PARAM_INVALID'
  | 'JOB_ALREADY_RUNNING'
  | 'JOB_NOT_MANUALLY_RUNNABLE'
  | 'JOB_DRY_RUN_NOT_SUPPORTED'
  | 'LEAVE_BLACKOUT_INVALID_ENFORCEMENT'
  | 'LEAVE_BLACKOUT_PERIOD'
  | 'LEAVE_RETROACTIVE_DENIED'
  | 'LEAVE_ADVANCE_NOTICE_INSUFFICIENT'
  | 'LEAVE_HANDOVER_REQUIRED'
  | 'LEAVE_SUBSTITUTE_REQUIRED'
  | 'LEAVE_INSUFFICIENT_BALANCE'
  | 'LEAVE_REJECTION_REASON_REQUIRED'
  | 'LEAVE_NOT_FOUND'
  | 'LEAVE_FORBIDDEN'
  | 'LEAVE_APPROVAL_DELEGATION_MISSING'
  | 'LEAVE_DOCUMENT_CODE_MISSING'
  | 'LEAVE_DOCUMENT_REQUIRED'
  | 'LEAVE_DOCUMENT_BOTH_REQUIRED'
  | 'LEAVE_INVALID_CERTIFICATE_CODE_FORMAT'
  | 'LEAVE_TEAM_OVERLAP'
  // APPROVAL-WORKFLOW-CONFIG (BE v1.76)
  | 'LEAVE_ADMIN_APPROVAL_NOT_REQUIRED'
  | 'LEAVE_INVALID_APPROVAL_WORKFLOW'
  // SKIP-NO-ACCRUAL (BE v1.77)
  | 'LEAVE_EVENT_DURATION_EXCEEDED'
  // DOCUMENT-GRACE (BE v1.77)
  | 'LEAVE_DOCUMENT_OVERDUE_WITHOUT_WAIVER'
  | 'LEAVE_DOCUMENT_WAIVER_REASON_REQUIRED'
  | 'LEAVE_TYPE_INVALID_DOCUMENT_GRACE_DAYS'
  | 'NO_EMPLOYEE_PROFILE'
  // Pacchetto HR — modulo TIMESHEET-REPORTING v2
  | 'TIMESHEET_TIMER_DISABLED'
  | 'TIMESHEET_INVALID_TIMER_TRANSITION'
  | 'TIMESHEET_TIMER_ALREADY_RUNNING'
  | 'TIMESHEET_NOT_FOUND'
  | 'TIMESHEET_FORBIDDEN'
  | 'TIMESHEET_PERIOD_LOCKED'
  | 'TIMESHEET_OVERRIDE_REASON_REQUIRED'
  | 'TIMESHEET_EXPENSES_DISABLED'
  | 'TIMESHEET_BUSINESS_TRIPS_DISABLED'
  | 'TIMESHEET_DRAFT_INCOMPLETE'
  | 'TIMESHEET_TEMPLATE_NOT_FOUND'
  | 'TIMESHEET_INVALID_DATE_RANGE'
  | 'TIMESHEET_TEMPLATE_INACTIVE'
  | 'TIMESHEET_TEMPLATE_CUSTOM_NOT_SUPPORTED'
  | 'TIMESHEET_TEMPLATE_OUT_OF_VALIDITY'
  | 'TIMESHEET_TEMPLATE_INVALID_RECURRENCE'
  | 'TIMESHEET_LOCK_FORBIDDEN'
  | 'TIMESHEET_LOCK_NOT_FOUND'
  | 'TIMESHEET_UNLOCK_REASON_REQUIRED'
  | 'TIMESHEET_LOCK_ALREADY_UNLOCKED'
  | 'TIMESHEET_LOCK_OVERLAP'
  | 'TIMESHEET_INVALID_WEEK_START'
  | 'TIMESHEET_ATTRIBUTE_INVALID_DATATYPE'
  | 'TIMESHEET_ATTRIBUTE_VALUE_INVALID'
  | 'TIMESHEET_TASK_REQUIRED'
  | 'TIMESHEET_TASK_NOT_ASSIGNED'
  | 'SHIFT_GENERATE_OVERWRITE_REMOVED'
  | 'SHIFT_ROTATION_INACTIVE'
  | 'SETTING_KEY_IMMUTABLE'
  | 'TIMESHEET_OVERRIDE_EVENT_READ_ONLY'
  // Pacchetto HR — modulo SMART-WORKING v2
  | 'SMART_WORKING_NOT_FOUND'
  | 'SMART_WORKING_FORBIDDEN'
  | 'OBLIGATION_PERIOD_LOCKED'
  | 'SMART_WORKING_INVALID_STATUS_TRANSITION'
  | 'SMART_WORKING_APPROVAL_DELEGATION_MISSING'
  | 'SMART_WORKING_REJECTION_REASON_REQUIRED'
  | 'SMART_WORKING_EMPLOYEE_TERMINATED'
  | 'SMART_WORKING_RETROACTIVE_DENIED'
  | 'SMART_WORKING_LEAVE_CONFLICT'
  | 'SMART_WORKING_HOLIDAY_DAY'
  | 'SMART_WORKING_POLICY_ADVANCE_INSUFFICIENT'
  | 'SMART_WORKING_POLICY_WEEKDAY_NOT_ALLOWED'
  | 'SMART_WORKING_POLICY_CONSECUTIVE_EXCEEDED'
  | 'SMART_WORKING_POLICY_WEEK_EXCEEDED'
  | 'SMART_WORKING_POLICY_MONTH_EXCEEDED'
  | 'SMART_WORKING_HANDOVER_REQUIRED'
  | 'SMART_WORKING_SUBSTITUTE_REQUIRED'
  | 'SMART_WORKING_BLACKOUT_PERIOD'
  | 'SMART_WORKING_QUORUM_VIOLATION'
  | 'SMART_WORKING_QUORUM_WARNING'
  | 'SMART_WORKING_INVALID_DATE_RANGE'
  | 'SMART_WORKING_INVALID_PART_OF_DAY'
  | 'SMART_WORKING_INVALID_POLICY_SCOPE'
  | 'SMART_WORKING_MULTIPLE_DEFAULTS'
  | 'SMART_WORKING_BLACKOUT_INVALID_ENFORCEMENT'
  | 'SMART_WORKING_ATTACHMENT_TYPE_MISMATCH'
  | 'SMART_WORKING_ATTACHMENT_LOCKED'
  | 'SMART_WORKING_BALANCE_READ_ONLY'
  | 'SMART_WORKING_EVENT_READ_ONLY'
  | 'EMPLOYEE_WORK_LOCATION_CHANGE_READ_ONLY'
  | 'SMART_WORKING_PLANNING_NOT_FOUND'
  | 'SMART_WORKING_PLANNING_INVALID_STATUS_TRANSITION'
  | 'SMART_WORKING_PLANNING_HORIZON_EXCEEDED'
  | 'SMART_WORKING_PLANNING_EMPTY'
  | 'SMART_WORKING_PLANNING_ROWS_BLOCKED'
  | 'SMART_WORKING_PLANNING_REQUESTS_NOT_DRAFT'
  | 'SMART_WORKING_PLANNING_DUPLICATE_DATE'
  // Documenti PDF generati dal sistema (verbali, rapportini, gantt — BE v1.268.0)
  | 'PDF_SERVICE_UNAVAILABLE'
  | 'PDF_RENDER_FAILED'
  | 'GANTT_PDF_INVALID'
  | 'VPS_DISPATCH_NOT_ACKNOWLEDGED'
  // Service Report (rapportini d'intervento)
  | 'SERVICE_REPORT_NOT_FOUND'
  | 'SERVICE_REPORT_INVALID_STATUS_TRANSITION'
  | 'SERVICE_REPORT_CONFIG_NOT_FOUND'
  | 'SERVICE_REPORT_INVALID_CHANNEL'
  | 'SERVICE_REPORT_ACKNOWLEDGE_FORBIDDEN'
  | 'SERVICE_REPORT_CANCEL_REASON_REQUIRED'
  | 'SERVICE_REPORT_PLACEHOLDER_UNRESOLVED'
  | 'SERVICE_REPORT_STATUS_INVALID_CATEGORY'
  | 'SERVICE_REPORT_STATUS_CODE_DUPLICATE'
  | 'SERVICE_REPORT_INVALID_TRIGGER'
  | 'SERVICE_REPORT_TEMPLATE_INVALID'
  | 'SERVICE_REPORT_MULTIPLE_DEFAULTS'
  | 'SERVICE_REPORT_AGGREGATION_NOT_SUPPORTED'
  | 'SERVICE_REPORT_CANNOT_DELETE_SENT'
  // Meeting Room (sale riunioni)
  | 'MEETING_ROOM_BOOKING_NOT_FOUND'
  | 'MEETING_ROOM_APPROVE_FORBIDDEN'
  | 'MEETING_ROOM_BOOKING_INVALID_STATUS'
  | 'MEETING_ROOM_CANCELLATION_REASON_REQUIRED'
  | 'MEETING_ROOM_PARTICIPANT_NOT_FOUND'
  | 'MEETING_ROOM_RESPOND_FORBIDDEN'
  | 'MEETING_ROOM_PARTICIPANT_INVALID_RESPONSE'
  | 'MEETING_ROOM_INVALID_CAPACITY'
  | 'MEETING_ROOM_BOOKING_INVALID_DATE_RANGE'
  | 'MEETING_ROOM_BOOKING_INVALID_ATTENDEE_COUNT'
  | 'MEETING_ROOM_BOOKING_INVALID_LAYOUT'
  | 'MEETING_ROOM_CAPACITY_EXCEEDED'
  | 'MEETING_ROOM_BOOKING_CONFLICT'
  | 'MEETING_ROOM_BOOKING_FORBIDDEN'
  | 'MEETING_ROOM_CATERING_NOTES_REQUIRED'
  | 'MEETING_ROOM_SETUP_NOTES_REQUIRED'
  | 'MEETING_ROOM_BOOKING_RECURRENCE_PATTERN_REQUIRED'
  | 'MEETING_ROOM_PARTICIPANT_INVALID_ROLE'
  // Fleet (flotta mezzi)
  | 'FLEET_VEHICLE_INVALID_LICENSE_PLATE'
  | 'FLEET_VEHICLE_LICENSE_PLATE_DUPLICATE'
  | 'FLEET_VEHICLE_INVALID_CATEGORY'
  | 'FLEET_VEHICLE_INVALID_FUEL_TYPE'
  | 'FLEET_VEHICLE_INVALID_OWNERSHIP'
  | 'FLEET_VEHICLE_BLOCKED_REASON_REQUIRED'
  | 'FLEET_ASSIGNMENT_TYPE_INVALID'
  | 'FLEET_ASSIGNMENT_INVALID_DATE_RANGE'
  | 'FLEET_ASSIGNMENT_BENEFIT_AMOUNT_REQUIRED'
  | 'FLEET_INSURANCE_INVALID_COVERAGE'
  | 'FLEET_INSURANCE_INVALID_DATE_RANGE'
  | 'FLEET_INSURANCE_MULTIPLE_ACTIVE'
  | 'FLEET_INSPECTION_INVALID_RESULT'
  | 'FLEET_INSPECTION_INVALID_DATE_RANGE'
  | 'FLEET_MAINTENANCE_INVALID_TYPE'
  | 'FLEET_COST_INVALID_TYPE'
  | 'FLEET_COST_INVALID_PERIOD'
  | 'FLEET_INCIDENT_INVALID_TYPE'
  | 'FLEET_INCIDENT_SEVERITY_INVALID'
  | 'FLEET_INCIDENT_INVALID_STATUS'
  | 'FLEET_INCIDENT_DAMAGE_AREA_INVALID'
  | 'FLEET_INCIDENT_BOOKING_NOT_FOUND'
  | 'FLEET_INCIDENT_BOOKING_VEHICLE_MISMATCH'
  | 'FLEET_TRAFFIC_VIOLATION_INVALID_TYPE'
  | 'FLEET_TRAFFIC_VIOLATION_DISPUTE_REASON_REQUIRED'
  // Travel (trasferte)
  | 'TRAVEL_BOOKING_NOT_FOUND'
  | 'TRAVEL_BOOKING_FORBIDDEN'
  | 'TRAVEL_BOOKING_INVALID_STATUS_TRANSITION'
  | 'TRAVEL_BOOKING_REJECTION_REASON_REQUIRED'
  | 'TRAVEL_BOOKING_CANCELLATION_REASON_REQUIRED'
  | 'TRAVEL_BOOKING_CONFIRMATION_REFERENCE_REQUIRED'
  | 'TRAVEL_QUOTE_NOT_FOUND'
  | 'TRAVEL_BOOKING_INVALID_TYPE'
  | 'TRAVEL_BOOKING_INVALID_PAYER_TYPE'
  | 'TRAVEL_BOOKING_INVALID_PRIORITY'
  | 'TRAVEL_BOOKING_INVALID_CURRENCY'
  | 'TRAVEL_BOOKING_INVALID_DATE_RANGE'
  | 'TRAVEL_BOOKING_INVALID_AMOUNT'
  | 'TRAVEL_BOOKING_PURPOSE_REQUIRED'
  | 'TRAVEL_QUOTE_INVALID_AMOUNT'
  | 'TRAVEL_QUOTE_INVALID_CURRENCY'
  | 'TRAVEL_LINK_INVALID_AMOUNT'
  | 'TRAVEL_LINK_BOOKING_NOT_CONFIRMED'
  | 'TRAVEL_POLICY_INVALID_TIER'
  | 'TRAVEL_POLICY_TIER_INVALID_DATE_RANGE'
  | 'TRAVEL_POLICY_TIER_OVERLAP'
  | 'TRAVEL_BOOKING_PARTICIPANT_INVALID_AMOUNT'
  // Clock-in / Presenze enterprise (event model)
  | 'ATTENDANCE_INVALID_EVENT'
  | 'ATTENDANCE_INVALID_WORK_LOCATION'
  | 'ATTENDANCE_INVALID_METHOD'
  | 'ATTENDANCE_SITE_REQUIRED'
  | 'ATTENDANCE_TRIP_LOCATION_REQUIRED'
  | 'ATTENDANCE_METHOD_METADATA_MISSING'
  | 'ATTENDANCE_INVALID_SEQUENCE'
  | 'ATTENDANCE_LEAVE_DAY'
  | 'ATTENDANCE_OVERRIDE_FORBIDDEN'
  | 'ATTENDANCE_SESSION_READ_ONLY'
  | 'ATTENDANCE_SOURCE_FORBIDDEN'
  | 'ATTENDANCE_SOURCE_INVALID_TYPE_METADATA'
  // LEAVE conformance (BE v1.48–v1.50)
  | 'LEAVE_COMMENT_FORBIDDEN'
  | 'LEAVE_REASON_FORBIDDEN'
  | 'LEAVE_INVALID_CATEGORY'
  | 'LEAVE_EVENT_LIMIT_EXCEEDED'
  | 'LEAVE_EMPLOYEE_TERMINATED'
  // Timesheet + activity-reason enforcement (BE v1.54–v1.55)
  | 'TIMESHEET_ATTRIBUTE_REQUIRED'
  | 'TIMESHEET_EXPORT_FORMAT_UNSUPPORTED'
  | 'REASON_REQUIRED_FOR_NON_BILLABLE'
  | 'REASON_REQUIRES_PROJECT'
  | 'REASON_REQUIRES_JUSTIFICATION'
  | 'REASON_INACTIVE'
  // Causali v1.101.0: la causale marcata non rendicontabile blocca create E patch
  | 'REASON_BLOCKS_SAVE'
  | 'VPS_ZERO_BILLING_HOURS'
  // Mezzo proprio e rimborso km (BE v1.100.0 — TRIP-VEHICLE §3/§5/§6)
  | 'TIMESHEET_OWN_VEHICLE_REQUIRED'
  | 'TIMESHEET_OWN_VEHICLE_DISABLED'
  | 'TIMESHEET_INVALID_TRIP_VEHICLE_MODE'
  | 'TIMESHEET_INVALID_KM_PAYER'
  | 'TIMESHEET_INVALID_DISTANCE'
  | 'OWN_VEHICLE_AUTHORIZATION_FORBIDDEN'
  | 'OWN_VEHICLE_PLATE_REQUIRED'
  | 'OWN_VEHICLE_INVALID_CATEGORY'
  | 'OWN_VEHICLE_INVALID_FUEL_TYPE'
  | 'MILEAGE_RATE_OVERLAP'
  | 'MILEAGE_RATE_AMBIGUOUS'
  | 'MILEAGE_RATE_INVALID_CATEGORY'
  | 'MILEAGE_RATE_INVALID_CURRENCY'
  | 'MILEAGE_RATE_INVALID_AMOUNT'
  | 'MILEAGE_RATE_INVALID_DATE_RANGE'
  // Bucket A — gate sede di lavoro (BE v1.51)
  | 'EMPLOYEE_WORK_LOCATION_FORBIDDEN'
  // Vehicle-booking enterprise (BE v1.60 — FLEET §1.3/§2.1)
  | 'FLEET_BOOKING_NOT_FOUND'
  | 'FLEET_BOOKING_FORBIDDEN'
  | 'FLEET_BOOKING_INVALID_DATE_RANGE'
  | 'FLEET_BOOKING_INVALID_PRIORITY'
  | 'FLEET_BOOKING_INVALID_STATUS_CATEGORY'
  | 'FLEET_BOOKING_REJECTION_REASON_REQUIRED'
  | 'FLEET_BOOKING_VEHICLE_REQUIRED'
  | 'FLEET_VEHICLE_NOT_FOUND'
  | 'FLEET_VEHICLE_BLOCKED'
  | 'FLEET_BOOKING_OVERLAP'
  | 'FLEET_BOOKING_OVERLAP_OVERRIDDEN'
  | 'FLEET_INSURANCE_EXPIRED'
  | 'FLEET_INSURANCE_EXPIRING_DURING_BOOKING'
  | 'FLEET_INSPECTION_EXPIRED'
  | 'FLEET_INSPECTION_EXPIRING_DURING_BOOKING'
  | 'FLEET_BOOKING_PARTICIPANTS_EXCEED_SEATS'
  | 'FLEET_BOOKING_START_DATA_REQUIRED'
  | 'FLEET_BOOKING_INVALID_FUEL_LEVEL'
  | 'FLEET_BOOKING_END_DATA_REQUIRED'
  | 'FLEET_BOOKING_ODOMETER_NOT_MONOTONE'
  | 'FLEET_BOOKING_ODOMETER_INCONSISTENT'
  | 'FLEET_BOOKING_CANCELLATION_REASON_REQUIRED'
  | 'FLEET_BOOKING_INVALID_STATUS_TRANSITION'
  | 'FLEET_BOOKING_PARTICIPANT_DUPLICATE'
  | 'FLEET_BOOKING_PARTICIPANT_IS_REQUESTER'
  | 'FLEET_BOOKING_PARTICIPANT_INVALID_ROLE'
  // Allegati delle anomalie veicolo (BE v1.107.0 — INCIDENT-PHOTOS)
  | 'FLEET_INCIDENT_ATTACHMENT_TYPE_INVALID'
  // Giustificativi e nota spese (BE v1.121.1 — EXPENSE-REIMBURSEMENT)
  | 'INVALID_ATTACHMENT_TYPE'
  | 'INVALID_ATTACHMENT_AMOUNT'
  | 'TRAVEL_ATTACHMENT_BOOKING_REQUIRED'
  | 'TRAVEL_ATTACHMENT_FILE_REQUIRED'
  | 'FLEET_BOOKING_INVALID_PAYER_TYPE'
  | 'FLEET_BOOKING_DAY_INVALID_AMOUNT'
  | 'EXPENSE_REPORT_ALREADY_EXISTS'
  | 'EXPENSE_REPORT_NOT_EDITABLE'
  | 'EXPENSE_REPORT_EMPTY'
  | 'EXPENSE_REPORT_INVALID_TRANSITION'
  | 'EXPENSE_MISSING_JUSTIFICATION'
  | 'ENTRY_LOCKED_BY_EXPENSE_REPORT'
  | 'EXPENSE_REPORT_NOT_APPROVER'
  | 'EXPENSE_REPORT_PAY_FORBIDDEN'
  | 'EXPENSE_REPORT_REJECTION_REASON_REQUIRED'
  | 'EXPENSE_REPORT_PAYROLL_PERIOD_REQUIRED'
  | 'EXPENSE_REPORT_DISABLED'
  | 'EXPENSE_REPORT_READ_ONLY'
  | 'EXPENSE_REPORT_FORBIDDEN'
  | 'EXPENSE_REPORT_NOT_FOUND'
  | 'EXPENSE_REPORT_EMPLOYEE_REQUIRED'
  | 'EXPENSE_REPORT_INVALID_PERIOD'
  | 'EXPENSE_JUSTIFICATION_THRESHOLD_INVALID'
  | 'EXPENSE_AUTO_COMPOSE_DAY_INVALID'
  // Sorgenti di timbratura (BE CLOCK-IN §W1). `ATTENDANCE_SOURCE_FORBIDDEN` è già dichiarato
  // più su, col resto del vocabolario presenze.
  | 'ATTENDANCE_REVOKE_REASON_REQUIRED'
  | 'ATTENDANCE_SOURCE_ALREADY_REVOKED'
  | 'ATTENDANCE_NFC_TAG_ALREADY_REGISTERED'
  // Timbratura da tag/QR (BE addendum CLOCK-IN-PAYLOAD del 12-08-2026). Il primo arriva con
  // status **401** e un `meta.reason`: è l'unico 401 che NON è una sessione scaduta.
  | 'ATTENDANCE_NFC_SIGNATURE_INVALID'
  | 'ATTENDANCE_SOURCE_NOT_FOUND'
  // Coda di approvazione: 403 se dichiari un approvatore diverso da te (audit sicurezza 12-08-2026).
  | 'PENDING_APPROVAL_FORBIDDEN'
  // ORDERS-LEGACY-PARITY (BE v1.104.0): monte ore d'ordine, voci da ordine, articolo da fatturare
  | 'ORDER_LINE_HOURS_EXCEEDED'
  | 'ORDER_LINE_CONSUMED'
  | 'ORDER_OVERRUN_REASON_REQUIRED'
  | 'ORDER_LINE_NOT_FOUND'
  | 'PROJECT_NEXT_SELF_REFERENCE'
  | 'PROJECT_NEXT_CYCLE_DETECTED'
  | 'SUB_ACTIVITY_PENDING_APPROVAL'
  | 'SUB_ACTIVITY_NOT_ASSIGNED'
  | 'BILLING_SERVICE_ITEM_UNRESOLVED'
  /**
   * ⚠️ **Avviso su 2xx**, non un errore: arriva in `meta.warnings` (vedi `warnings.ts`). La
   * riga d'ordine è passata a `consumed` — quella transizione resta — ma la copia del team
   * sulla commessa successiva è fallita.
   */
  | 'ORDER_NEXT_PROJECT_TEAM_COPY_FAILED'
  | 'SUB_ACTIVITY_REQUIRED'
  // Albero e ciclo di vita delle voci di attività (ProjectSubActivityResourceDefinition)
  | 'SUB_ACTIVITY_SELF_REFERENCE'
  | 'SUB_ACTIVITY_PARENT_NOT_FOUND'
  | 'SUB_ACTIVITY_PARENT_PROJECT_MISMATCH'
  | 'SUB_ACTIVITY_PARENT_INACTIVE'
  | 'SUB_ACTIVITY_CYCLE_DETECTED'
  | 'SUB_ACTIVITY_HAS_CHILDREN'
  | 'SUB_ACTIVITY_HAS_TIMESHEETS'
  | 'SUB_ACTIVITY_NOT_FOUND'
  | 'SUB_ACTIVITY_APPROVE_FORBIDDEN'
  | 'SUB_ACTIVITY_IDS_REQUIRED'
  | 'SUB_ACTIVITY_PROJECT_MISMATCH'
  | 'SUB_ACTIVITY_INACTIVE'
  // Consultazione dei log (BE v1.117.0 — BE-DONE-LOGS-API-v1 §5)
  | 'LOG_INVALID_RANGE'
  | 'LOG_RANGE_TOO_WIDE'
  | 'LOG_INVALID_STATUS_CLASS'
  | 'LOG_INVALID_METHOD'
  | 'LOG_TENANT_OUT_OF_SCOPE'
  | 'LOG_TRACE_ID_REQUIRED'
  | 'LOG_TRACE_NOT_FOUND'
  | 'LOG_TENANT_UNRESOLVED'
  | 'LOG_STORE_UNAVAILABLE'
  | 'LOG_PRESET_NAME_REQUIRED'
  | 'LOG_PRESET_NOT_OWNER'
  // Il permesso che manca, e il perimetro che manca: due rifiuti diversi sulla stessa pagina
  | 'CLAIM_MISSING'
  | 'TASK_PERIMETER_REQUIRED'
  // La rotta della scadenza efficace per intervallo (BE v1.212.0, task 1874)
  | 'TASK_EFFECTIVE_DUE_RANGE_REQUIRED'
  // Il cancello dei moduli per azienda (BE-CONTRACT-TENANT-MODULES-v1, 11-09-2026): 403 su ogni
  // rotta di un modulo spento, per chiunque. `SUPPORT_MODULE_DISABLED` è il 422 della sola porta
  // anonima `POST /support-intakes/open`, che applica da sé il gate sul tenant dell'argomento.
  | 'MODULE_DISABLED'
  | 'SUPPORT_MODULE_DISABLED'
  // Chi accende i moduli (BE-RESPONSE-TO-FE-MODULI-v1, 26-10-2026): l'interruttore solo la
  // piattaforma, la restrizione di gruppo la capogruppo che amministra la controllata.
  | 'MODULE_MANAGED_BY_PLATFORM'
  | 'MODULE_MANAGED_BY_PARENT'
  | 'MODULE_RESTRICTED_BY_ANCESTOR'
  | 'MODULE_RESTRICTION_NO_PARENT'
  | 'MODULE_UNKNOWN'
  | 'MISSING_MODULES'
  | 'MISSING_TENANT_ID'
  | 'TENANT_NOT_FOUND'
  | 'CROSS_TENANT_DENIED'
  | 'TENANT_NOT_ADMINISTRABLE'
  // MaeSpace (BE v1.242.0 / v1.244.0): «Apri in MaeSpace» e la stanza della commessa
  | 'MAESPACE_DISABLED'
  | 'MAESPACE_SSO_NOT_ALLOWED'
  | 'MAESPACE_SSO_IMPERSONATION'
  | 'MAESPACE_NO_USER'
  | 'MAESPACE_NO_EMAIL'
  | 'MAESPACE_USER_NOT_PROVISIONED'
  | 'MAESPACE_UNAVAILABLE'
  | 'MAESPACE_UNREACHABLE'
  | 'MAESPACE_BAD_RESPONSE'
  | 'MAESPACE_ERROR'
  | 'MAESPACE_NOT_A_MEMBER'
  | 'MAESPACE_ROOM_NOT_READY'
  | 'MAESPACE_FILE_NOT_FOUND'
  | 'MAESPACE_FOLDER_INVALID'
  | 'MAESPACE_UPLOAD_EMPTY'
  | 'MAESPACE_UPLOAD_TOO_LARGE'
  | 'MAESPACE_QUOTA_EXCEEDED'
  | 'MAESPACE_PORTAL_NOT_ALLOWED'
  | 'MAESPACE_PROJECT_WITHOUT_CLIENT'
  | 'MAESPACE_NO_MEETING'
  | 'MAESPACE_ARCHIVE_DISABLED'
  | 'MAESPACE_STORAGE_REJECTED'
  | 'MAESPACE_STORAGE_UNREACHABLE'
  // Server MCP — il consenso agli assistenti (docs/FE-TO-BE-MCP-v1.md)
  | 'MCP_REQUEST_NOT_FOUND'
  | 'MCP_REQUEST_EXPIRED'
  | 'MCP_REQUEST_ALREADY_DECIDED'
  | 'MCP_NOT_ALLOWED'
  | 'MCP_WRITE_NOT_ALLOWED'
  | 'MCP_TENANT_NOT_ALLOWED'
  | 'MCP_IMPERSONATION'
  | 'MCP_MANAGE_NOT_ALLOWED'
  | 'MCP_CONNECTION_NOT_FOUND'
  // Lucchetto d'area della capogruppo (BE AREE-SU-RISORSE §3/§5.1): colonne di `tenants`,
  // risorse intere e chiavi di `tenant-attributes` sotto un prefisso governato.
  | 'TENANT_AREA_LOCKED'
  // Chi scrive un'azienda (task 1739, BE-RESPONSE-TO-FE-PERIMETRO-SCRITTURA-TENANT-v1): la
  // amministra solo chi opera lì o chi le sta sopra; la posizione nel gruppo, solo chi sta sopra.
  | 'TENANT_NOT_ADMINISTRABLE'
  | 'TENANT_PARENT_NOT_ADMINISTRABLE'
  | 'TENANT_GROUP_POSITION_NOT_WRITABLE'
  | 'TENANT_SUBTREE_NOT_ERASABLE'
  | 'TENANT_ALTERNATIVE_INVALID'
  // Destinatari di un avviso: dal 05-10-2026 accettano i gruppi, e una voce sbagliata si rifiuta
  | 'NOTIFICATION_RECIPIENTS_INVALID'
  // Impersonazione: portale in sola lettura, sessione non rinnovabile
  | 'IMPERSONATION_READ_ONLY'
  | 'IMPERSONATION_NO_REFRESH'
  | 'IMPERSONATION_ACCESS_MISMATCH'
  // Modulo Task — assi dell'assegnazione (BE v1.146.0, TASK-MODULE T2 §8.2/§8.4)
  // Rinomina di un'etichetta (BE v1.177.1): non è una PATCH, ha i suoi rifiuti
  | 'TASK_LABEL_NAME_REQUIRED'
  | 'TASK_LABEL_NAME_TOO_LONG'
  | 'TASK_LABEL_NAME_DUPLICATE'
  | 'TASK_LABEL_PROJECT_WITHOUT_CUSTOMER'
  | 'TASK_LABEL_PROJECT_CUSTOMER_MISMATCH'
  | 'TASK_LABEL_CUSTOMER_OTHER_TENANT'
  | 'TASK_LABEL_PROJECT_OTHER_TENANT'
  | 'TASK_PROJECT_CUSTOMER_MISMATCH'
  | 'TASK_ALREADY_CLAIMED'
  | 'TASK_CLAIM_NOT_IN_QUEUE'
  | 'TASK_CLAIM_NO_EMPLOYEE'
  | 'TASK_REASSIGN_EMPLOYEE_REQUIRED'
  | 'TASK_REASSIGN_REASON_REQUIRED'
  | 'TASK_REASSIGN_EMPLOYEE_UNKNOWN'
  | 'TASK_REASSIGN_EMPLOYEE_INACTIVE'
  | 'TASK_REASSIGN_SAME_ASSIGNEE'
  | 'TASK_FILTER_PERIMETER_INVALID'
  | 'TASK_PROJECT_PERIMETER_DENIED'
  | 'TASK_FILTER_FOLLOWEDBYME_INVALID'
  | 'TASK_NOT_FOUND'
  // Configurazione board a tre livelli (BE v1.148.0)
  | 'BOARD_CONFIG_ASPECT_LOCKED'
  | 'BOARD_CONFIG_ASPECT_NOT_JSON'
  | 'BOARD_CONFIG_UNKNOWN_ASPECT'
  | 'BOARD_CONFIG_LEVEL_ALREADY_EXISTS'
  | 'BOARD_CONFIG_INVALID_OVERFLOW_BEHAVIOR'
  // Fuso del luogo di lavoro (BE v1.145.0, ATTENDANCE-TENANT-TIMEZONE)
  | 'SITE_INVALID_TIMEZONE'
  | 'TENANT_INVALID_TIMEZONE'
  // Finestra di pausa su orari e preset (BE v1.142.0, MISSED-PUNCH-REMINDER §1.1)
  | 'WORKING_HOUR_INCOMPLETE_BREAK_WINDOW'
  | 'WORKING_HOUR_BREAK_OUTSIDE_SHIFT'
  | 'WORKING_HOUR_BREAK_WINDOW_MISMATCH'
  // Configurazione dei promemoria timbratura (BE v1.142.0 §1.3)
  | 'ATTENDANCE_REMINDER_READ_ONLY'
  | 'ATTENDANCE_REMINDER_MINUTES_OUT_OF_RANGE'
  | 'ATTENDANCE_PAUSE_REMINDER_AFTER_AUTOCLOSE'
  | 'ATTENDANCE_EXIT_REMINDER_AFTER_DAY_CLOSE'
  | 'ATTENDANCE_PAUSE_REMINDER_WITHOUT_TRACKING'
  // Muro sulla coppia (utente, azienda) del portale clienti (BE v1.147.0, P8)
  | 'CLIENT_PORTAL_MODE_MISMATCH'
  | 'CLIENT_PORTAL_ACCESS_INVALID'
  | 'PORTAL_ACCESS_ROLE_CONFLICT'
  | 'PORTAL_ACCESS_ALREADY_EXISTS'
  | 'CLIENT_PORTAL_TENANTCLIENT_NOT_FOUND'
  | 'CLIENT_PORTAL_CLIENT_MISMATCH'
  // Invito staff al portale (POST /api/client-portal-accounts/invite)
  | 'PORTAL_ACCESS_QUOTA_EXCEEDED'
  | 'CLIENT_PORTAL_USER_IS_INTERNAL'
  | 'PORTAL_INVITE_DAILY_CAP'
  | 'PORTAL_INVITE_ROLE_MISSING'
  | 'PORTAL_ACCESS_PRIVATE_SINGLE_USER'
  | 'PORTAL_ACCESS_CONTACT_CLIENT_MISMATCH'
  | 'CLIENT_PORTAL_MANAGE_FORBIDDEN'
  | 'PORTAL_INVITE_EMAIL_INVALID'
  | 'PORTAL_INVITE_MISSING_FIELDS'
  | 'PORTAL_INVITE_USER_CREATE_FAILED'
  // Template di comunicazione protetti (BE v1.149, COMMUNICATION-TEMPLATES §1.4)
  | 'TEMPLATE_SYSTEM_READONLY'
  | 'TEMPLATE_CATEGORY_NOT_ON_MASTER'
  // Flussi di lavorazione e gerarchia del lavoro (BE v1.149, task 1400)
  | 'WORKFLOW_TRANSITION_NOT_ALLOWED'
  | 'WORKFLOW_STATE_UNKNOWN'
  | 'WORKFLOW_NO_EQUIVALENT_STATE'
  | 'WORKFLOW_TARGET_STATE_REQUIRED'
  | 'WORKFLOW_TRANSITION_FORBIDDEN_FOR_USER'
  | 'WORKFLOW_TRANSITION_REQUIRES_COMMENT'
  | 'WORKFLOW_TRANSITION_REQUIRES_PUBLIC_REPLY'
  | 'WORKFLOW_TRANSITION_REQUIRES_FIELDS'
  | 'WORKFLOW_NOT_ACTIVE'
  | 'WORKFLOW_IN_USE'
  | 'WORKFLOW_OVERRIDE_LOCKED'
  | 'WORKFLOW_INVALID_CATEGORY'
  | 'WORKFLOW_INVALID_ALLOWED_FOR'
  | 'WORKFLOW_TRANSITION_CLAIM_REQUIRED'
  | 'WORKFLOW_TRANSITION_CROSS_WORKFLOW'
  | 'WORKFLOW_UNKNOWN_REQUIRED_FIELD'
  | 'WORKFLOW_STATE_CODE_IMMUTABLE'
  | 'WORKFLOW_STATE_IS_SINK'
  | 'WORKFLOW_STATE_UNREACHABLE'
  | 'WORKFLOW_INCOMPLETE_CATEGORIES'
  | 'WORKFLOW_DUPLICATE_STATE_CODE'
  | 'TASK_HAS_OPEN_CHILDREN'
  | 'TASK_HAS_OPEN_DESCENDANTS'
  | 'TASK_DEPENDENCY_UNSATISFIED'
  | 'TASK_DEPENDENCY_OVERRIDE_FORBIDDEN'
  | 'TASK_DEPENDENCY_OVERRIDE_REASON_REQUIRED'
  | 'TASK_DEPENDENCY_INVALID_TYPE'
  | 'TASK_DEPENDENCY_INVALID_LAG'
  | 'TASK_DEPENDENCY_SELF'
  | 'TASK_DEPENDENCY_CYCLE'
  | 'TASK_DEPENDENCY_HIERARCHY_DEADLOCK'
  | 'TASK_CLOSURE_POLICY_DEADLOCK'
  | 'SETTING_CLOSURE_POLICY_DEADLOCK'
  | 'TASK_WORKFLOW_REQUIRED'
  | 'GENERATION_UNASSIGNED_NODES'
  | 'PROJECT_APPROVE_NOT_ALLOWED'
  | 'TASK_SOURCE_NODE_IMMUTABLE'
  | 'TASK_GROUPING_NOT_IN_PROJECT'
  | 'TASK_GROUPING_INVALID_TYPE'
  | 'TASK_GROUPING_INCONSISTENT'
  | 'SCOPE_TAXONOMY_DISABLED'
  | 'MILESTONE_ONLY_ON_FEATURE'
  | 'MILESTONE_NOT_IN_PROJECT'
  | 'SCOPE_PROJECT_MISMATCH'
  | 'SCOPE_NOT_BILLABLE'
  | 'TASK_ASSIGNMENT_REQUIRED'
  | 'PROJECT_REQUIRED'
  | 'DESCRIPTION_REQUIRED'
  | 'ENTRY_INCOMPLETE'
  | 'TASK_REMAINING_HOURS_INVALID'
  | 'PROJECT_TASK_PARENT_CYCLE'
  | 'TASK_NESTING_TOO_DEEP'
  | 'TASK_TOO_DEEP'
  | 'TASK_PARENT_IMMUTABLE'
  | 'TASK_PARENT_NOT_FOUND'
  | 'TASK_CHILD_HAS_NO_PUBLIC_CHANNEL'
  | 'TASK_COMMENT_REACTION_INVALID_KIND'
  | 'TASK_COMMENT_REACTION_COMMENT_NOT_FOUND'
  | 'TASK_COMMENT_REACTION_ALREADY_EXISTS'
  | 'TASK_COMMENT_REACTION_NOT_OWNER'
  | 'TASK_COMMENT_NOT_AUTHOR'
  | 'TASK_ISSUE_NOT_REPORTER'
  | 'TASK_FOLLOWER_NOT_OWNER'
  // Posta in uscita: il registro delle e-mail (BE-TO-FE-EMAIL-ROUTING-v1)
  | 'EMAIL_LOG_NOT_FOUND'
  | 'EMAIL_LOG_INVALID_FILTER'
  // Il generatore dei PDF (`ApiErrorMapper`): rapportini, verbali, gantt
  | 'PDF_SERVICE_UNAVAILABLE'
  | 'PDF_RENDER_FAILED'
  // Registrazione libera: dal MASTER-TENANT-WRITE-GUARD l'azienda di destinazione è obbligatoria
  | 'REGISTRATION_TENANT_REQUIRED'
  // Trasferte: i tetti della policy di viaggio (§3.4) e lo scavalcamento dell'approvazione
  | 'TRAVEL_POLICY_HOTEL_PRICE_EXCEEDED'
  | 'TRAVEL_POLICY_ROOM_TYPE_EXCEEDED'
  | 'TRAVEL_POLICY_FLIGHT_CLASS_EXCEEDED'
  | 'TRAVEL_POLICY_TRAIN_CLASS_EXCEEDED'
  | 'TRAVEL_POLICY_CAR_CATEGORY_EXCEEDED'
  | 'TRAVEL_POLICY_OVERRIDE_REASON_REQUIRED'
  | 'TRAVEL_BOOKING_BYPASS_REASON_REQUIRED'
  // Trasferte e prenotazioni dei mezzi: sollecito e scavalcamento (stesso vocabolario nei due controller)
  | 'BYPASS_NOT_ALLOWED'
  | 'NOT_PENDING'
  | 'REMINDER_TOO_SOON'
  // Verbale di prestazione al cliente (`VpsSendService`, `TimesheetEntryVpsController`, portale)
  | 'VPS_NOT_REQUIRED_ON_PROJECT'
  | 'VPS_BLOCKED_BY_REASON'
  | 'VPS_CLIENT_NO_RECIPIENT'
  | 'VPS_ALREADY_PENDING'
  | 'VPS_RESEND_CLAIM_REQUIRED'
  | 'VPS_PEC_NOT_CONFIGURED'
  | 'VPS_ENTRY_NOT_FOUND'
  | 'VPS_SEND_FAILED'
  | 'VPS_CLAIM_REQUIRED'
  | 'VPS_NOTE_TOO_LONG'
  | 'VPS_DISPATCH_NOT_ACKNOWLEDGED'
  // Smart working: pianificazione del periodo, condivisione di gruppo, storico dell'abilitazione
  | 'SMART_WORKING_PLANNING_RETROACTIVE_DENIED'
  | 'SMART_WORKING_PLANNING_INCONSISTENT_VALIDATOR'
  | 'SMART_WORKING_PLANNING_INVALID_DATE_RANGE'
  | 'SMART_WORKING_PLANNING_DATE_OUT_OF_RANGE'
  | 'SMART_WORKING_PLANNING_INVALID_VIEW_MODE'
  | 'SMART_WORKING_GROUP_SCOPE_NOT_APPLICABLE'
  | 'EMPLOYEE_SW_ELIGIBILITY_CHANGE_READ_ONLY'
  // Pianificazione attività (PLANNING v2): stati, serie, invitati, regole, letture
  | 'PLANNING_INVALID_STATE_TRANSITION'
  | 'PLANNING_ENTRY_NOT_CONSUMABLE'
  | 'PLANNING_ENTRY_NOT_FOUND'
  | 'PLANNING_ENTRY_CONFLICT'
  | 'PLANNING_ENTRY_EXISTS'
  | 'PLANNING_CAPACITY_ZERO'
  | 'TIMESHEET_ENTRY_EXISTS'
  | 'TIMESHEET_MONTH_LOCKED'
  | 'PLANNING_SERIES_HAS_LIVE_OCCURRENCES'
  | 'PLANNING_SERIES_OCCURRENCE_CONSUMED'
  | 'PLANNING_SERIES_DAYS_REQUIRED'
  | 'PLANNING_SERIES_INVALID_DAY_OF_MONTH'
  | 'PLANNING_SERIES_INVALID_INITIAL_STATUS'
  | 'PLANNING_SERIES_INVALID_RANGE'
  | 'PLANNING_SERIES_RANGE_TOO_LONG'
  | 'INVALID_RECURRENCE_TYPE'
  | 'PLANNING_EXTERNAL_ATTENDEE_NOT_ALLOWED'
  | 'PLANNING_ATTENDEE_INVALID'
  | 'INVALID_ATTENDEE_ROLE'
  | 'INVALID_ATTENDEE_RESPONSE'
  | 'INVALID_ORIGIN_VALUE'
  | 'INVALID_MEETING_TITLE_MODE'
  | 'PLANNING_POLICY_THRESHOLD_REQUIRED'
  | 'PLANNING_POLICY_SCOPE_INVALID'
  | 'PLANNING_POLICY_TENANT_MISMATCH'
  | 'PLANNING_POLICY_GROUP_SCOPE_NOT_ALLOWED'
  | 'DUPLICATE_PLANNING_POLICY_DEFAULT'
  | 'INVALID_PLANNING_MODE'
  | 'INVALID_COMPLIANCE_TRACKING'
  | 'INVALID_COVERAGE_MODE'
  | 'INVALID_REQUIRED_STATUS'
  | 'PLANNING_READ_NOT_ALLOWED'
  | 'PLANNING_PROJECT_NOT_FOUND'
  | 'PLANNING_PROJECT_READ_NOT_ALLOWED'
  | 'INVALID_RANGE'
  | 'RANGE_TOO_LONG'
  | 'DATES_REQUIRED'
  | 'TOO_MANY_DATES'
  | 'OBLIGATIONS_READ_NOT_ALLOWED'
  | 'OBLIGATION_RULE_INVALID'
  // Portale clienti: accessi, documenti, caricamenti, pagine su misura, richieste
  | 'CLIENT_PORTAL_ONLY'
  | 'CLIENT_PORTAL_ACCOUNT_INACTIVE'
  | 'CLIENT_PORTAL_ROUTE_FORBIDDEN'
  | 'CLIENT_PORTAL_ACCOUNT_DELETE_FORBIDDEN'
  | 'CLIENT_PORTAL_INVALID_SECTION'
  | 'CLIENT_PORTAL_DOC_TITLE_REQUIRED'
  | 'CLIENT_PORTAL_DOC_FILE_REQUIRED'
  | 'CLIENT_PORTAL_DOC_FILE_NOT_FOUND'
  | 'CLIENT_PORTAL_DOC_CLIENT_NOT_IN_PORTFOLIO'
  | 'CLIENT_PORTAL_DOC_IMMUTABLE'
  | 'PORTAL_ACCESS_SELF_REVOKE'
  | 'PORTAL_CONTACT_NAME_REQUIRED'
  | 'PORTAL_UPLOAD_EMPTY'
  | 'PORTAL_UPLOAD_TOO_LARGE'
  | 'PORTAL_UPLOAD_TYPE_NOT_ALLOWED'
  | 'PORTAL_UPLOAD_NO_TENANT'
  | 'PORTAL_UPLOAD_QUOTA_REACHED'
  | 'PORTAL_ATTACHMENT_QUOTA_REACHED'
  | 'PORTAL_PAGE_NOT_ASSIGNED'
  | 'PORTAL_PAGE_KEY_INVALID'
  | 'PORTAL_PAGE_KEY_LOOKS_LIKE_ID'
  | 'PORTAL_PAGE_LABEL_REQUIRED'
  | 'PORTAL_PAGE_PATH_INVALID'
  | 'PORTAL_PAGE_PATH_RESERVED'
  | 'PORTAL_PAGE_PATH_DUPLICATE'
  | 'PORTAL_PAGE_CONFIG_TOO_LARGE'
  | 'PORTAL_PAGE_CONFIG_NOT_JSON'
  | 'PORTAL_PAGE_CONFIG_BUDGET_EXCEEDED'
  | 'PORTAL_PAGE_CLIENT_NOT_IN_PORTFOLIO'
  | 'SUPPORT_REQUEST_CLOSED'
  | 'SUPPORT_REQUEST_REJECTED'
  | 'SUPPORT_REQUEST_NOT_RESOLVED'
  | 'SUPPORT_COMMENT_EMPTY'
  | 'SUPPORT_INTAKE_FIELD_TOO_LONG'
  | 'TENANT_REQUIRED'
  | (string & {});

/** Mapping `meta.code` → messaggio user-friendly italiano. */
export const API_ERROR_MESSAGES: Record<string, string> = {
  TENANT_NOT_ADMINISTRABLE: 'Quest’azienda non si modifica da qui: la gestisce chi lavora al suo interno, o chi le sta sopra nel gruppo con il permesso di amministrare le controllate (lo concede chi amministra il gruppo).',
  TENANT_ALTERNATIVE_INVALID: 'L’azienda scelta per accogliere le controllate non va bene: non può essere quella che elimini né una del suo ramo. Scegline un’altra.',
  LICENSE_NOT_IMPLEMENTED_YET: 'Questa funzione è a catalogo ma non ancora implementata. La stiamo costruendo — torna a trovarci.',
  LICENSE_REQUIRED: 'Serve una licenza attiva per usare questa funzione. Apri la sezione Licenze per attivarla.',
  LICENSE_EXCLUDED: 'Questa licenza è mutualmente esclusiva con un\'altra già attiva. Disattiva l\'altra prima di procedere.',
  LICENSE_NOT_AVAILABLE_AS_ADDON: 'Questa licenza è acquistabile solo all\'interno di un piano, non come componente aggiuntivo singolo.',
  MISSING_PREREQUISITE: 'Manca una licenza propedeutica. Verifica le dipendenze prima di attivare questa.',
  SUBSCRIPTION_ALREADY_ACTIVE: 'Esiste già una sottoscrizione attiva. Annulla o aggiorna quella corrente prima di crearne una nuova.',
  INVALID_TRANSITION: 'Cambio di stato non consentito nella sottoscrizione corrente.',
  CYCLE_DETECTED: 'La gerarchia capogruppo creerebbe un ciclo. Scegli un parent diverso.',
  IMPERSONATION_READ_ONLY: 'Stai guardando il portale come il cliente: qui si può solo consultare. Per agire torna a te con «Termina impersonification».',
  IMPERSONATION_NO_REFRESH: 'La sessione impersonata è scaduta: torna a te e riaprila se ti serve ancora.',
  IMPERSONATION_ACCESS_MISMATCH: 'L’impersonificazione vale solo per l’accesso da cui l’hai aperta. Per un altro accesso, termina e riparti da quello.',
  CROSS_TENANT_DENIED_CLAIM: 'Non hai il permesso di operare sulle aziende sotto la tua: lo concede chi amministra il gruppo.',
  CROSS_TENANT_DENIED_NOT_DESCENDANT: 'L’azienda selezionata non è sotto la tua nel gruppo. Puoi operare solo sulle aziende che ne discendono.',
  GANTT_PDF_INVALID: 'Il PDF del diagramma non si è potuto preparare con questi dati. Prova a restringere il periodo o le righe; se si ripete, segnala il problema.',
  CROSS_TENANT_DENIED: 'Quest’azienda non si gestisce da qui: la vede la piattaforma, o chi la amministra da un’azienda che le sta sopra nel gruppo.',
  TENANT_NOT_FOUND: 'L’azienda non esiste più, o non si raggiunge da qui. Aggiorna l’elenco e riprova.',
  MISSING_TENANT_ID: 'Manca l’azienda su cui operare. Riapri la sua scheda e riprova.',
  /* ── Chi accende i moduli (BE-RESPONSE-TO-FE-MODULI-v1, 26-10-2026) ──
     Sono rifiuti di **ruolo nel gruppo**, non di permesso: il rimedio è chiedere alla persona
     giusta — la piattaforma per accendere, l'azienda più in alto per togliere la sua restrizione —
     e il messaggio deve dire quale, perché un «non hai i permessi» manda dall'amministratore
     dell'azienda, che non può farci niente. */
  MODULE_MANAGED_BY_PLATFORM: 'I moduli dell’azienda li attiva e li spegne la piattaforma: per cambiarne uno rivolgiti all’amministrazione di MaeWay.',
  MODULE_MANAGED_BY_PARENT: 'Un’azienda non si spegne i moduli da sola: lo fa un’azienda che le sta sopra nel gruppo.',
  MODULE_RESTRICTED_BY_ANCESTOR: 'Questo modulo l’ha spento un’azienda che sta più in alto nel gruppo: solo lei lo può riaccendere.',
  MODULE_RESTRICTION_NO_PARENT: 'Quest’azienda non ha una capogruppo: per spegnerle un modulo si usa l’interruttore della piattaforma.',
  MISSING_MODULES: 'Non è stato scelto nessun modulo da cambiare.',
  /**
   * `403` su POST/PATCH/DELETE quando si opera sull'**azienda di piattaforma** senza essere
   * super-admin. Il master non è un'azienda: è il modello da cui nascono le altre — i ruoli che
   * vivono lì sono condivisi con tutte, e le categorie marcate `CopyInNewTenants` vengono copiate
   * in **ogni** azienda nuova. Chi scrive lì scrive nel modello di quelle che verranno.
   *
   * ⚠️ Sulla stessa azione convivono **due** rifiuti diversi, e questo va **oltre** il 401, non al
   * suo posto: chi non ha il claim CRUD prende `401` e al muro non ci arriva nemmeno; questo `403`
   * lo vede solo chi *avrebbe* il permesso di scrivere. Chi legge deve capire che il divieto non
   * riguarda lui ma l'azienda su cui si trova — da qui il rimedio nella frase.
   */
  INVALID_CALENDAR_PROVIDER: 'Fornitore di calendario non ammesso: sono previsti Google, Microsoft 365 e CalDAV.',
  CALENDAR_INTEGRATION_DISABLED: 'Il collegamento con i calendari esterni non è attivo per questa azienda. Lo accende chi la amministra.',
  CALENDAR_PROVIDER_NOT_CONFIGURED: 'Per questo fornitore mancano le credenziali dell’applicazione: vanno configurate una volta sola nelle impostazioni di integrazione calendario.',
  MAESPACE_DISABLED: 'MaeSpace non è attivo per questa azienda: lo accende un amministratore da «Moduli attivi».',
  MAESPACE_SSO_NOT_ALLOWED: 'Il tuo profilo non prevede l’accesso a MaeSpace da qui: chiedi a un amministratore il permesso «maespace.sso».',
  MAESPACE_SSO_IMPERSONATION: 'Durante un’impersonificazione non si entra in MaeSpace al posto dell’utente: torna alla tua sessione per aprirlo.',
  MAESPACE_NO_USER: 'Non riusciamo a riconoscere il tuo utente: esci e rientra, poi riprova.',
  MAESPACE_NO_EMAIL: 'Il tuo utente non ha un indirizzo email utilizzabile per MaeSpace: aggiungilo dal tuo profilo o chiedilo a un amministratore.',
  MAESPACE_USER_NOT_PROVISIONED: 'Non hai ancora un account MaeSpace: chiedi a un amministratore di MaeSpace di invitarti.',
  MAESPACE_UNAVAILABLE: 'MaeSpace non ha risposto come previsto. Riprova fra poco.',
  MAESPACE_UNREACHABLE: 'MaeSpace non è raggiungibile in questo momento. Riprova fra poco.',
  MAESPACE_BAD_RESPONSE: 'MaeSpace ha risposto in un modo che non ci aspettavamo. Riprova fra poco; se si ripete, segnalalo.',
  MAESPACE_ERROR: 'MaeSpace non ha completato la richiesta. Riprova fra poco.',
  MAESPACE_NOT_A_MEMBER: 'Non sei fra i membri di questa stanza MaeSpace: la vedono il PM e il team del progetto. Chiedi al PM di aggiungerti al team.',
  MAESPACE_ROOM_NOT_READY: 'Il progetto non ha ancora una stanza MaeSpace: guarda lo stato della stanza nella scheda Team.',
  MAESPACE_FILE_NOT_FOUND: 'Il file non è più nella stanza: qualcuno l’ha spostato o cancellato. Ricarica l’elenco.',
  MAESPACE_FOLDER_INVALID: 'Il percorso della cartella non è valido: usa nomi separati da «/», senza «..».',
  MAESPACE_UPLOAD_EMPTY: 'Non è arrivato nessun file: sceglilo di nuovo e riprova.',
  MAESPACE_UPLOAD_TOO_LARGE: 'Il file supera i 100 MB ammessi da MaeSpace: comprimilo o dividilo.',
  MAESPACE_QUOTA_EXCEEDED: 'Lo spazio di MaeSpace è esaurito: avvisa un amministratore.',
  MAESPACE_PORTAL_NOT_ALLOWED: 'Il tuo profilo non prevede la pubblicazione nel portale clienti: chiedila a chi gestisce il portale.',
  MAESPACE_PROJECT_WITHOUT_CLIENT: 'Il progetto non ha un cliente, quindi non c’è un portale in cui pubblicare: indica il cliente nei dati del progetto.',
  MAESPACE_NO_MEETING: 'Questa riga non ha una riunione MaeSpace: non ci sono registrazioni né minute da mostrare.',
  MAESPACE_ARCHIVE_DISABLED: 'L’archivio dei file su MaeSpace è spento: si accende dalla configurazione del sistema.',
  MAESPACE_STORAGE_REJECTED: 'Lo spazio di archiviazione di MaeSpace ha rifiutato il file. Riprova; se si ripete, segnalalo.',
  MAESPACE_STORAGE_UNREACHABLE: 'Lo spazio di archiviazione di MaeSpace non risponde. Riprova fra poco.',
  MCP_REQUEST_NOT_FOUND: 'Questa richiesta di collegamento non esiste: riparti dall’assistente e collegalo di nuovo.',
  MCP_REQUEST_EXPIRED: 'La richiesta di collegamento è scaduta (vale 10 minuti): riparti dall’assistente e collegalo di nuovo.',
  MCP_REQUEST_ALREADY_DECIDED: 'Questa richiesta è già stata approvata o rifiutata: se l’assistente non risulta collegato, riparti da lì.',
  MCP_NOT_ALLOWED: 'Il tuo profilo non prevede il collegamento di assistenti: chiedi a un amministratore il permesso «mcp.use».',
  MCP_WRITE_NOT_ALLOWED: 'In questa azienda il tuo profilo non prevede la scrittura tramite assistente: togli la spunta e approva in sola lettura, o chiedi il permesso «mcp.write».',
  MCP_TENANT_NOT_ALLOWED: 'Per questa azienda il collegamento di assistenti non è attivo o non ti è concesso: scegline un’altra.',
  MCP_IMPERSONATION: 'Durante un’impersonificazione non si collega un assistente al posto dell’utente: torna alla tua sessione.',
  MCP_MANAGE_NOT_ALLOWED: 'Vedere i collegamenti degli altri richiede il permesso «mcp.manage»: i tuoi li trovi in Impostazioni › Integrazioni.',
  MCP_CONNECTION_NOT_FOUND: 'Il collegamento non c’è più (revocato o scaduto): ricarica l’elenco.',
  EXTERNAL_SYNC_LOCKED: 'Questo campo arriva dal gestionale collegato e qui non si modifica: la correzione va fatta lì, e arriva da sola al prossimo allineamento.',
  CONNECTOR_OPERATION_UNKNOWN: 'Questo connettore non accetta il comando richiesto: ricarica la pagina, i comandi disponibili potrebbero essere cambiati.',
  CONNECTOR_PARAMS_INVALID: 'Il comando è partito con dati non validi: ricarica la pagina e riprova; se si ripete, segnalalo.',
  CONNECTOR_COMMAND_SUPERADMIN_ONLY: 'Il giro vero di aggancio e pulizia lo lancia solo chi amministra la piattaforma: tu puoi lanciare la prova.',
  CONNECTOR_DISABLED: 'Il connettore è spento: accendilo prima di lanciare questo comando. Le prove si possono fare anche a connettore spento.',
  CONNECTOR_COMMAND_PENDING: 'C’è già un comando in coda o in esecuzione su questo connettore: aspetta che finisca, o annulla quello in coda.',
  CONNECTOR_COMMAND_IMMUTABLE: 'Un comando già creato non si modifica: si può solo annullare finché è in coda.',
  CONNECTOR_COMMAND_STARTED: 'Il comando è già stato preso in carico e non si può più annullare: aspetta che finisca, l’esito compare qui.',
  EMAIL_ROUTING_INVALID: 'Un indirizzo della posta in uscita non è valido: controlla la casella di dirottamento e i destinatari ammessi (un indirizzo, o un dominio con la @ davanti).',
  CONNECTOR_SYSTEM_UNKNOWN: 'Il sistema non ha ancora un connettore per questo gestionale: ricarica la pagina, l’elenco dei connettori disponibili potrebbe essere cambiato.',
  CALENDAR_PROVIDER_NOT_IMPLEMENTED: 'Il collegamento con questo fornitore non è ancora disponibile sul sistema: non c’è niente da configurare, arriverà con un aggiornamento della piattaforma.',
  /**
   * NON è un guasto: significa **pianificazione disattivata** per quella persona. Chi lo riceve
   * da `planning-policies/resolve` deve spegnere la funzione, non mostrare un errore — vedi
   * `resolvePlanningPolicy`, che lo traduce in `null`.
   */
  PLANNING_POLICY_NOT_RESOLVED: 'Per questa persona non c’è nessuna regola di pianificazione applicabile: la funzione risulta disattivata.',
  PLANNING_POLICY_OVERRIDE_NOT_ALLOWED: 'La regola di default di questa azienda non ammette eccezioni per sede, gruppo o dipendente. Per crearne una, attiva prima «Ammetti regole più specifiche» sulla regola di default.',
  MASTER_TENANT_READ_ONLY: 'Questa è l\'azienda modello: quello che c\'è qui viene copiato in ogni azienda nuova, quindi si modifica solo da super-amministratore. Cambia azienda dal menu in alto per lavorare sui tuoi dati.',
  /**
   * `GET refresh-token/change-tenant` → 401, dal BE `v1.152.0`. Prima rispondeva **200** emettendo
   * il token: era la porta di `isOwner`, che promuoveva a titolare dell'azienda di destinazione chi
   * era titolare altrove. Per un'interfaccia è una rottura, ed è voluta.
   */
  TENANT_ENTRY_DENIED: 'Non hai accesso a questa azienda: il passaggio è stato rifiutato. Se dovresti averlo, chiedi a chi amministra quell\'azienda di associarti.',
  QUOTA_EXCEEDED: 'Hai raggiunto il limite previsto dalla tua licenza per questa risorsa. Aggiorna il piano per aumentare la quota.',
  USER_NOT_FOUND: 'Utente non trovato. Effettua nuovamente il login.',
  // Site Sharing v1 (BE v1.17.1)
  EMPLOYEE_TENANT_NOT_HOSTED_IN_SITE: 'L\'azienda del dipendente non è ospitata in questa sede. Aggiungi prima un\'associazione (mapping) per quell’azienda.',
  FLAG_ALREADY_ASSIGNED_TO_OTHER_SITE: 'Questo flag (legale/amministrativa/fatturazione/principale) è già assegnato a un\'altra sede per questa azienda. Rimuovilo da lì prima di assegnarlo qui.',
  MAPPING_ALREADY_EXISTS: 'Questa azienda è già associata attivamente a questa sede.',
  LAST_OWNER_CANNOT_BE_REMOVED: 'Non puoi rimuovere l\'unico proprietario attivo della sede. Assegna prima un altro owner.',
  // ERP v2 (29 giu 2026)
  DUPLICATE_RESOURCE: 'Esiste già un elemento con questi dati. Modifica quello esistente invece di crearne un duplicato.',
  DUPLICATE_SHIFT_ASSIGNMENT: 'Questo dipendente ha già un turno pianificato per questa data. È ammesso un solo turno al giorno per dipendente.',
  DUPLICATE_VAT_NUMBER: 'Esiste già un cliente con questa partita IVA in questa azienda.',
  DUPLICATE_SITE_WORKING_TIME: 'Per questa sede esiste già un orario configurato per questo giorno.',
  INVALID_EMPLOYEE_TYPE: 'Tipo dipendente non valido: ammessi solo «interno» o «esterno».',
  INVALID_RELATIONSHIP_STATE: 'Il tipo di relazione non è coerente con l\'elemento collegato. Verifica la selezione.',
  // Allineamento v1.20 (Sprint 1 / working-times / mapping sedi)
  INVALID_ATTRIBUTE_VALUE: 'Valore non valido: controlla paese, valuta, timezone o forma legale.',
  DUPLICATE_SITE_TENANT_MAPPING: 'Questa azienda è già associata a questa sede.',
  INVALID_DAILY_WORKING_MINUTES: 'Ore giornaliere fuori dall\'intervallo consentito (0–24 ore).',
  CROSS_GROUP_LINK_DENIED: 'Non puoi collegare utenti fuori dal tuo gruppo societario: serve il permesso di gestione sottotenant.',
  NOT_AN_INVITATION_OTP: 'Questo link non è un invito valido (forse è un link di reset password?).',
  // CRM Client (BE v1.20.37) e tenant-attributes (BE v1.20.38)
  CLIENT_DUPLICATE_IN_PORTFOLIO: 'Questo cliente è già presente nel portafoglio del tuo gruppo (stessa P.IVA o codice fiscale).',
  // Assegnazione dei ruoli (task 2030, BE v1.266.0)
  ROLE_MASTER_ONLY: 'Questo ruolo vale su tutta la piattaforma: si dà e si toglie solo dall’azienda principale della piattaforma, non da qui.',
  ROLE_ESCALATION: 'Questo ruolo dà dei permessi che tu non hai: può darlo o toglierlo solo chi li ha, di solito chi amministra l’azienda.',
  ROLE_USER_NOT_IN_TENANT: 'Questa persona non è associata a questa azienda: prima va aggiunta fra gli utenti dell’azienda, poi le si può dare un ruolo.',
  ROLE_AUTO_MANAGED: 'Questo ruolo lo dà e lo toglie il sistema da solo: non si cambia a mano.',
  // Variazione anagrafica dal portale (task 2056, BE v1.266.0)
  CLIENT_CHANGE_REQUEST_NO_CLIENT: 'Questo accesso non è collegato a un cliente: le modifiche non si possono chiedere da qui.',
  PORTAL_ACCESS_NOT_ADMIN: 'Le modifiche ai dati le chiede il referente della vostra azienda.',
  CLIENT_CHANGE_REQUEST_REASON_REQUIRED: 'Scrivi perché i dati vanno cambiati.',
  CLIENT_CHANGE_REQUEST_EMPTY: 'Cambia almeno un dato prima di inviare la richiesta.',
  CLIENT_CHANGE_REQUEST_FIELD_NOT_REQUESTABLE: 'Uno dei dati non si può chiedere di cambiare da qui.',
  CLIENT_CHANGE_REQUEST_FIELD_TOO_LONG: 'Un valore è troppo lungo: accorcialo e riprova.',
  CLIENT_CHANGE_REQUEST_INVALID_VALUE: 'Un valore non è valido: controlla email, nazione (due lettere) e le voci scelte.',
  CLIENT_CHANGE_REQUEST_NO_CHANGES: 'I valori indicati sono già quelli registrati: non c’è niente da cambiare.',
  CLIENT_CHANGE_REQUEST_TOO_MANY_OPEN: 'Avete già dieci richieste in attesa: aspettate che vengano valutate prima di aprirne altre.',
  CLIENT_CHANGE_REQUEST_NOT_FOUND: 'La richiesta non si trova più, oppure non è di un cliente della tua azienda.',
  CLIENT_CHANGE_REQUEST_CLOSED: 'La richiesta è già stata chiusa da qualcun altro: ricarica per vedere l’esito.',
  CLIENT_CHANGE_REQUEST_NOTHING_TO_APPLY: 'Scegli almeno un dato da applicare, oppure rifiuta la richiesta.',
  CLIENT_CHANGE_REQUEST_FIELD_TWICE: 'Un dato è segnato sia da applicare qui sia come già cambiato nel gestionale: scegline una.',
  CLIENT_CHANGE_REQUEST_FIELD_NOT_IN_REQUEST: 'Uno dei dati scelti non fa parte della richiesta: ricarica e riprova.',
  CLIENT_CHANGE_REQUEST_REJECTION_REASON_REQUIRED: 'Scrivi il motivo del rifiuto: il cliente lo legge.',
  // Circolari (task 2055, BE v1.266.0)
  CIRCULAR_CONCURRENT_UPDATE: 'Qualcun altro ha appena cambiato questa circolare: ricaricala e riprova.',
  CIRCULAR_SCHEDULED_LOCKED: 'Una circolare programmata non si riscrive: annulla la programmazione, correggila e programmala di nuovo.',
  CIRCULAR_PUBLISHED_READONLY: 'Una circolare pubblicata non si modifica né si elimina: si possono cambiare solo le etichette.',
  INVALID_CIRCULAR_TRANSITION: 'La circolare è già passata a un altro stato: ricaricala per vedere com’è adesso.',
  CIRCULAR_REVIEW_REQUIRED: 'L’azienda chiede la revisione prima di pubblicare: mandala in revisione.',
  CIRCULAR_REJECT_REASON_REQUIRED: 'Scrivi perché la rimandi indietro: chi l’ha scritta lo legge.',
  CIRCULAR_SCHEDULE_INVALID: 'Scegli un giorno e un’ora nel futuro.',
  CIRCULAR_AUDIENCE_EMPTY: 'Con questi destinatari non la riceverebbe nessuno: allarga il pubblico.',
  CIRCULAR_AUDIENCE_INVALID: 'Fra i destinatari scelti c’è qualcuno che non appartiene alle aziende raggiunte: toglilo e riprova.',
  CIRCULAR_TENANT_OUT_OF_SCOPE: 'Una delle aziende scelte non fa parte dell’ambito: toglila o cambia l’ambito.',
  CIRCULAR_LABEL_NOT_VISIBLE: 'Una delle etichette scelte non è disponibile per questa azienda: toglila e riprova.',
  CIRCULAR_VERSION_DUPLICATE: 'Questa versione è già usata da un’altra circolare dell’azienda: scegline un’altra.',
  CIRCULAR_INVALID: 'Controlla titolo, testo, versione e tipologia: uno dei campi non va bene.',
  CIRCULAR_TENANT_REQUIRED: 'La circolare non ha un’azienda: rientra nell’azienda e riprova.',
  DUPLICATE_ATTRIBUTE_KEY: 'Esiste già un attributo dell’azienda con questa chiave.',
  INVALID_VALUE_FOR_TYPE: 'Il valore non è coerente con il tipo dichiarato dell\'attributo.',
  /* Lo stesso codice da due risorse con due vocabolari: le impostazioni dell'azienda ammettono
     anche JSON, i campi del progetto anche l'elenco di valori. Elencarne uno sbagliava l'altro. */
  INVALID_VALUE_TYPE: 'Il tipo di valore scelto non è fra quelli previsti (testo, numero intero o decimale, sì/no, data, data e ora, e — a seconda della scheda — elenco di valori o JSON): scegline uno dall’elenco.',
  MISSING_ATTRIBUTE_KEY: 'La chiave dell\'attributo è obbligatoria.',
  // Modulo Progetti (BE v1.21/v1.22)
  INVALID_STATUS_TRANSITION: 'Cambio di fase non consentito dal ciclo di vita del progetto.',
  INVALID_STATUS_VALUE: 'Valore di stato non ammesso dal vocabolario.',
  CHANGE_ORDER_REQUIRED: 'Dopo la fase Bozza i campi contrattuali (valore, ore, deadline, budget) si modificano solo con un change order approvato.',
  DUPLICATE_PROJECT_CODE: 'Esiste già un progetto con questo codice.',
  /* ⚠️ Generico: il sistema lo manda per **qualunque** cancellazione fermata da dati collegati (è la
     rete di sicurezza sui vincoli del database), non solo per i progetti. Fino al 10-10-2026 il testo
     diceva «il progetto ha dati collegati» anche cancellando un turno. Chi sa cosa c'è collegato lo
     dice al posto suo (`projectTaskErrors.ts`, la verifica prima di eliminare un turno). */
  HAS_DEPENDENCIES: 'Impossibile eliminare: ci sono altri dati collegati a questo elemento. Toglili o spostali prima, poi riprova.',
  DUPLICATE_TEAM_MEMBER: 'Il dipendente fa già parte del team di questo progetto.',
  HOUR_CAP_EXCEEDED: 'Superato il tetto ore configurato per questo progetto.',
  SCOPE_NOT_ENFORCEABLE: 'Questo tipo di tetto ore non è applicabile a milestone o categorie.',
  ALREADY_PROCESSED: 'Questo change order è già stato processato.',
  USE_APPROVE_ENDPOINT: 'L\'approvazione va eseguita con l\'azione dedicata, non modificando lo stato direttamente.',
  EXTERNALLY_MANAGED: 'Campo gestito dal gestionale esterno collegato: la modifica va fatta lì.',
  RETAINER_AMOUNT_REQUIRED: 'Con questo modello di fatturazione è obbligatorio l\'importo del canone (retainer).',
  ACTIVITY_REASON_REQUIRED: 'Questo progetto richiede la causale attività sulle ore.',
  // Group Resource Pool (BE v1.23/v1.24/v1.25)
  CROSS_TENANT_DENIED_GROUP: 'Il dipendente non appartiene al gruppo societario abilitato per questa operazione.',
  HIERARCHY_CYCLE: 'Non si può: quella persona sta già sopra il responsabile scelto, e nessuno può essere il capo del proprio capo.',
  SUBORDINATE_ALREADY_HAS_MANAGER: 'Questa persona ha già un responsabile: un dipendente ne ha uno solo. Spostala invece di aggiungerla.',
  EMPLOYEE_IS_HIERARCHY_ROOT: 'Questa persona è un vertice della gerarchia, e un vertice non risponde a nessuno. Toglile prima il ruolo di vertice.',
  EMPLOYEE_IS_SUBORDINATE: 'Questa persona ha un responsabile, quindi non può essere un vertice. Staccala prima dal suo responsabile.',
  EMPLOYEE_IDENTITY_DUPLICATES_PRESENT: 'Ci sono ancora dipendenti con lo stesso codice fiscale o la stessa email nella stessa azienda: sistemali prima, poi riprova.',
  INVALID_COST_SPLIT: 'Le percentuali di ripartizione costi devono sommare 100.',
  INVALID_ALLOCATION_PERCENT: 'La percentuale di allocazione deve essere tra 0 e 100.',
  // Estensioni configurazione per-tenant (BE v1.23.0 — tenant-config extensions)
  DUPLICATE_PROJECT_TYPE_PRESET: 'Esiste già un preset per questa tipologia di progetto. Modifica quello esistente.',
  TARDINESS_POLICY_SCOPE_INVALID: 'Ambito policy non valido: se indichi un dipendente non puoi indicare anche sede o gruppo.',
  TARDINESS_POLICY_TENANT_MISMATCH: 'Sede, gruppo o dipendente di ancoraggio non appartengono a questa azienda.',
  DUPLICATE_TARDINESS_DEFAULT: 'Esiste già una policy di default (senza ambito) per questa azienda.',
  TARDINESS_POLICY_NOT_RESOLVED: 'Nessuna policy ritardi applicabile: configurazione incompleta per questo dipendente.',
  EMPLOYEE_NOT_FOUND: 'Dipendente non trovato in questa azienda.',
  EMPLOYEE_ID_REQUIRED: 'È obbligatorio indicare il dipendente.',
  DUPLICATE_PROJECT_ATTRIBUTE_DEFINITION: 'Esiste già un campo custom con questa chiave per questa azienda.',
  PROJECT_ATTRIBUTE_ENUM_INVALID: 'Con tipo «enum» serve un elenco di valori non vuoto; con gli altri tipi l\'elenco va lasciato vuoto.',
  DUPLICATE_PROJECT_ATTRIBUTE_VALUE: 'Questo campo custom è già valorizzato per questo progetto.',
  PROJECT_ATTRIBUTE_TENANT_MISMATCH: 'Progetto e campo custom non appartengono alla stessa azienda.',
  // Visibilità di gruppo holding sulle config entities (BE v1.24.0)
  INVALID_SCOPE: 'Ambito non valido: si sceglie fra «solo questa azienda» e «anche le aziende del gruppo».',
  /* ⚠️ La stessa frase che le schermate mostrano **prima** di provare, da `lib/groupScope`:
     un rifiuto che elenca requisiti diversi da quelli letti due secondi fa fa pensare a un
     terzo motivo che non c'e'. */
  GROUP_SCOPE_DENIED: GROUP_SCOPE_REQUISITI,
  GROUP_RECORD_READONLY: 'Questo è un record di gruppo di un\'altra società: solo l’azienda proprietaria può modificarlo o eliminarlo.',
  PROJECT_ATTRIBUTE_HAS_GROUP_VALUES: 'Impossibile eliminare/declassare: il campo è valorizzato su progetti di altre società del gruppo.',
  PROJECT_CATEGORY_HAS_GROUP_REFERENCES: 'Impossibile eliminare/declassare: la categoria è usata da progetti di altre società del gruppo.',
  // Workflow changelog + notifiche (BE-DONE-CHANGELOG-WORKFLOW-v1)
  INVALID_CHANGELOG_TRANSITION: 'Cambio di stato del changelog non consentito dal workflow (bozza → revisione → pronto → rilasciato).',
  INVALID_CHANGELOG_STATUS: 'Stato del changelog non valido: ammessi solo bozza, revisione, pronto, rilasciato.',
  INVALID_CHANGELOG_VERSION: 'Versione non valida: usa il formato semantico X.Y.Z (es. 1.4.0).',
  DUPLICATE_CHANGELOG_VERSION: 'Esiste già un changelog con questa versione. Scegli un numero di versione diverso.',
  CHANGELOG_SCOPE_DENIED: 'Non hai i permessi per questa operazione sul changelog.',
  INVALID_NOTIFICATION_CHANNEL: 'Canale di notifica non valido: ammessi nessuno, push, email o entrambi.',
  INVALID_NOTIFICATION_TARGET: 'Destinatari non validi: seleziona un pubblico ammesso dall\'elenco.',
  // Gestionale Sprint (BE-DONE-GESTIONALE-SPRINT-v1)
  INVALID_PRODUCT_TYPE: 'Tipo prodotto non valido: ammessi «prodotto» o «trasversale».',
  INVALID_SPRINT_PRIORITY: 'Priorità non valida: ammesse Alta, Media o Bassa.',
  INVALID_SPRINT_QUARTER: 'Quarter non valido: ammessi Q1, Q2, Q3 o Q4.',
  INVALID_SPRINT_STATE: 'Stato non valido: ammessi APERTO, CHIUSO, PENDING, STOPPED o GOLIVE.',
  DUPLICATE_SPRINT_MONTH: 'Esiste già uno sprint per questo mese di competenza e anno.',
  DUPLICATE_SPRINT_TASK: 'Esiste già un task con questo titolo per lo stesso prodotto e cliente.',
  DUPLICATE_SPRINT_PROGRESS: 'Esiste già un avanzamento per questo task in questo sprint.',
  DUPLICATE_SPRINT_MEMBER: 'Questo utente è già associato al prodotto.',
  PRODUCT_HAS_TASKS: 'Impossibile eliminare il prodotto: ha task collegati.',
  SPRINT_HAS_PROGRESS: 'Impossibile eliminare lo sprint: ha avanzamenti collegati.',
  SPRINT_LOCKED: 'Sprint bloccato: la baseline è congelata e non è modificabile.',
  SPRINT_ADMIN_REQUIRED: 'Operazione riservata agli amministratori del gestionale sprint (claim canManageSprints).',
  PRODUCT_SCOPE_DENIED: 'Puoi modificare solo i prodotti a cui sei associato.',
  PROJECT_TENANT_MISMATCH: 'Il progetto collegato non appartiene a questa azienda.',
  SPRINT_TASK_NOT_FOUND: 'Task sprint non trovato.',
  SPRINT_NOT_FOUND: 'Sprint non trovato.',
  // Pacchetto HR — modulo LEAVE (assenze/ferie v1)
  LEAVE_INVALID_STATUS_TRANSITION: 'Transizione di stato non consentita per questa richiesta di assenza.',
  LEAVE_INVALID_DATE_RANGE: 'Intervallo date non valido: la data di fine deve essere ≥ inizio.',
  LEAVE_INVALID_UNIT_OF_MEASURE: 'Unità di misura non valida: ammessi giorni, ore o entrambi.',
  LEAVE_INVALID_PART_OF_DAY: 'Frazione di giornata non valida.',
  LEAVE_INVALID_PRIORITY: 'Priorità non valida.',
  LEAVE_TYPE_REQUIRED: 'Il tipo di assenza è obbligatorio.',
  LEAVE_TYPE_INACTIVE: 'Questo tipo di assenza non è attivo.',
  LEAVE_REASON_REQUIRED: 'Per questo tipo di assenza è obbligatoria la motivazione.',
  LEAVE_REASON_TYPE_MISMATCH: 'La motivazione non appartiene al tipo di assenza selezionato.',
  LEAVE_REASON_INACTIVE: 'Questa motivazione non è attiva.',
  LEAVE_REASON_NOT_REJECTABLE: 'Questa motivazione non è rifiutabile.',
  LEAVE_INVALID_ACCRUAL_POLICY: 'Politica di maturazione non valida.',
  LEAVE_INVALID_CARRY_OVER_POLICY: 'Politica di riporto non valida.',
  LEAVE_INVALID_DOCUMENT_TYPE: 'Tipo di documento non valido.',
  LEAVE_INVALID_DOCUMENT_SUBMISSION_MODE: 'Modalità di invio documento non valida.',
  LEAVE_INVALID_AUTO_APPROVE_POLICY: 'Politica di auto-approvazione non valida.',
  LEAVE_TYPE_CODE_DUPLICATE: 'Esiste già un tipo di assenza con questo codice.',
  LEAVE_REASON_CODE_DUPLICATE: 'Esiste già una motivazione con questo codice.',
  LEAVE_BALANCE_READ_ONLY: 'Il saldo assenze è di sola lettura (calcolato dal sistema).',
  LEAVE_EVENT_READ_ONLY: 'Lo storico eventi è di sola lettura.',
  LEAVE_ATTACHMENT_TYPE_MISMATCH: 'Il tipo di allegato non è coerente con il documento richiesto.',
  LEAVE_ATTACHMENT_LOCKED: 'Non è possibile eliminare l\'allegato: il tempo per correggerlo è scaduto, serve l\'HR.',
  LEAVE_ATTACHMENT_DELETE_REASON_REQUIRED: 'Indica una motivazione (almeno 5 caratteri) per eliminare l\'allegato.',
  LEAVE_INVALID_ELIGIBILITY: 'Eleggibilità del tipo di assenza non valida.',
  LEAVE_CROSS_YEAR_HOURS_FALLBACK: 'Richiesta a ore a cavallo d\'anno: registrata interamente sull\'anno di inizio.',
  LEAVE_DAY_CANCELLATION_REASON_REQUIRED: 'Indica la motivazione della cancellazione dei giorni.',
  LEAVE_PARTIAL_CANCELLATION_NOT_ALLOWED: 'Questo tipo di assenza non consente di cancellare singoli giorni: va annullata per intero.',
  LEAVE_DAY_NOT_FOUND: 'Giorno non presente nella richiesta.',
  LEAVE_DAY_ALREADY_CANCELLED: 'Giorno già cancellato.',
  LEAVE_CANNOT_CANCEL_PAST_DAY: 'Non è possibile cancellare un giorno già passato.',
  LEAVE_DAY_INVALID_DATE_RANGE: 'Data non valida.',
  LEAVE_INVALID_ELIGIBILITY_MODE: 'Modalità di eleggibilità non valida.',
  LEAVE_INVALID_REQUIRED_PROTECTED_STATUS_TYPE: 'Uno degli status protetti richiesti non esiste.',
  LEAVE_INVALID_REQUIRED_PROTECTED_STATUS_SUBTYPE: 'Uno dei sottotipi richiesti non esiste.',
  LEAVE_ELIGIBILITY_MODE_REQUIRES_STATUS_TYPES: 'Indica almeno uno status protetto: senza, il tipo non sarebbe richiedibile da nessuno.',
  // Admin job runner (BE Fase 1)
  JOB_UNKNOWN: 'Job non presente nel catalogo.',
  JOB_PARAM_MISSING: 'Manca un parametro obbligatorio del job.',
  JOB_PARAM_INVALID: 'Parametro del job non valido.',
  JOB_ALREADY_RUNNING: 'Il job è già in esecuzione: attendi che finisca.',
  JOB_NOT_MANUALLY_RUNNABLE: 'Questo job non è lanciabile a mano.',
  JOB_DRY_RUN_NOT_SUPPORTED: 'La simulazione non è supportata: i job sono idempotenti, si esegue e si verifica l\'esito.',
  LEAVE_BLACKOUT_INVALID_ENFORCEMENT: 'Modalità di applicazione del periodo di blackout non valida.',
  LEAVE_BLACKOUT_PERIOD: 'Le date cadono in un periodo in cui l’azienda non concede assenze di questo tipo.',
  LEAVE_RETROACTIVE_DENIED: 'Richiesta retroattiva non consentita oltre il limite previsto.',
  LEAVE_ADVANCE_NOTICE_INSUFFICIENT: 'Preavviso insufficiente per questo tipo di assenza.',
  LEAVE_HANDOVER_REQUIRED: 'Per questa richiesta sono obbligatorie le note di consegna (handover).',
  LEAVE_SUBSTITUTE_REQUIRED: 'Per questa richiesta è obbligatorio indicare un sostituto.',
  LEAVE_INSUFFICIENT_BALANCE: 'Saldo insufficiente per questo tipo di assenza.',
  LEAVE_REJECTION_REASON_REQUIRED: 'La motivazione del rifiuto è obbligatoria.',
  LEAVE_NOT_FOUND: 'Richiesta di assenza non trovata.',
  LEAVE_FORBIDDEN: 'Non hai i permessi per questa operazione sulla richiesta di assenza.',
  LEAVE_APPROVAL_DELEGATION_MISSING: 'Manca una delega di approvazione valida per questa operazione.',
  LEAVE_DOCUMENT_CODE_MISSING: 'È richiesto il codice del documento.',
  LEAVE_DOCUMENT_REQUIRED: 'Per questa richiesta è obbligatorio allegare un documento.',
  LEAVE_DOCUMENT_BOTH_REQUIRED: 'Sono richiesti sia il codice sia l\'allegato del documento.',
  LEAVE_INVALID_CERTIFICATE_CODE_FORMAT: 'Formato del codice certificato non valido.',
  LEAVE_TEAM_OVERLAP: 'Attenzione: sovrapposizione con assenze di altri membri del team.',
  // I messaggi dicono cosa fare, non solo cosa è andato storto: sono errori che
  // arrivano in faccia a un approvatore nel mezzo di un'azione.
  LEAVE_ADMIN_APPROVAL_NOT_REQUIRED: 'Per questo tipo di assenza non è previsto il passaggio HR: l’approvazione del responsabile è definitiva.',
  LEAVE_INVALID_APPROVAL_WORKFLOW: 'Workflow di approvazione non valido. Ammessi: solo responsabile, responsabile + HR, nessuna approvazione (quest’ultima solo sul singolo tipo).',
  LEAVE_EVENT_DURATION_EXCEEDED: 'La durata richiesta supera i giorni previsti per singolo evento su questo tipo di assenza.',
  LEAVE_DOCUMENT_OVERDUE_WITHOUT_WAIVER: 'Il termine per il documento è scaduto: allega il documento oppure concedi una deroga prima di approvare.',
  LEAVE_DOCUMENT_WAIVER_REASON_REQUIRED: 'La motivazione della deroga deve essere di almeno 20 caratteri.',
  LEAVE_TYPE_INVALID_DOCUMENT_GRACE_DAYS: 'I giorni di tolleranza per il documento devono essere maggiori di zero.',
  NO_EMPLOYEE_PROFILE: 'Nessun profilo dipendente associato al tuo utente: contatta l\'amministratore.',
  // Pacchetto HR — modulo TIMESHEET-REPORTING v2
  TIMESHEET_TIMER_DISABLED: 'Il timer non è abilitato per questa azienda.',
  TIMESHEET_INVALID_TIMER_TRANSITION: 'Operazione timer non valida nello stato corrente.',
  TIMESHEET_TIMER_ALREADY_RUNNING: 'Hai già un timer in esecuzione su un\'altra voce. Fermalo prima di avviarne un altro.',
  TIMESHEET_NOT_FOUND: 'Voce di timesheet non trovata.',
  TIMESHEET_FORBIDDEN: 'Non hai i permessi per questa operazione sul timesheet.',
  TIMESHEET_PERIOD_LOCKED: 'Periodo di rendicontazione chiuso: la voce non è modificabile.',
  TIMESHEET_OVERRIDE_REASON_REQUIRED: 'Per modificare un periodo chiuso è obbligatorio indicare una motivazione di override.',
  TIMESHEET_EXPENSES_DISABLED: 'La gestione spese non è abilitata per questa azienda.',
  TIMESHEET_BUSINESS_TRIPS_DISABLED: 'La gestione trasferte non è abilitata per questa azienda.',
  TIMESHEET_DRAFT_INCOMPLETE: 'Per finalizzare la voce servono progetto, attività, descrizione e, dove il progetto la chiede, la causale.',
  TIMESHEET_TEMPLATE_NOT_FOUND: 'Template non trovato.',
  TIMESHEET_INVALID_DATE_RANGE: 'Intervallo date non valido.',
  TIMESHEET_TEMPLATE_INACTIVE: 'Questo template non è attivo.',
  TIMESHEET_TEMPLATE_CUSTOM_NOT_SUPPORTED: 'La ricorrenza «custom» del template non è ancora supportata.',
  TIMESHEET_TEMPLATE_OUT_OF_VALIDITY: 'Le date sono fuori dal periodo di validità del template.',
  TIMESHEET_TEMPLATE_INVALID_RECURRENCE: 'Configurazione di ricorrenza del template non valida.',
  TIMESHEET_LOCK_FORBIDDEN: 'Non hai i permessi per gestire i periodi di chiusura.',
  TIMESHEET_LOCK_NOT_FOUND: 'Periodo di chiusura non trovato.',
  TIMESHEET_UNLOCK_REASON_REQUIRED: 'La motivazione di sblocco è obbligatoria.',
  TIMESHEET_LOCK_ALREADY_UNLOCKED: 'Questo periodo è già stato sbloccato.',
  TIMESHEET_LOCK_OVERLAP: 'Il periodo di chiusura si sovrappone a un altro esistente.',
  TIMESHEET_INVALID_WEEK_START: 'La data di inizio settimana deve essere un lunedì.',
  TIMESHEET_ATTRIBUTE_INVALID_DATATYPE: 'Configurazione del campo personalizzato non valida.',
  TIMESHEET_ATTRIBUTE_VALUE_INVALID: 'Valore del campo personalizzato non coerente con il tipo atteso.',
  /* `requiresTaskAssignment` del progetto (BE v1.272.0): chi ha `project.write` ne è esente. */
  TIMESHEET_TASK_REQUIRED: 'Su questo progetto le ore si registrano su un task o su una voce della WBS: scegli quello su cui hai lavorato.',
  TIMESHEET_TASK_NOT_ASSIGNED: 'Su questo progetto le ore si registrano solo sui task e sulle voci della WBS assegnati a te: scegline uno tuo, o chiedi al responsabile del progetto di assegnartelo.',
  // Turni — «Genera turni» col motore unico (BE v1.272.0)
  SHIFT_GENERATE_OVERWRITE_REMOVED: 'La generazione non sovrascrive i giorni che hanno già un turno. Per rifare un periodo cancella prima i turni di quei giorni, e ricorda che un giorno cancellato a mano non viene rigenerato finché non lo si rimette.',
  SHIFT_ROTATION_INACTIVE: 'La rotazione è disattivata, e i turni si generano solo dalle rotazioni attive: riattivala e riprova.',
  SETTING_KEY_IMMUTABLE: 'Di una regola già salvata non si cambia la chiave: creane una nuova con la chiave giusta ed elimina questa.',
  TIMESHEET_OVERRIDE_EVENT_READ_ONLY: 'Gli eventi di override sono di sola lettura.',
  // Pacchetto HR — modulo SMART-WORKING v2
  SMART_WORKING_NOT_FOUND: 'Richiesta di smart working non trovata.',
  SMART_WORKING_FORBIDDEN: 'Puoi agire sulle tue richieste, e su quelle di una persona solo se sei il suo responsabile diretto o dell’ufficio del personale.',
  SMART_WORKING_INVALID_STATUS_TRANSITION: 'Cambio di stato non consentito per questa richiesta.',
  SMART_WORKING_APPROVAL_DELEGATION_MISSING: 'Manca una delega di approvazione valida.',
  SMART_WORKING_REJECTION_REASON_REQUIRED: 'La motivazione del rifiuto è obbligatoria.',
  SMART_WORKING_EMPLOYEE_TERMINATED: 'Il dipendente non è più attivo.',
  SMART_WORKING_RETROACTIVE_DENIED: 'Richiesta retroattiva non consentita.',
  SMART_WORKING_LEAVE_CONFLICT: 'Le date sono in conflitto con un\'assenza approvata.',
  SMART_WORKING_HOLIDAY_DAY: 'Il periodo è interamente festivo: non serve la richiesta.',
  SMART_WORKING_POLICY_ADVANCE_INSUFFICIENT: 'Preavviso insufficiente secondo la policy.',
  SMART_WORKING_POLICY_WEEKDAY_NOT_ALLOWED: 'Giorno della settimana non ammesso dalla policy.',
  SMART_WORKING_POLICY_CONSECUTIVE_EXCEEDED: 'Superato il numero massimo di giorni consecutivi.',
  SMART_WORKING_POLICY_WEEK_EXCEEDED: 'Superato il limite settimanale della policy.',
  SMART_WORKING_POLICY_MONTH_EXCEEDED: 'Superato il limite mensile della policy.',
  SMART_WORKING_HANDOVER_REQUIRED: 'Sono obbligatorie le note di consegna (handover).',
  SMART_WORKING_SUBSTITUTE_REQUIRED: 'È obbligatorio indicare un sostituto in sede.',
  SMART_WORKING_BLACKOUT_PERIOD: 'Quel giorno è fra quelli in cui l’azienda vuole la presenza in sede: lo smart working non è ammesso.',
  SMART_WORKING_QUORUM_VIOLATION: 'Numero minimo di presenze in sede non rispettato per queste date.',
  SMART_WORKING_QUORUM_WARNING: 'Attenzione: presenze in sede sotto il minimo consigliato.',
  SMART_WORKING_INVALID_DATE_RANGE: 'Intervallo date non valido.',
  SMART_WORKING_INVALID_PART_OF_DAY: 'Frazione di giornata non valida (mezza giornata richiede un solo giorno).',
  SMART_WORKING_INVALID_POLICY_SCOPE: 'Ambito policy o quorum non valido.',
  /* Task 1921: chi può fare smart working. */
  SMART_WORKING_INVALID_ELIGIBLE_AFTER_MONTHS: 'I mesi dall’assunzione vanno da 0 a 240, oppure vuoto per «da subito».',
  EMPLOYEE_SW_ELIGIBILITY_FORBIDDEN: 'Se una persona può fare smart working lo decidono l’ufficio del personale e il suo responsabile diretto. La persona stessa non può cambiarlo.',
  EMPLOYEE_SW_ELIGIBILITY_INVALID: 'Valore non previsto: scegli fra «Segue la regola», «Può già» e «Non può».',
  /* Task 1921: obblighi e solleciti, chiusure aziendali. */
  OBLIGATION_RULE_GROUP_LOCKED: 'L’azienda non ha aperto questo gruppo ai responsabili: le sue regole le scrive l’ufficio del personale.',
  OBLIGATION_RULE_NOT_GROUP_LEADER: 'Puoi scrivere regole solo per i gruppi di cui sei responsabile.',
  OBLIGATION_RULE_GROUP_ONLY: 'Da responsabile di gruppo puoi scrivere solo regole del tuo gruppo: scegli il gruppo nella regola.',
  OBLIGATION_RULE_NOT_ALLOWED: 'Non hai il permesso di scrivere le regole degli obblighi.',
  /* Task 1904: chi personalizza i task, livello per livello. */
  TASK_CONFIG_GROUP_LOCKED: 'L’azienda non ha aperto questo gruppo ai responsabili: bacheca, flusso e regole dei task del gruppo le cambia l’amministratore.',
  TASK_CONFIG_NOT_GROUP_LEADER: 'Puoi personalizzare i task solo dei gruppi di cui sei responsabile.',
  TASK_CONFIG_GROUP_ONLY: 'Da responsabile di gruppo cambi solo il tuo gruppo: quel che vale per tutta l’azienda lo cambia l’amministratore.',
  SETTING_GROUP_LOCK_INVALID: 'Questa scelta sui gruppi non si può salvare: controlla che i gruppi indicati siano di questa azienda.',
  SETTING_LEVEL_NOT_ALLOWED: 'Questa regola non si decide a questo livello.',
  BUSINESS_GROUP_NOT_FOUND: 'Il gruppo non è di questa azienda, o non esiste più.',
  OBLIGATION_CONFIRM_NOT_APPLICABLE: 'Questa scadenza non si conferma: si rispetta facendo quel che chiede.',
  LEAVE_REQUEST_FROM_COMPANY_CLOSURE: 'Questa richiesta nasce da una chiusura aziendale: non si cambia da sola. Si cambia la chiusura, in «Regole di lavoro → Assenze».',
  SMART_WORKING_MULTIPLE_DEFAULTS: 'Esiste già una policy di default attiva per questa azienda.',
  SMART_WORKING_BLACKOUT_INVALID_ENFORCEMENT: 'Modalità di applicazione del blackout non valida.',
  SMART_WORKING_ATTACHMENT_TYPE_MISMATCH: 'Tipo di allegato non coerente.',
  SMART_WORKING_ATTACHMENT_LOCKED: 'Impossibile eliminare l\'allegato: la richiesta è approvata.',
  SMART_WORKING_BALANCE_READ_ONLY: 'Il saldo smart working è di sola lettura.',
  SMART_WORKING_EVENT_READ_ONLY: 'Lo storico eventi è di sola lettura.',
  EMPLOYEE_WORK_LOCATION_CHANGE_READ_ONLY: 'Lo storico dei cambi sede di lavoro è di sola lettura.',
  SMART_WORKING_PLANNING_NOT_FOUND: 'Pianificazione non trovata.',
  PLANNING_ENTRY_CONSUMED: 'Il piano è già stato consumato da una registrazione di ore: non è più modificabile. Si può annullare, non eliminare.',
  /* BE pianificazione su task §3: la riga consuma un piano fatto per un altro task. */
  PLANNING_ENTRY_TASK_MISMATCH: 'Il piano collegato è per un altro task: scegli il task del piano, oppure togli il collegamento al piano.',
  PLANNING_WRITE_NOT_ALLOWED: 'Puoi pianificare il tuo tempo, e quello di una persona solo se sei il suo responsabile diretto o dell’ufficio del personale.',
  PLANNING_INVALID_PLANNED_MINUTES: 'I minuti pianificati devono essere maggiori di zero.',
  PLANNING_SERIES_NOT_FOUND: 'Serie di pianificazione non trovata.',
  SMART_WORKING_PLANNING_INVALID_STATUS_TRANSITION: 'Cambio di stato della pianificazione non consentito.',
  SMART_WORKING_PLANNING_HORIZON_EXCEEDED: 'Le date superano l\'orizzonte di pianificazione consentito.',
  SMART_WORKING_PLANNING_EMPTY: 'La pianificazione non contiene richieste.',
  SMART_WORKING_PLANNING_ROWS_BLOCKED: 'Alcuni giorni non rispettano le regole dello smart working, e la pianificazione non è partita: togli o cambia quei giorni e inviala di nuovo.',
  SMART_WORKING_PLANNING_REQUESTS_NOT_DRAFT: 'Tutte le richieste della pianificazione devono essere in bozza.',
  SMART_WORKING_PLANNING_DUPLICATE_DATE: 'Data duplicata nella pianificazione.',
  // Service Report
  SERVICE_REPORT_NOT_FOUND: 'Rapportino non trovato.',
  SERVICE_REPORT_INVALID_STATUS_TRANSITION: 'Operazione non consentita per lo stato attuale del rapportino.',
  SERVICE_REPORT_CONFIG_NOT_FOUND: 'Nessuna configurazione di invio disponibile: selezionane una o impostane una di default.',
  SERVICE_REPORT_INVALID_CHANNEL: 'Configurazione di invio non valida per questo canale.',
  SERVICE_REPORT_ACKNOWLEDGE_FORBIDDEN: 'Non hai i permessi per confermare la ricezione del rapportino.',
  SERVICE_REPORT_CANCEL_REASON_REQUIRED: 'La motivazione dell\'annullamento è obbligatoria.',
  SERVICE_REPORT_PLACEHOLDER_UNRESOLVED: 'Uno o più segnaposto del template non sono stati risolti.',
  SERVICE_REPORT_STATUS_INVALID_CATEGORY: 'Categoria o ambito dello stato rapportino non validi.',
  SERVICE_REPORT_STATUS_CODE_DUPLICATE: 'Esiste già uno stato rapportino con questo codice.',
  SERVICE_REPORT_INVALID_TRIGGER: 'Tipo di trigger della configurazione non valido.',
  SERVICE_REPORT_TEMPLATE_INVALID: 'Il template del contenuto non è un JSON valido.',
  SERVICE_REPORT_MULTIPLE_DEFAULTS: 'Esiste già una configurazione di default attiva per questa azienda.',
  SERVICE_REPORT_AGGREGATION_NOT_SUPPORTED: 'Tipo di aggregazione non supportato (ammesso solo per voce singola).',
  SERVICE_REPORT_CANNOT_DELETE_SENT: 'Impossibile eliminare un rapportino già inviato.',
  // Meeting Room
  MEETING_ROOM_BOOKING_NOT_FOUND: 'Prenotazione sala non trovata.',
  /* Dal 28-10-2026 (MR-01) lo stesso codice arriva anche a chi annulla una prenotazione che non è sua
     e non è della segreteria: annullare la propria è sempre permesso. */
  MEETING_ROOM_APPROVE_FORBIDDEN: 'Questa operazione la fa chi ha prenotato la sala o la segreteria: le prenotazioni degli altri non le puoi confermare né annullare.',
  MEETING_ROOM_BOOKING_FORBIDDEN: 'Puoi cambiare o togliere solo le prenotazioni di sala fatte da te: per quelle degli altri chiedi alla segreteria.',
  MEETING_ROOM_BOOKING_INVALID_STATUS: 'Operazione non consentita per lo stato attuale della prenotazione.',
  MEETING_ROOM_CANCELLATION_REASON_REQUIRED: 'Annullando la prenotazione di un’altra persona serve un motivo: le arriva insieme all’avviso.',
  MEETING_ROOM_PARTICIPANT_NOT_FOUND: 'Partecipante non trovato.',
  MEETING_ROOM_RESPOND_FORBIDDEN: 'Puoi rispondere solo agli inviti che ti riguardano.',
  MEETING_ROOM_PARTICIPANT_INVALID_RESPONSE: 'Risposta all\'invito non valida.',
  MEETING_ROOM_INVALID_CAPACITY: 'La capienza della sala deve essere maggiore di zero.',
  MEETING_ROOM_BOOKING_INVALID_DATE_RANGE: 'Intervallo orario non valido (l\'inizio deve precedere la fine).',
  MEETING_ROOM_BOOKING_INVALID_ATTENDEE_COUNT: 'Numero di partecipanti non valido.',
  MEETING_ROOM_BOOKING_INVALID_LAYOUT: 'Disposizione sala non valida.',
  MEETING_ROOM_CAPACITY_EXCEEDED: 'I partecipanti superano la capienza della sala.',
  /* Dal 28-10-2026 (MR-01) la sala la occupa solo una prenotazione confermata: le richieste in attesa
     non bloccano, quindi il conflitto è sempre con una confermata. */
  MEETING_ROOM_BOOKING_CONFLICT: 'La sala è già occupata da una prenotazione confermata in questo orario: scegli un’altra fascia o un’altra sala.',
  MEETING_ROOM_CATERING_NOTES_REQUIRED: 'Indica le note per il catering richiesto.',
  MEETING_ROOM_SETUP_NOTES_REQUIRED: 'Indica le note per l\'allestimento richiesto.',
  MEETING_ROOM_BOOKING_RECURRENCE_PATTERN_REQUIRED: 'Specifica la ricorrenza per una prenotazione ricorrente.',
  MEETING_ROOM_PARTICIPANT_INVALID_ROLE: 'Ruolo partecipante non valido.',
  // Fleet
  FLEET_VEHICLE_INVALID_LICENSE_PLATE: 'Targa non valida (formato non riconosciuto).',
  FLEET_VEHICLE_LICENSE_PLATE_DUPLICATE: 'Esiste già un mezzo con questa targa.',
  FLEET_VEHICLE_INVALID_CATEGORY: 'Categoria mezzo non valida.',
  FLEET_VEHICLE_INVALID_FUEL_TYPE: 'Tipo di alimentazione non valido.',
  FLEET_VEHICLE_INVALID_OWNERSHIP: 'Tipo di possesso non valido.',
  FLEET_VEHICLE_BLOCKED_REASON_REQUIRED: 'Indica il motivo del blocco del mezzo.',
  FLEET_ASSIGNMENT_TYPE_INVALID: 'Tipo di assegnazione non valido.',
  FLEET_ASSIGNMENT_INVALID_DATE_RANGE: 'Intervallo date dell\'assegnazione non valido.',
  FLEET_ASSIGNMENT_BENEFIT_AMOUNT_REQUIRED: 'Indica l\'importo del fringe benefit mensile.',
  FLEET_INSURANCE_INVALID_COVERAGE: 'Tipo di copertura assicurativa non valido.',
  FLEET_INSURANCE_INVALID_DATE_RANGE: 'Intervallo date della polizza non valido.',
  FLEET_INSURANCE_MULTIPLE_ACTIVE: 'Esiste già una polizza attiva per questo mezzo.',
  FLEET_INSPECTION_INVALID_RESULT: 'Esito revisione non valido.',
  FLEET_INSPECTION_INVALID_DATE_RANGE: 'La data revisione deve precedere la scadenza.',
  FLEET_MAINTENANCE_INVALID_TYPE: 'Tipo di manutenzione non valido.',
  FLEET_COST_INVALID_TYPE: 'Voce di costo non valida.',
  FLEET_COST_INVALID_PERIOD: 'Periodicità del costo non valida.',
  FLEET_INCIDENT_INVALID_TYPE: 'Tipo di sinistro non valido.',
  FLEET_INCIDENT_SEVERITY_INVALID: 'Gravità del sinistro non valida.',
  FLEET_INCIDENT_INVALID_STATUS: 'Stato del sinistro non valido.',
  FLEET_INCIDENT_DAMAGE_AREA_INVALID: 'Zona del danno non valida: scegline una dall’elenco.',
  FLEET_INCIDENT_BOOKING_NOT_FOUND: 'La prenotazione indicata non esiste.',
  /* Il caso del copia-incolla: il danno finirebbe sul viaggio — e quindi sulla persona — sbagliata. */
  FLEET_INCIDENT_BOOKING_VEHICLE_MISMATCH: 'La prenotazione indicata è di un altro mezzo: scegline una di questo veicolo.',
  FLEET_TRAFFIC_VIOLATION_INVALID_TYPE: 'Tipo di violazione non valido.',
  FLEET_TRAFFIC_VIOLATION_DISPUTE_REASON_REQUIRED: 'Indica il motivo della contestazione.',
  // Travel
  TRAVEL_BOOKING_NOT_FOUND: 'Trasferta non trovata.',
  TRAVEL_BOOKING_FORBIDDEN: 'Non hai i permessi per questa operazione sulla trasferta.',
  TRAVEL_BOOKING_INVALID_STATUS_TRANSITION: 'Operazione non consentita per lo stato attuale della trasferta.',
  TRAVEL_BOOKING_REJECTION_REASON_REQUIRED: 'La motivazione del rifiuto è obbligatoria.',
  TRAVEL_BOOKING_CANCELLATION_REASON_REQUIRED: 'La motivazione dell\'annullamento è obbligatoria.',
  TRAVEL_BOOKING_CONFIRMATION_REFERENCE_REQUIRED: 'Indica un codice prenotazione o un riferimento esterno per confermare.',
  TRAVEL_QUOTE_NOT_FOUND: 'Preventivo non trovato.',
  TRAVEL_BOOKING_INVALID_TYPE: 'Tipo di trasferta non valido.',
  TRAVEL_BOOKING_INVALID_PAYER_TYPE: 'Modalità di pagamento non valida.',
  TRAVEL_BOOKING_INVALID_PRIORITY: 'Priorità non valida.',
  TRAVEL_BOOKING_INVALID_CURRENCY: 'Valuta non valida (usa il codice ISO, es. EUR).',
  TRAVEL_BOOKING_INVALID_DATE_RANGE: 'Intervallo date della trasferta non valido.',
  TRAVEL_BOOKING_INVALID_AMOUNT: 'Importo non valido.',
  TRAVEL_BOOKING_PURPOSE_REQUIRED: 'La motivazione della trasferta è obbligatoria.',
  TRAVEL_QUOTE_INVALID_AMOUNT: 'Importo del preventivo non valido.',
  TRAVEL_QUOTE_INVALID_CURRENCY: 'Valuta del preventivo non valida.',
  TRAVEL_LINK_INVALID_AMOUNT: 'Importo del collegamento spesa non valido.',
  TRAVEL_LINK_BOOKING_NOT_CONFIRMED: 'Puoi collegare le spese solo a una trasferta confermata.',
  TRAVEL_POLICY_INVALID_TIER: 'Fascia policy non valida.',
  TRAVEL_POLICY_TIER_INVALID_DATE_RANGE: 'Intervallo date della fascia policy non valido.',
  TRAVEL_POLICY_TIER_OVERLAP: 'La fascia policy si sovrappone a un\'altra per lo stesso dipendente.',
  TRAVEL_BOOKING_PARTICIPANT_INVALID_AMOUNT: 'Importo del partecipante non valido.',
  // Clock-in / Presenze enterprise
  ATTENDANCE_INVALID_EVENT: 'Tipo di timbratura non valido.',
  ATTENDANCE_INVALID_WORK_LOCATION: 'Luogo di lavoro non valido.',
  ATTENDANCE_INVALID_METHOD: 'Metodo di autenticazione della timbratura non valido.',
  ATTENDANCE_SITE_REQUIRED: 'Per la timbratura in sede è richiesta la sede.',
  ATTENDANCE_TRIP_LOCATION_REQUIRED: 'Per la timbratura in trasferta è richiesta la località.',
  ATTENDANCE_METHOD_METADATA_MISSING: 'Mancano i dati richiesti dal metodo di timbratura scelto.',
  ATTENDANCE_INVALID_SEQUENCE: 'Sequenza timbrature non valida (verifica entrata/uscita/pausa).',
  ATTENDANCE_LEAVE_DAY: 'Nel giorno selezionato risulta un\'assenza approvata.',
  ATTENDANCE_OVERRIDE_FORBIDDEN: 'Non hai i permessi per forzare/correggere la timbratura.',
  ATTENDANCE_SESSION_READ_ONLY: 'Il riepilogo giornaliero delle presenze è di sola lettura.',
  ATTENDANCE_SOURCE_FORBIDDEN: 'Non hai i permessi per gestire le sorgenti di timbratura.',
  ATTENDANCE_SOURCE_INVALID_TYPE_METADATA: 'Configurazione della sorgente di timbratura non valida.',
  // LEAVE conformance (BE v1.48–v1.50)
  LEAVE_COMMENT_FORBIDDEN: 'Puoi modificare o eliminare solo i commenti che hai scritto tu.',
  LEAVE_REASON_FORBIDDEN: 'Non hai il permesso di gestire le motivazioni di assenza (richiede il claim leave.reason.write).',
  LEAVE_INVALID_CATEGORY: 'Categoria del tipo di assenza non valida: ammesse ferie, permesso, malattia o altro.',
  LEAVE_EVENT_LIMIT_EXCEEDED: 'Hai raggiunto il numero massimo di eventi consentiti nell\'anno per questo tipo di assenza.',
  LEAVE_EMPLOYEE_TERMINATED: 'Il dipendente non è più attivo: impossibile inviare la richiesta.',
  // Timesheet + activity-reason enforcement (BE v1.54–v1.55)
  TIMESHEET_ATTRIBUTE_REQUIRED: 'Per finalizzare la voce mancano alcuni campi personalizzati obbligatori.',
  TIMESHEET_EXPORT_FORMAT_UNSUPPORTED: 'Formato di esportazione non supportato: usa XLSX o CSV.',
  REASON_REQUIRED_FOR_NON_BILLABLE: 'Per le ore non fatturabili è obbligatoria una causale attività.',
  REASON_REQUIRES_PROJECT: 'Questa causale attività richiede un progetto associato.',
  REASON_REQUIRES_JUSTIFICATION: 'Questa causale attività richiede una nota/giustificazione.',
  REASON_INACTIVE: 'Questa causale attività non è più attiva.',
  // Il `detail` del BE nomina la causale: `getUserMessage()` lo preferisce a questo testo,
  // che resta per i casi in cui il detail manchi.
  REASON_BLOCKS_SAVE: 'Questa causale è marcata come non rendicontabile: le righe che la usano non possono essere salvate. Scegline un\'altra.',
  VPS_ZERO_BILLING_HOURS: 'Le ore fatturabili sono a zero: questa riga non può essere inviata al VPS del cliente.',
  // Mezzo proprio e rimborso km (BE v1.100.0). Il `meta` del BE porta i dati del conflitto
  // (`conflictingRateId`, `rateIds`): dove serve, la UI li mostra al posto di questi testi.
  TIMESHEET_OWN_VEHICLE_REQUIRED: 'Hai indicato «mezzo proprio» ma non quale: senza targa il rimborso non è giustificabile in caso di verifica.',
  TIMESHEET_OWN_VEHICLE_DISABLED: 'L\'uso del mezzo proprio è disattivato per questa azienda: scegli un altro mezzo o chiedi all\'amministratore di abilitarlo.',
  TIMESHEET_INVALID_TRIP_VEHICLE_MODE: 'Tipo di mezzo non riconosciuto: ammessi mezzo aziendale, mezzo proprio, mezzo del cliente o nessun mezzo.',
  TIMESHEET_INVALID_KM_PAYER: 'Il rimborso può essere a carico del dipendente, dell\'azienda o del cliente.',
  TIMESHEET_INVALID_DISTANCE: 'I chilometri percorsi non possono essere negativi.',
  OWN_VEHICLE_AUTHORIZATION_FORBIDDEN: 'L\'uso del mezzo proprio per servizio lo autorizza il tuo responsabile: non puoi autorizzarti da solo.',
  OWN_VEHICLE_PLATE_REQUIRED: 'La targa è obbligatoria: è ciò che rende il rimborso giustificabile in caso di verifica.',
  OWN_VEHICLE_INVALID_CATEGORY: 'Fascia del mezzo non valida: scegline una dall\'elenco.',
  OWN_VEHICLE_INVALID_FUEL_TYPE: 'Alimentazione non valida: scegline una dall\'elenco.',
  MILEAGE_RATE_OVERLAP: 'Esiste già una tariffa con la stessa specificità valida negli stessi giorni. Chiudi la validità di quella prima di aprire questa.',
  MILEAGE_RATE_AMBIGUOUS: 'Due tariffe di pari specificità coprono la data di questa registrazione: finché sono entrambe valide il rimborso non è calcolabile. Correggi la validità di una delle due.',
  MILEAGE_RATE_INVALID_CATEGORY: 'Fascia non valida: scegline una dall\'elenco.',
  MILEAGE_RATE_INVALID_CURRENCY: 'La valuta deve essere un codice ISO di 3 lettere (es. EUR).',
  MILEAGE_RATE_INVALID_AMOUNT: 'La tariffa al chilometro deve essere maggiore di zero.',
  MILEAGE_RATE_INVALID_DATE_RANGE: 'La data di inizio validità deve precedere quella di fine.',
  // Causali di sistema (BE VPS-POLICY-INHERITANCE §3): le LEAVE_* sono generate dalle
  // assenze approvate, quindi governate dal seed e non modificabili da fuori.
  ACTIVITY_REASON_ISSYSTEM_RESERVED: 'Solo il sistema può creare causali di sistema. Togli il contrassegno e riprova.',
  ACTIVITY_REASON_SYSTEM_READONLY: 'Questa è una causale di sistema: la usa la generazione automatica delle ore dalle assenze approvate, quindi non si può modificare né eliminare. Creane una nuova se ti serve una variante.',
  // Bucket A — gate sede di lavoro (BE v1.51)
  EMPLOYEE_WORK_LOCATION_FORBIDDEN: 'Non hai il permesso di cambiare la sede di lavoro predefinita.',
  // Vehicle-booking enterprise (BE v1.60 — FLEET §1.3/§2.1)
  FLEET_BOOKING_NOT_FOUND: 'Prenotazione mezzo non trovata.',
  FLEET_BOOKING_FORBIDDEN: 'Non hai i permessi per questa operazione sulla prenotazione mezzo.',
  FLEET_BOOKING_INVALID_DATE_RANGE: 'Intervallo date della prenotazione non valido (la fine deve seguire l\'inizio).',
  FLEET_BOOKING_INVALID_PRIORITY: 'Priorità non valida (ammesse: normale, urgente).',
  FLEET_BOOKING_INVALID_STATUS_CATEGORY: 'Stato della prenotazione non valido.',
  FLEET_BOOKING_REJECTION_REASON_REQUIRED: 'La motivazione del rifiuto è obbligatoria.',
  FLEET_BOOKING_VEHICLE_REQUIRED: 'Seleziona un mezzo da assegnare alla prenotazione.',
  FLEET_VEHICLE_NOT_FOUND: 'Mezzo non trovato.',
  FLEET_VEHICLE_BLOCKED: 'Il mezzo selezionato è bloccato e non è assegnabile.',
  FLEET_BOOKING_OVERLAP: 'Il mezzo è già prenotato in questo intervallo di date.',
  FLEET_BOOKING_OVERLAP_OVERRIDDEN: 'Sovrapposizione con un\'altra prenotazione forzata dall\'amministratore.',
  FLEET_INSURANCE_EXPIRED: 'La polizza assicurativa del mezzo è scaduta.',
  FLEET_INSURANCE_EXPIRING_DURING_BOOKING: 'Attenzione: la polizza assicurativa scade durante la prenotazione.',
  FLEET_INSPECTION_EXPIRED: 'La revisione del mezzo è scaduta.',
  FLEET_INSPECTION_EXPIRING_DURING_BOOKING: 'Attenzione: la revisione del mezzo scade durante la prenotazione.',
  FLEET_BOOKING_PARTICIPANTS_EXCEED_SEATS: 'I partecipanti superano i posti disponibili sul mezzo.',
  FLEET_BOOKING_START_DATA_REQUIRED: 'Per avviare il viaggio indica livello carburante e chilometraggio iniziali.',
  FLEET_BOOKING_INVALID_FUEL_LEVEL: 'Livello carburante non valido.',
  FLEET_BOOKING_END_DATA_REQUIRED: 'Per chiudere il viaggio indica livello carburante e chilometraggio finali.',
  FLEET_BOOKING_ODOMETER_NOT_MONOTONE: 'Il chilometraggio finale non può essere inferiore a quello iniziale.',
  FLEET_BOOKING_ODOMETER_INCONSISTENT: 'Attenzione: il chilometraggio rilevato non è coerente con quello atteso.',
  FLEET_BOOKING_CANCELLATION_REASON_REQUIRED: 'La motivazione dell\'annullamento è obbligatoria.',
  FLEET_BOOKING_INVALID_STATUS_TRANSITION: 'Operazione non consentita per lo stato attuale della prenotazione.',
  FLEET_BOOKING_PARTICIPANT_DUPLICATE: 'Questo dipendente è già tra i partecipanti della prenotazione.',
  FLEET_BOOKING_PARTICIPANT_IS_REQUESTER: 'Il richiedente non può essere aggiunto anche come partecipante.',
  FLEET_BOOKING_PARTICIPANT_INVALID_ROLE: 'Ruolo partecipante non valido (ammessi: conducente, passeggero, navigatore).',
  FLEET_INCIDENT_ATTACHMENT_TYPE_INVALID: 'Tipo di allegato non valido: ammessi foto del danno, documento o altro.',

  // ── Giustificativi e nota spese (EXPENSE-REIMBURSEMENT). I due 422 «di forma» arrivano con
  // `meta.allowedValues`: dove c'è spazio si mostrano quelli, questi testi sono la rete.
  INVALID_ATTACHMENT_TYPE: 'Tipo di allegato non previsto: scegline uno dall\'elenco.',
  INVALID_ATTACHMENT_AMOUNT: 'L\'importo di un giustificativo non può essere negativo.',
  TRAVEL_ATTACHMENT_BOOKING_REQUIRED: 'Il documento va collegato a una prenotazione trasferta.',
  TRAVEL_ATTACHMENT_FILE_REQUIRED: 'Serve il file, oppure un indirizzo da cui scaricarlo.',
  FLEET_BOOKING_INVALID_PAYER_TYPE: 'Pagatore non valido: prepagato azienda, anticipo del dipendente o a carico del cliente.',
  FLEET_BOOKING_DAY_INVALID_AMOUNT: 'L\'importo imputato a una giornata non può essere negativo.',
  EXPENSE_REPORT_ALREADY_EXISTS: 'Per quel mese esiste già una nota spese non più modificabile.',
  EXPENSE_REPORT_NOT_EDITABLE: 'La nota si modifica solo finché è in bozza.',
  EXPENSE_REPORT_EMPTY: 'In quel mese non ci sono giornate con spese anticipate da te: non c\'è nulla da mettere in nota.',
  EXPENSE_REPORT_INVALID_TRANSITION: 'Questo passaggio non è possibile dallo stato attuale della nota.',
  EXPENSE_MISSING_JUSTIFICATION: 'Alcune voci di spesa superano la soglia e non hanno un giustificativo del tipo corrispondente: allega le ricevute e ricomponi la nota.',
  // Il 422 arriva anche sulla DELETE della riga, non solo sulla PATCH: la fotografia
  // punterebbe a una giornata sparita.
  ENTRY_LOCKED_BY_EXPENSE_REPORT: 'Questa giornata fa parte di una nota spese già inviata: gli importi anticipati sono congelati. Ore, descrizione e progetto restano modificabili.',
  EXPENSE_REPORT_NOT_APPROVER: 'Non puoi decidere su questa nota spese: non è di un tuo sottoposto e non hai una delega attiva.',
  EXPENSE_REPORT_PAY_FORBIDDEN: 'Serve il permesso di liquidazione per segnare una nota come rimborsata.',
  EXPENSE_REPORT_REJECTION_REASON_REQUIRED: 'Per respingere serve il motivo: è quello che il titolare legge per correggere.',
  EXPENSE_REPORT_PAYROLL_PERIOD_REQUIRED: 'Indica con quale busta paga è uscito il rimborso.',
  EXPENSE_REPORT_DISABLED: 'La nota spese non è attiva su questa azienda: la accende un amministratore dalle impostazioni di rendicontazione.',
  EXPENSE_REPORT_READ_ONLY: 'Le note spese non si creano né si cancellano a mano: si compongono dal mese.',
  EXPENSE_REPORT_FORBIDDEN: 'Non hai i permessi per questa operazione sulla nota spese.',
  EXPENSE_REPORT_NOT_FOUND: 'Nota spese inesistente.',
  EXPENSE_REPORT_EMPLOYEE_REQUIRED: 'Manca il dipendente per cui comporre la nota.',
  EXPENSE_REPORT_INVALID_PERIOD: 'Periodo non valido: controlla mese e anno.',
  EXPENSE_JUSTIFICATION_THRESHOLD_INVALID: 'La soglia dei giustificativi non può essere negativa.',
  EXPENSE_AUTO_COMPOSE_DAY_INVALID: 'Il giorno di composizione automatica deve essere fra 1 e 28.',

  // ── Sorgenti di timbratura (CLOCK-IN §W1). Il 403 ha già il suo messaggio più su.
  ATTENDANCE_REVOKE_REASON_REQUIRED: 'Per revocare serve il motivo: è ciò che distingue «tag smarrito» da «dipendente cessato» da «sede chiusa».',
  // Il `meta` porta `revokedAt` e `revokedReason` della revoca precedente: dove c'è spazio si
  // mostrano quelli, che dicono molto più di questo testo.
  ATTENDANCE_SOURCE_ALREADY_REVOKED: 'Questa sorgente risulta già revocata.',
  ATTENDANCE_NFC_TAG_ALREADY_REGISTERED: 'Questo tag NFC è già registrato e attivo: revoca la registrazione precedente prima di riprogrammarlo.',
  /* Arriva con **401**, e non è una sessione scaduta: il testo non deve mai suggerire di
     rifare il login. Il `meta.reason` distingue i tre casi (`signature_mismatch` = tag
     clonato o modificato, `revoked` = tag revocato, `malformed` = sorgente mai programmata) e
     dove c'è spazio si mostra quello — le azioni sono diverse. */
  ATTENDANCE_NFC_SIGNATURE_INVALID: 'Questo tag non è valido: la firma non corrisponde. Rivolgiti all’ufficio del personale — la sessione è ancora attiva, non serve rifare l’accesso.',
  /* Il caso tipico non è un tag inesistente: è il **codice fisico** del chip mandato al posto di
     quello registrato dall'HR. Il rimedio è dell'HR, non di chi timbra. */
  ATTENDANCE_SOURCE_NOT_FOUND: 'Questo punto di timbratura non risulta registrato: segnalalo all’ufficio del personale, che deve censire il tag.',
  PENDING_APPROVAL_FORBIDDEN: 'Puoi vedere solo la tua coda di approvazione.',

  // ── Monte ore d'ordine (ORDERS-LEGACY-PARITY). I tre codici di sforamento portano nel
  // `meta` i numeri della riga: dove c'è spazio per mostrarli si usa OrderOverrunModal,
  // che è molto più utile di questi messaggi generici — qui restano come rete di sicurezza.
  ORDER_LINE_HOURS_EXCEEDED: 'Le ore superano il monte ore venduto su questa riga d\'ordine, e il progetto è configurata per bloccare lo sforamento. Serve l\'autorizzazione di un responsabile.',
  ORDER_LINE_CONSUMED: 'Questa riga d\'ordine è esaurita o chiusa: non accetta altre ore fatturabili.',
  ORDER_OVERRUN_REASON_REQUIRED: 'Puoi superare il monte ore, ma devi indicare una motivazione.',
  ORDER_LINE_NOT_FOUND: 'Riga d\'ordine non trovata.',
  PROJECT_NEXT_SELF_REFERENCE: 'Un progetto non può proseguire su sé stesso.',
  PROJECT_NEXT_CYCLE_DETECTED: 'I progetti successivi formano un anello: seguendo la catena si tornerebbe al punto di partenza.',
  SUB_ACTIVITY_PENDING_APPROVAL: 'Questa voce di attività è in attesa di conferma: non si può ancora rendicontare su di essa.',
  SUB_ACTIVITY_NOT_ASSIGNED: 'Questa voce di attività non è fra quelle assegnate a te.',
  BILLING_SERVICE_ITEM_UNRESOLVED: 'Non è stato possibile determinare l\'articolo da fatturare: manca sia sulla voce sia sulla riga d\'ordine, e nessuna regola di fatturazione abbina.',
  // Incontrato provando il percorso di scrittura sul dev: questo tenant ha
  // `requireSubActivityOnTimesheet` attivo e il 422 arrivava senza messaggio nostro.
  SUB_ACTIVITY_REQUIRED: 'Manca l’attività: scegli l’«Attività» del progetto su cui hai lavorato.',
  SUB_ACTIVITY_SELF_REFERENCE: 'Un’attività non può essere madre di sé stessa.',
  SUB_ACTIVITY_PARENT_NOT_FOUND: 'L’attività madre scelta non esiste più: ricarica e scegline un’altra.',
  SUB_ACTIVITY_PARENT_PROJECT_MISMATCH: 'L’attività madre scelta appartiene a un altro progetto.',
  SUB_ACTIVITY_PARENT_INACTIVE: 'L’attività madre scelta è disattivata: riattivala prima di metterle sotto delle figlie.',
  SUB_ACTIVITY_CYCLE_DETECTED: 'Con questa attività madre la gerarchia girerebbe in tondo: scegline una che non stia sotto questa voce.',
  SUB_ACTIVITY_HAS_CHILDREN: 'L’attività ha delle figlie: elimina prima quelle, oppure disattivala.',
  SUB_ACTIVITY_HAS_TIMESHEETS: 'Sull’attività ci sono ore registrate: non si elimina, si disattiva.',
  SUB_ACTIVITY_NOT_FOUND: 'Attività non trovata: qualcuno potrebbe averla eliminata.',
  SUB_ACTIVITY_APPROVE_FORBIDDEN: 'Confermare le attività spetta a chi può modificare il progetto o al suo responsabile.',
  SUB_ACTIVITY_IDS_REQUIRED: 'Nessuna attività da confermare.',
  SUB_ACTIVITY_PROJECT_MISMATCH: 'L’attività scelta non appartiene al progetto della registrazione.',
  SUB_ACTIVITY_INACTIVE: 'L’attività scelta è disattivata: scegline un’altra.',

  // ── Consultazione dei log. I due 422 di finestra la pagina li previene lato client
  // (`validaFinestra`), ma restano qui perché una chiamata da altrove non deve mostrare
  // il codice grezzo.
  LOG_INVALID_RANGE: 'La data di fine del periodo precede quella di inizio.',
  LOG_RANGE_TOO_WIDE: 'Il periodo supera i 92 giorni interrogabili in una sola volta: restringilo.',
  // v1.118.0: prima un valore sbagliato veniva ignorato in silenzio e la risposta tornava
  // l'insieme COMPLETO — un «falso pieno» che faceva sospettare il sistema, non il filtro.
  LOG_INVALID_STATUS_CLASS: 'Classe di status non valida: ammesse 2xx, 3xx, 4xx, 5xx e «senza stato».',
  LOG_INVALID_METHOD: 'Metodo HTTP non valido: controlla di non aver scritto un refuso.',
  LOG_TENANT_OUT_OF_SCOPE: 'L’azienda richiesta è fuori dal tuo perimetro: puoi filtrare solo dentro il tuo sotto-albero.',
  LOG_TRACE_ID_REQUIRED: 'Serve un traceId per aprire la catena di una richiesta.',
  LOG_TRACE_NOT_FOUND: 'Nessuna riga con questo traceId nel tuo perimetro. Può esistere su un’azienda che non vedi.',
  LOG_TENANT_UNRESOLVED: 'Non è stato possibile determinare l’azienda dalla sessione corrente.',
  // 503 e non 500 per scelta del BE: distingue «archivio irraggiungibile» da «richiesta
  // sbagliata», così l'operatore sa che non c'è niente da correggere nei filtri.
  LOG_STORE_UNAVAILABLE: 'L\'archivio dei log non è raggiungibile: è un problema di infrastruttura, non della tua ricerca. Riprova fra qualche minuto.',
  // v1.123.0, filtri salvati. Il nome è l'unica cosa che si rilegge fra sei mesi: il server lo
  // pretende, e la nostra maschera lo pretende prima — questo copre chi arriva da altrove.
  LOG_PRESET_NAME_REQUIRED: 'Un filtro salvato ha bisogno di un nome per essere ritrovato.',
  // Il messaggio dice cosa fare invece che limitarsi a negare: su un filtro condiviso la via
  // d'uscita è duplicarlo, che è un gesto che l'utente può compiere da solo.
  LOG_PRESET_NOT_OWNER: 'Questo filtro salvato l\'ha creato un\'altra persona: puoi usarlo, ma per cambiarlo salvane una copia tua.',

  /* ── I due rifiuti che si somigliano e non lo sono ──

     `CLAIM_MISSING` è il 401 del muro dei claim (`JwtClaimsValidationMiddleware`): l'utenza non ha
     il permesso per quella risorsa in quell'azienda. Arriva **con** un corpo, ed è per questo che
     `client.ts` non lo tratta come sessione scaduta: senza il `code` avremmo rinnovato il token a
     vuoto e mostrato un logout al posto di un divieto. Il server manda anche un `detail` che nomina
     l'entità, e lo lasciamo passare — «GET su 'Task'» a chi amministra dice esattamente cosa
     concedere, ed è più utile di qualunque parafrasi nostra.

     `TASK_PERIMETER_REQUIRED` è un 403 e vuol dire l'opposto: il permesso c'è, manca la **domanda**.
     L'elenco dei task non ha un perimetro per-riga — ne ha cinque legittimi (i miei, la mia coda, le
     code dei miei collaboratori, quelli che seguo, per assegnatario) e il sesto, «tutti», è un
     claim a parte. Quindi il server rifiuta invece di rispondere con una lista vuota, che si
     leggerebbe «non ci sono task»: una bugia che manda a cercare un guasto che non esiste. Se questo
     messaggio compare, il difetto è nostro — una schermata che ha smesso di mandare il predicato. */
  CLAIM_MISSING: 'Non hai il permesso per questa sezione in questa azienda. Chiedi a chi amministra di aggiungertelo: il nome del permesso è nel dettaglio qui sopra.',
  TASK_PERIMETER_REQUIRED: 'Questa pagina ha chiesto l’elenco dei task senza dire quali. Segnalalo: è un difetto della pagina, non un errore tuo.',
  TASK_EFFECTIVE_DUE_RANGE_REQUIRED: 'Questa pagina ha chiesto i task per scadenza senza dire fra quali date. Segnalalo: è un difetto della pagina, non un errore tuo.',

  /* ── Modulo Task, assi dell'assegnazione (BE v1.146.0) ──
     Il 409 della presa in carico è una corsa persa, non un errore dell'utente: il messaggio
     lo dice, perché «operazione non consentita» farebbe ritentare all'infinito. */
  /* ── La rinomina di un'etichetta (BE v1.177.1) ──
     Il duplicato è il rifiuto che conta, e il messaggio dice **perché** esiste: due righe con lo
     stesso nome sono i sinonimi per cui la creazione è gated, e allineare i task per nome non
     saprebbe quale delle due seguire. */
  TASK_LABEL_NAME_REQUIRED: 'Scrivi il nuovo nome dell’etichetta.',
  TASK_LABEL_NAME_TOO_LONG: 'Il nome dell’etichetta è troppo lungo: al massimo 100 caratteri.',
  TASK_LABEL_PROJECT_WITHOUT_CUSTOMER: 'Un’etichetta con un progetto deve avere anche il cliente: scegli prima il cliente.',
  TASK_LABEL_PROJECT_CUSTOMER_MISMATCH: 'Il progetto scelto appartiene a un altro cliente: scegli un progetto di questo cliente.',
  TASK_LABEL_CUSTOMER_OTHER_TENANT: 'Il cliente scelto non è un cliente di questa azienda.',
  TASK_LABEL_PROJECT_OTHER_TENANT: 'Il progetto scelto non è un progetto di questa azienda.',
  TASK_PROJECT_PERIMETER_DENIED: 'Non sei responsabile né nel team di questo progetto, quindi non ne vedi tutti i task. Chiedi a chi lo gestisce di aggiungerti al team, oppure togli il filtro «Progetto».',
  TASK_PROJECT_CUSTOMER_MISMATCH: 'Il progetto appartiene a un altro cliente: cambia il cliente o il progetto, e i due devono corrispondere.',
  TASK_LABEL_NAME_DUPLICATE: 'Esiste già un’etichetta con questo nome. Due etichette omonime non si potrebbero più distinguere sui task, che ne portano il nome e non un collegamento.',
  TASK_ALREADY_CLAIMED: 'Qualcun altro ha preso in carico questo task prima di te. Ricarica per vedere chi ce l’ha.',
  TASK_CLAIM_NOT_IN_QUEUE: 'Puoi prendere in carico solo i task della tua coda. Chiedi a chi gestisce la coda di assegnartelo.',
  TASK_CLAIM_NO_EMPLOYEE: 'Il tuo account non è collegato a un’anagrafica dipendente, e la presa in carico assegna il task a un dipendente. Chiedi all’HR di collegarlo.',
  TASK_REASSIGN_EMPLOYEE_REQUIRED: 'Scegli il dipendente che prende il task.',
  TASK_REASSIGN_REASON_REQUIRED: 'Scrivi perché stai riassegnando: senza motivo, fra sei mesi non si distingue una scelta da un errore.',
  TASK_REASSIGN_EMPLOYEE_UNKNOWN: 'Questo dipendente non esiste in questa azienda.',
  TASK_REASSIGN_EMPLOYEE_INACTIVE: 'Questo dipendente non è più attivo: scegli qualcun altro.',
  TASK_REASSIGN_SAME_ASSIGNEE: 'Il task è già assegnato a questa persona.',
  /* I due 400 sui predicati virtuali: il server rifiuta invece di ignorare, perché un filtro
     di perimetro ignorato mostrerebbe i task di tutti a chi crede di vedere i propri. */
  TASK_FILTER_PERIMETER_INVALID: 'I filtri di perimetro («i miei», «la mia coda») ammettono solo l’uguaglianza. Segnalalo: è un difetto della pagina, non un errore tuo.',
  TASK_FILTER_FOLLOWEDBYME_INVALID: 'Il filtro «solo seguiti» ammette solo l’uguaglianza. Segnalalo: è un difetto della pagina.',
  TASK_NOT_FOUND: 'Questo task non esiste più, o non è visibile con i tuoi permessi.',

  /* ── Configurazione di board a tre livelli (BE v1.148.0) ── */
  BOARD_CONFIG_ASPECT_LOCKED: 'Questo aspetto della board è bloccato da un livello superiore: la tua modifica non sarebbe applicata, quindi il sistema la rifiuta.',
  BOARD_CONFIG_ASPECT_NOT_JSON: 'La configurazione di questo aspetto non è JSON valido.',
  BOARD_CONFIG_UNKNOWN_ASPECT: 'Nome di aspetto sconosciuto: gli aspetti configurabili sono sette e l’elenco arriva dal sistema.',
  BOARD_CONFIG_LEVEL_ALREADY_EXISTS: 'Esiste già una configurazione per questo livello: modifica quella invece di crearne una seconda.',
  BOARD_CONFIG_INVALID_OVERFLOW_BEHAVIOR: 'Il comportamento allo sfondamento del limite può essere solo «avvisa», «blocca» o «blocca con deroga».',

  /* ── Fuso del luogo di lavoro (BE v1.145.0) ──
     Il fuso è validato IANA sia sulla sede sia sull'azienda: il messaggio dice la forma
     giusta, perché «valore non valido» su un elenco di 400 fusi non aiuta nessuno. */
  SITE_INVALID_TIMEZONE: 'Fuso orario della sede non riconosciuto: serve un identificativo IANA, es. «Europe/Rome». Vuoto = eredita quello dell’azienda.',
  TENANT_INVALID_TIMEZONE: 'Fuso orario dell’azienda non riconosciuto: serve un identificativo IANA, es. «Europe/Rome».',

  /* ── Finestra di pausa su orari personali e preset (BE v1.142.0) ── */
  WORKING_HOUR_INCOMPLETE_BREAK_WINDOW: 'La finestra di pausa vuole entrambi gli orari: inizio e fine, o nessuno dei due.',
  WORKING_HOUR_BREAK_OUTSIDE_SHIFT: 'La pausa deve stare dentro l’orario di lavoro e finire dopo il suo inizio.',
  WORKING_HOUR_BREAK_WINDOW_MISMATCH: 'La durata della finestra di pausa è fuori dai limiti minimo/massimo di questo orario.',

  /* ── Promemoria delle timbrature mancate (BE v1.142.0) ── */
  ATTENDANCE_REMINDER_READ_ONLY: 'I promemoria li scrive il job automatico: questa lista è di sola lettura.',
  ATTENDANCE_REMINDER_MINUTES_OUT_OF_RANGE: 'I margini dei promemoria vanno da 5 a 240 minuti.',
  ATTENDANCE_PAUSE_REMINDER_AFTER_AUTOCLOSE: 'Il promemoria di fine pausa scatterebbe dopo la chiusura automatica della pausa: alza prima i minuti di chiusura automatica, poi il margine.',
  ATTENDANCE_EXIT_REMINDER_AFTER_DAY_CLOSE: 'Il promemoria di uscita scatterebbe dopo la chiusura della giornata: a giornata chiusa non partirebbe mai.',
  ATTENDANCE_PAUSE_REMINDER_WITHOUT_TRACKING: 'I promemoria di pausa servono solo dove le pause si timbrano: attiva prima la tracciatura delle pause.',

  /* ── Muro sulla coppia (utente, azienda) del portale clienti (BE v1.147.0) ──
     Il MODE_MISMATCH non è un permesso mancante: è una richiesta interna partita col
     contesto cliente addosso, cioè un difetto del client. Va detto così, altrimenti si va a
     cercare un claim che non c'entra. */
  CLIENT_PORTAL_MODE_MISMATCH: 'Questa richiesta è arrivata con il contesto «cliente» su una rotta interna. Ricarica l’applicazione: è un difetto della pagina, non dei tuoi permessi.',
  CLIENT_PORTAL_ACCESS_INVALID: 'L’accesso cliente selezionato non è tuo o non è più valido: scegline un altro.',
  PORTAL_ACCESS_ROLE_CONFLICT: 'Nella stessa azienda questa persona è sia interna sia cliente del portale: è una configurazione da correggere, non un permesso da concedere.',
  /* P3: l'unicita' e' per coppia (tenant×cliente, utente) e la revoca libera lo slot: non e'
     «un utente, un accesso», ed e' la ragione per cui il messaggio nomina il cliente. */
  PORTAL_ACCESS_ALREADY_EXISTS: 'Questa persona ha già un accesso attivo per questo cliente. Se non riesce a entrare, rimanda l’invito dalla tabella; altrimenti revoca quello esistente.',
  CLIENT_PORTAL_TENANTCLIENT_NOT_FOUND: 'Il cliente non risulta collegato a un’azienda: collegalo nel CRM prima di creare un accesso al portale.',
  CLIENT_PORTAL_CLIENT_MISMATCH: 'Il cliente indicato non è quello della coppia azienda×cliente scelta.',

  /* ── Invito staff al portale: ogni codice ha il suo rimedio, sono rimedi diversi ── */
  PORTAL_ACCESS_QUOTA_EXCEEDED: 'La quota di accessi al portale è esaurita per questa azienda. Si alza da «Regole di lavoro → Portale clienti»: la cambia chi amministra il portale.',
  PORTAL_QUOTA_INVALID: 'Il numero di accessi al portale dev’essere zero o più.',
  TENANT_ATTRIBUTE_KEY_RESERVED: 'Questo valore lo cambia solo chi amministra il portale clienti (permesso «client.portal.manage»).',
  CLIENT_PORTAL_USER_IS_INTERNAL: 'Questa email è di una persona interna a questa azienda: dentro la stessa azienda non si è insieme collega e cliente. Usa un altro indirizzo.',
  PORTAL_INVITE_DAILY_CAP: 'Hai raggiunto il tetto di 20 inviti al giorno per questo cliente. Riprova domani.',
  PORTAL_INVITE_ROLE_MISSING: 'I profili del portale non sono stati caricati su questa installazione: va lanciato il seed dei profili (erp-roles/seed) da un amministratore di sistema.',
  PORTAL_ACCESS_PRIVATE_SINGLE_USER: 'Un cliente persona fisica ha un solo accesso al portale: non c’è nessuno da invitare.',
  PORTAL_ACCESS_CONTACT_CLIENT_MISMATCH: 'Il referente scelto non appartiene a questo cliente: sceglierne un altro, o lascia il campo vuoto.',
  CLIENT_PORTAL_MANAGE_FORBIDDEN: 'Non hai il permesso di amministrare gli accessi al portale di questa azienda: chiedilo a un amministratore.',
  PORTAL_INVITE_EMAIL_INVALID: 'L’indirizzo email non è valido: controlla di averlo scritto per intero.',
  PORTAL_INVITE_MISSING_FIELDS: 'Mancano dei dati obbligatori per l’invito: serve almeno l’email.',
  PORTAL_INVITE_USER_CREATE_FAILED: 'Non è stato possibile creare l’utenza per questa email. Se l’indirizzo è già usato altrove, prova con uno diverso.',

  /* ── Template di comunicazione (BE v1.149) ──
     Fino a questa release `erasable` non era controllato da nessuna riga di server: una DELETE su
     un template di sistema passava, e spariva l'email di un flusso senza che niente lo dicesse. */
  TEMPLATE_SYSTEM_READONLY: 'Questo template lo manda un flusso del sistema, che lo cerca per codice: il titolo non si cambia e non si cancella. Oggetto, contenuto e interruttore Attivo restano modificabili.',
  TEMPLATE_CATEGORY_NOT_ON_MASTER: 'Un template di sistema può stare solo in una cartella dell’azienda principale: altrove continuerebbe a mandare posta ma sparirebbe dall’elenco di tutti.',

  /* ── Flussi di lavorazione (BE v1.149, task 1400) ──
     Il `status` era una stringa libera: adesso un task può vivere dentro un flusso, e i rifiuti
     parlano di un grafo che l'utente non vede. I messaggi devono dire dove si guarda. */
  WORKFLOW_TRANSITION_NOT_ALLOWED: 'Il flusso di lavorazione non prevede questo passaggio da qui. Apri il task e guarda la fascia «Stato», in cima alla scheda: elenca gli stati raggiungibili adesso.',
  WORKFLOW_STATE_UNKNOWN: 'Questo stato non esiste nel flusso del task. Gli stati ammessi sono nella fascia «Stato», in cima alla scheda.',
  WORKFLOW_NO_EQUIVALENT_STATE: 'Il gruppo scelto usa un flusso senza uno stato equivalente a quello attuale: indica lo stato in cui portare il task.',
  WORKFLOW_TARGET_STATE_REQUIRED: 'Scegli lo stato in cui portare il task.',
  // Nomi di `WorkflowTransitionRules.cs:22-26`: fino al 23-09 qui c'erano codici inesistenti.
  WORKFLOW_TRANSITION_FORBIDDEN_FOR_USER: 'Questo passaggio non puoi farlo tu: lo fa un’altra persona, o serve un permesso che il tuo profilo non ha.',
  WORKFLOW_TRANSITION_REQUIRES_COMMENT: 'Questo passaggio vuole un commento: senza, resta solo la data e fra sei mesi nessuno sa perché.',
  WORKFLOW_TRANSITION_REQUIRES_PUBLIC_REPLY: 'Questo passaggio vuole una risposta al richiedente, non una nota interna: scrivi il testo e togli la spunta «interna».',
  WORKFLOW_TRANSITION_REQUIRES_FIELDS: 'Prima di questo passaggio vanno compilati i campi che la transizione richiede.',
  WORKFLOW_NOT_ACTIVE: 'Un flusso non attivo non si può assegnare a una coda: attivalo prima, e l’attivazione controlla il grafo.',
  WORKFLOW_IN_USE: 'Questo flusso ha task dentro o è assegnato a una coda: non si cancella, si disattiva — così chi ci è passato conserva una storia leggibile.',
  WORKFLOW_OVERRIDE_LOCKED: 'Il flusso è imposto da un livello superiore con il blocco attivo: la deroga per questa coda non è ammessa.',
  WORKFLOW_INVALID_CATEGORY: 'Ogni stato deve dichiarare una delle sette categorie: è da lì che il sistema capisce se il task è aperto, in attesa o chiuso.',
  WORKFLOW_INVALID_ALLOWED_FOR: 'Chi può fare il passaggio può essere solo «chiunque», «l’assegnatario» o «chi ha un permesso».',
  WORKFLOW_TRANSITION_CLAIM_REQUIRED: 'Hai scelto «solo chi ha un permesso» senza indicare quale: così la transizione sarebbe chiusa a tutti.',
  WORKFLOW_TRANSITION_CROSS_WORKFLOW: 'Una transizione collega due stati dello stesso flusso: questi appartengono a flussi diversi.',
  WORKFLOW_UNKNOWN_REQUIRED_FIELD: 'Uno dei campi richiesti non esiste sul task: un campo inventato bloccherebbe il passaggio senza che nessuno capisca perché.',
  WORKFLOW_STATE_CODE_IMMUTABLE: 'Il codice di uno stato non si cambia: è già scritto dentro i task che ci sono passati. Cambia il nome, oppure crea uno stato nuovo e disattiva il vecchio.',
  WORKFLOW_STATE_IS_SINK: 'Da questo stato non esce nessuna transizione: chi ci finisce non ne esce più.',
  WORKFLOW_STATE_UNREACHABLE: 'A questo stato non arriva nessuna transizione: nessun task potrà mai raggiungerlo.',
  WORKFLOW_INCOMPLETE_CATEGORIES: 'Al flusso mancano stati per alcune categorie obbligatorie: senza, il sistema non sa dire quando un task è aperto o chiuso.',
  WORKFLOW_DUPLICATE_STATE_CODE: 'Due stati con lo stesso codice: il codice identifica lo stato dentro i task, quindi deve essere unico.',
  /*
   * LA GERARCHIA, dopo la v1.200.0: N livelli, e il blocco è sui **discendenti**.
   *
   * I due codici vecchi restano in catalogo e non sono morti per distrazione: il server che
   * risponde non è sempre quello che pensiamo — un ambiente dietro di una versione manda ancora
   * `TASK_HAS_OPEN_CHILDREN`, e un messaggio grezzo in inglese al posto del nostro è la cosa che
   * l'utente vede. Costano due righe; toglierle si fa quando nessun ambiente è più indietro.
   */
  /*
   * F4 — un'azienda può **vietare** i task fuori flusso (`task.workflowRequired`). Senza
   * questo codice l'utente vedrebbe il messaggio grezzo del server, che non dice dove si
   * rimedia: la scelta è di chi amministra, non di chi sta creando il task.
   */
  TASK_WORKFLOW_REQUIRED: 'Questa azienda non ammette task senza flusso di lavorazione, e il gruppo scelto non ne ha uno. Scegli un gruppo che abbia un flusso, oppure chiedi a chi amministra di assegnarne uno a questo gruppo da «Flussi di lavorazione».',
  /* LA GENERAZIONE DALLA WBS e il raggruppamento (v1.200.0). */
  GENERATION_UNASSIGNED_NODES: 'Alcuni nodi da generare non hanno un assegnatario, e hai chiesto che li abbiano tutti. L’elenco è nell’anteprima: assegnali nella WBS, oppure scegli un gruppo predefinito per la generazione.',
  PROJECT_APPROVE_NOT_ALLOWED: 'Per approvare la WPS serve essere capo progetto o membro del team di questo progetto.',
  TASK_SOURCE_NODE_IMMUTABLE: 'Il legame con il nodo della WBS si scrive alla generazione e non si sposta più: un nodo genera un task solo.',
  TASK_GROUPING_NOT_IN_PROJECT: 'Il raggruppamento scelto appartiene a un altro progetto. Milestone, feature e attività devono essere dello stesso progetto del task — e un task senza progetto può riferire solo nodi liberi.',
  TASK_GROUPING_INVALID_TYPE: 'Il nodo scelto è del tipo sbagliato: la feature dev’essere un nodo «feature» e l’attività un nodo «attività».',
  TASK_GROUPING_INCONSISTENT: 'La feature scelta non alimenta la milestone scelta: raggruppando per l’una o per l’altra lo stesso lavoro comparirebbe in due posti diversi. Scegline una che le colleghi, o lascia vuota la milestone.',
  SCOPE_TAXONOMY_DISABLED: 'Un nodo senza progetto richiede la tassonomia libera, che per questa azienda è spenta. La accende chi amministra l’azienda master.',
  MILESTONE_ONLY_ON_FEATURE: 'La milestone si dichiara solo su un nodo di tipo «feature»: è il blocco di valore che la alimenta.',
  MILESTONE_NOT_IN_PROJECT: 'La milestone appartiene a un altro progetto.',
  /* ⚠️ Sul server le porte sono tre: padre e figlio nell'albero dei task, padre e figlio fra i nodi
     della WBS, e — dalla rendicontazione — un'attività della WBS di un altro progetto. Il testo qui
     resta neutro; quale delle tre sia lo dice il `meta`, e lo legge `API_ERROR_MESSAGE_BUILDERS`. */
  SCOPE_PROJECT_MISMATCH: 'Stai collegando due cose che stanno su progetti diversi: devono stare sullo stesso progetto, o tutte e due su nessuno.',
  /* ── La rendicontazione sulla WBS (`TimesheetEntryResourceDefinition`, TASK-SCADENZE R2 e R19) ── */
  SCOPE_NOT_BILLABLE: 'L’attività scelta non appartiene a nessun progetto: è una voce di classificazione, e le ore si rendicontano solo sulle attività di un progetto. Scegline una del progetto della riga.',
  TASK_ASSIGNMENT_REQUIRED: 'Manca l’attività: scegli l’«Attività» del progetto su cui hai lavorato. Va bene anche una sotto-attività collegata a un’attività.',
  /* BE 3a3e088: progetto, attività e descrizione sempre obbligatori, tranne assenze e bozze. */
  PROJECT_REQUIRED: 'Manca il progetto: scegli su quale progetto hai lavorato.',
  DESCRIPTION_REQUIRED: 'Manca la descrizione: scrivi in breve cosa hai fatto.',
  ENTRY_INCOMPLETE: 'La riga di partenza è incompleta: servono progetto, attività e descrizione. Completala e ripeti.',
  TASK_REMAINING_HOURS_INVALID: 'Le ore rimanenti di un task non possono essere negative: se il lavoro è finito, scrivi 0.',
  PROJECT_TASK_PARENT_CYCLE: 'Il padre indicato è un discendente di questo nodo: si formerebbe un anello.',
  TASK_HAS_OPEN_DESCENDANTS: 'Sotto questo task c’è ancora del lavoro aperto — non solo fra i figli diretti, ma in tutto il ramo sotto di lui. Chiudilo o annullalo prima, oppure usa «Chiudi i task aperti sotto». Un task «risolto» conta ancora come aperto: aspetta una conferma.',
  TASK_DEPENDENCY_UNSATISFIED: 'Un vincolo con un altro task non è ancora soddisfatto: aspetta che il predecessore arrivi dove serve. Chi gestisce il progetto o il gruppo può forzarlo, scrivendo il motivo.',
  TASK_DEPENDENCY_OVERRIDE_FORBIDDEN: 'Forzare un vincolo fra task tocca a chi gestisce il progetto o il gruppo del task.',
  TASK_DEPENDENCY_OVERRIDE_REASON_REQUIRED: 'Per forzare un vincolo serve un motivo di almeno dieci caratteri: resta nella cronologia del task.',
  TASK_DEPENDENCY_INVALID_TYPE: 'Il tipo di vincolo non è fra quelli previsti.',
  TASK_DEPENDENCY_INVALID_LAG: 'Il ritardo sta fra -365 e 365 giorni lavorativi.',
  TASK_DEPENDENCY_SELF: 'Un task non può dipendere da sé stesso.',
  TASK_DEPENDENCY_CYCLE: 'Questo vincolo chiuderebbe un cerchio: i task finirebbero ad aspettarsi a vicenda.',
  TASK_CLOSURE_POLICY_DEADLOCK: 'Con la chiusura «bloccata» questo task non si chiuderebbe mai: un task del suo ramo aspetta la sua chiusura. Togli quel vincolo, poi cambia la regola.',
  SETTING_CLOSURE_POLICY_DEADLOCK: 'Con questa regola di chiusura alcuni task non si chiuderebbero mai: un task aspetta la chiusura di un antenato che a sua volta aspetta il ramo. Togli quei vincoli, poi cambia la regola.',
  TASK_DEPENDENCY_HIERARCHY_DEADLOCK: 'Il task scelto sta sopra questo nel ramo e non si chiude finché il ramo è aperto: aspettarne la chiusura bloccherebbe entrambi. Scegli «Si avvia quando l’altro è avviato» o «Si chiude quando l’altro è avviato».',
  TASK_TOO_DEEP: 'Questo ramo ha raggiunto la profondità massima consentita dall’azienda: appendi il task più in alto, oppure chiedi a chi amministra di alzare il limite in «Regole dei task».',
  /* Pre-v1.200.0. Vedi sopra: restano per gli ambienti indietro di una versione. */
  TASK_HAS_OPEN_CHILDREN: 'Questo task ha figli ancora aperti: chiudili o annullali prima di risolverlo. Un figlio «risolto» conta ancora come aperto — aspetta una conferma.',
  TASK_NESTING_TOO_DEEP: 'La gerarchia è di un livello solo: un figlio non può avere figli.',
  TASK_PARENT_IMMUTABLE: 'Il padre si sceglie alla creazione e non si cambia: un pezzo di lavoro non cambia padre, si chiude e se ne apre un altro.',
  TASK_PARENT_NOT_FOUND: 'Il task padre indicato non esiste, o non è visibile con i tuoi permessi.',
  TASK_CHILD_HAS_NO_PUBLIC_CHANNEL: 'Un task figlio non ha un canale verso il richiedente: per rispondergli scrivi sul task padre.',
  TASK_COMMENT_REACTION_INVALID_KIND: 'Questa reaction non è fra quelle disponibili.',
  TASK_COMMENT_REACTION_COMMENT_NOT_FOUND: 'Il commento non c\'è più: ricarica il filo.',
  TASK_COMMENT_REACTION_ALREADY_EXISTS: 'Avevi già messo questa reaction.',
  TASK_COMMENT_REACTION_NOT_OWNER: 'Una reaction la toglie solo chi l\'ha messa.',
  // BE-RESPONSE-TO-FE-PERMESSI-TASK-v1 §1-§2.
  TASK_COMMENT_NOT_AUTHOR: 'Questo commento l’ha scritto un’altra persona: lo modifica solo lei, e lo cancella lei o un amministratore.',
  TASK_ISSUE_NOT_REPORTER: 'Questa segnalazione l’ha aperta un’altra persona: descrizione, tipo e gravità li cambia solo lei. Lo stato puoi cambiarlo tu.',
  TASK_FOLLOWER_NOT_OWNER: 'Gli avvisi di chi segue un task li sceglie la persona stessa: puoi toglierla dal task, non cambiarle le notifiche.',

  /* ── Posta in uscita: il registro (EmailLogAdminController) ── */
  EMAIL_LOG_NOT_FOUND: 'Questa e-mail non è più nel registro: le righe si cancellano dopo 90 giorni. Ricarica l’elenco.',
  EMAIL_LOG_INVALID_FILTER: 'Il filtro sull’esito non è valido: si sceglie fra spedite, non riuscite, solo registrate e saltate. Togli il filtro e riprova.',

  /* ── Il generatore dei PDF: non dipende da quello che si è scritto, e il rimedio è aspettare o segnalarlo ── */
  PDF_SERVICE_UNAVAILABLE: 'Il servizio che prepara i PDF non è disponibile in questo momento. Non dipende dai tuoi dati: riprova fra qualche minuto e, se si ripete, segnalalo.',
  PDF_RENDER_FAILED: 'Il PDF non è stato generato. Non dipende da quello che hai inserito: riprovare non aiuta, segnalalo indicando cosa stavi stampando.',

  /* Dal MASTER-TENANT-WRITE-GUARD la registrazione deve dichiarare l'azienda: la nostra pagina di
     registrazione libera non la manda, quindi chi ci arriva prende sempre questo rifiuto. Il
     rimedio vero per lui è l'invito. */
  REGISTRATION_TENANT_REQUIRED: 'Non ci si può registrare da soli: l’accesso a MaeWay arriva con l’invito dell’azienda per cui lavori. Chiedilo a chi la amministra.',

  /* ── Trasferte: i tetti della policy di viaggio (§3.4) ──
     Scattano alla conferma o alla scelta del preventivo, sul dipendente della trasferta. Il
     dettaglio del sistema porta i valori interni («business», «suite»): qui si dice cosa fare.
     Chi ha il permesso di derogare deve mandare una motivazione, che da questa schermata non si
     può ancora scrivere: per questo il rimedio è stare nei limiti. */
  TRAVEL_POLICY_HOTEL_PRICE_EXCEEDED: 'Il prezzo a notte supera il massimo previsto dalla policy di viaggio di chi parte: scegli un preventivo più economico, oppure correggi l’importo se è quello di tutto il soggiorno.',
  TRAVEL_POLICY_ROOM_TYPE_EXCEEDED: 'Il tipo di camera è oltre quello ammesso dalla policy di viaggio di chi parte: scegli una sistemazione di livello più basso.',
  TRAVEL_POLICY_FLIGHT_CLASS_EXCEEDED: 'La classe del volo è oltre quella ammessa dalla policy di viaggio di chi parte: scegli una classe inferiore.',
  TRAVEL_POLICY_TRAIN_CLASS_EXCEEDED: 'La classe del treno è oltre quella ammessa dalla policy di viaggio di chi parte: scegli una classe inferiore.',
  TRAVEL_POLICY_CAR_CATEGORY_EXCEEDED: 'La categoria dell’auto a noleggio è oltre quella ammessa dalla policy di viaggio di chi parte: scegline una più piccola.',
  TRAVEL_POLICY_OVERRIDE_REASON_REQUIRED: 'La trasferta supera i limiti della policy di viaggio. Puoi derogare, ma serve una motivazione di almeno 20 caratteri, e scriverla da qui è previsto e non ancora disponibile: per ora riporta la prenotazione nei limiti.',
  TRAVEL_BOOKING_BYPASS_REASON_REQUIRED: 'Per saltare l’approvazione serve una motivazione di almeno 10 caratteri: scrivi perché la richiesta non può aspettare il responsabile.',
  BYPASS_NOT_ALLOWED: 'Non puoi saltare l’approvazione di questa richiesta: l’azienda non lo consente, il tuo profilo non lo prevede, oppure la richiesta è tua. Lascia che la approvi il responsabile.',
  NOT_PENDING: 'La richiesta non è più in attesa del responsabile: qualcuno l’ha già gestita. Ricarica per vedere com’è adesso.',
  REMINDER_TOO_SOON: 'Il responsabile è già stato sollecitato da poco: aspetta qualche ora prima di sollecitarlo di nuovo.',

  /* ── Verbale di prestazione (VPS) al cliente ──
     Il dettaglio del sistema nomina campi e permessi per come stanno nel codice («custom field
     'vps-richiesto'», «claim 'canSeeVps'»): qui il nome è quello della schermata. */
  VPS_NOT_REQUIRED_ON_PROJECT: 'Questo progetto non prevede il verbale al cliente: se serve, attivalo nei dati del progetto e riprova.',
  VPS_BLOCKED_BY_REASON: 'La causale di questa rendicontazione esclude il verbale al cliente: se il verbale serve, cambia la causale.',
  VPS_CLIENT_NO_RECIPIENT: 'Il cliente non ha un indirizzo a cui mandare i verbali: aggiungilo nella sua scheda, alla voce «Verbali», e riprova.',
  VPS_ALREADY_PENDING: 'Il verbale è già partito per PEC e si aspetta la ricevuta: attendi l’esito prima di rimandarlo.',
  VPS_RESEND_CLAIM_REQUIRED: 'Il verbale è già stato inviato, e rimandarlo richiede un permesso che il tuo profilo non ha: chiedilo a un amministratore.',
  VPS_PEC_NOT_CONFIGURED: 'Il cliente vuole i verbali per PEC, ma l’invio per PEC non è configurato: togli la PEC dalla scheda del cliente oppure chiedi a un amministratore di configurarla.',
  VPS_ENTRY_NOT_FOUND: 'La rendicontazione non c’è più, o non è fra quelle che puoi vedere: ricarica la pagina.',
  VPS_SEND_FAILED: 'Il verbale non è partito: la posta non l’ha consegnato a nessun destinatario. Controlla gli indirizzi del cliente e riprova fra poco; se si ripete, segnalalo.',
  VPS_CLAIM_REQUIRED: 'Il tuo profilo non prevede l’invio dei verbali al cliente: chiedi il permesso a un amministratore.',
  VPS_NOTE_TOO_LONG: 'La nota del verbale supera i 500 caratteri: accorciala e riprova.',
  VPS_DISPATCH_NOT_ACKNOWLEDGED: 'Prima di confermare il verbale aprilo e leggilo: la conferma vale solo per un verbale aperto.',

  /* ── Smart working: la pianificazione di un periodo ── */
  SMART_WORKING_PLANNING_RETROACTIVE_DENIED: 'La pianificazione non può comprendere giorni già passati: scegli un periodo che parta da oggi in avanti.',
  SMART_WORKING_PLANNING_INCONSISTENT_VALIDATOR: 'I giorni di questa pianificazione andrebbero ad approvatori diversi, quindi non si inviano insieme: inviali uno alla volta, oppure chiedi all’ufficio del personale di controllare chi è il tuo responsabile.',
  SMART_WORKING_PLANNING_INVALID_DATE_RANGE: 'Il periodo della pianificazione non è valido: indica il giorno, e un inizio che non sia dopo la fine.',
  SMART_WORKING_PLANNING_DATE_OUT_OF_RANGE: 'Il giorno scelto è fuori dal periodo di questa pianificazione: scegline uno compreso fra l’inizio e la fine.',
  SMART_WORKING_PLANNING_INVALID_VIEW_MODE: 'La vista scelta per la pianificazione non è fra quelle previste: ricarica la pagina e riprova.',
  SMART_WORKING_GROUP_SCOPE_NOT_APPLICABLE: 'Una policy vale anche per le aziende del gruppo solo se è di tutta l’azienda o di una sede: persone e gruppi di lavoro sono di un’azienda sola. Cambia «Dove vale» oppure l’ambito.',
  EMPLOYEE_SW_ELIGIBILITY_CHANGE_READ_ONLY: 'Lo storico dell’abilitazione allo smart working non si modifica a mano: si cambia l’interruttore nella scheda del dipendente, e lo storico si aggiorna da solo.',

  /* ── Pianificazione delle attività ── */
  PLANNING_INVALID_STATE_TRANSITION: 'Questa operazione non si può fare sul piano nello stato in cui è adesso: ricarica la pagina per vedere com’è.',
  PLANNING_ENTRY_NOT_CONSUMABLE: 'Si collega una registrazione di ore solo a un piano confermato o già in parte rendicontato: conferma prima il piano, oppure togli il collegamento.',
  PLANNING_ENTRY_NOT_FOUND: 'Il piano non c’è più: qualcuno l’ha eliminato. Ricarica la pagina.',
  PLANNING_ENTRY_CONFLICT: 'Il piano è in conflitto con un altro impegno di quel giorno: risolvi il conflitto prima di trasformarlo in ore.',
  PLANNING_ENTRY_EXISTS: 'In quel giorno c’è già un piano: non si duplica sopra.',
  PLANNING_CAPACITY_ZERO: 'Quel giorno non è lavorativo per la persona (festivo, chiusura o assenza): non c’è tempo da pianificare.',
  TIMESHEET_ENTRY_EXISTS: 'In quel giorno c’è già una registrazione di ore: il piano non la sovrascrive.',
  TIMESHEET_MONTH_LOCKED: 'La rendicontazione di quel mese è stata chiusa: va riaperta prima di modificarla.',
  PLANNING_SERIES_HAS_LIVE_OCCURRENCES: 'La serie ha ancora giornate attive: annulla la serie prima di eliminarla.',
  PLANNING_SERIES_OCCURRENCE_CONSUMED: 'Una giornata della serie è già stata rendicontata, quindi la serie non si elimina: si può solo annullare.',
  PLANNING_SERIES_DAYS_REQUIRED: 'Una ricorrenza settimanale vuole almeno un giorno della settimana: sceglilo e salva di nuovo.',
  PLANNING_SERIES_INVALID_DAY_OF_MONTH: 'Il giorno del mese va da 1 a 31.',
  PLANNING_SERIES_INVALID_INITIAL_STATUS: 'Lo stato iniziale delle giornate della serie non è fra quelli previsti: scegline uno dall’elenco.',
  PLANNING_SERIES_INVALID_RANGE: 'La fine della serie viene prima dell’inizio: correggi le date.',
  PLANNING_SERIES_RANGE_TOO_LONG: 'Una serie copre al massimo un anno (366 giorni): accorcia il periodo, o dividilo in due serie.',
  INVALID_RECURRENCE_TYPE: 'Il tipo di ricorrenza non è fra quelli previsti: scegline uno dall’elenco.',
  PLANNING_EXTERNAL_ATTENDEE_NOT_ALLOWED: 'Questa azienda non ammette invitati esterni alle riunioni pianificate: invita solo colleghi.',
  PLANNING_ATTENDEE_INVALID: 'Un invitato è o un collega o un indirizzo esterno, non tutti e due: scegline uno.',
  INVALID_ATTENDEE_ROLE: 'Il ruolo dell’invitato non è fra quelli previsti: scegline uno dall’elenco.',
  INVALID_ATTENDEE_RESPONSE: 'La risposta all’invito non è fra quelle previste: scegline una dall’elenco.',
  INVALID_ORIGIN_VALUE: 'L’origine del piano non è fra quelle previste: ricarica la pagina e riprova.',
  INVALID_MEETING_TITLE_MODE: 'Il modo di intitolare la riunione non è fra quelli previsti: scegline uno dall’elenco.',
  PLANNING_POLICY_THRESHOLD_REQUIRED: 'Con questo modo di misurare la copertura serve una soglia maggiore di zero, e in percentuale non oltre 100: compila la soglia.',
  PLANNING_POLICY_SCOPE_INVALID: 'Una regola per una singola persona non può essere anche per una sede o un gruppo: togli la sede o il gruppo.',
  PLANNING_POLICY_TENANT_MISMATCH: 'La sede, il gruppo o la persona scelti non sono di questa azienda: scegline uno fra i suoi.',
  PLANNING_POLICY_GROUP_SCOPE_NOT_ALLOWED: 'Una regola per tutto il gruppo ha senso solo su una capogruppo: questa azienda non ha aziende sotto di sé. Scegli «Solo questa azienda».',
  DUPLICATE_PLANNING_POLICY_DEFAULT: 'Questa azienda ha già una regola di pianificazione predefinita: modifica quella invece di crearne un’altra.',
  INVALID_PLANNING_MODE: 'Il modo di pianificare non è fra quelli previsti: scegline uno dall’elenco.',
  INVALID_COMPLIANCE_TRACKING: 'Il controllo del rispetto non è fra quelli previsti: scegline uno dall’elenco.',
  INVALID_COVERAGE_MODE: 'Il modo di misurare la copertura non è fra quelli previsti: scegline uno dall’elenco.',
  INVALID_REQUIRED_STATUS: 'Lo stato richiesto al piano non è fra quelli previsti: scegline uno dall’elenco.',
  PLANNING_READ_NOT_ALLOWED: 'Non puoi vedere la pianificazione di questa persona: la vedono lei, il suo responsabile e l’ufficio del personale.',
  PLANNING_PROJECT_NOT_FOUND: 'Il progetto non c’è più, o non è fra quelli che puoi vedere: ricarica la pagina.',
  PLANNING_PROJECT_READ_NOT_ALLOWED: 'La pianificazione di un progetto la vedono il suo responsabile e chi ha la visibilità estesa: chiedila a loro.',
  INVALID_RANGE: 'La data di fine viene prima di quella di inizio: invertile e riprova.',
  RANGE_TOO_LONG: 'Il periodo scelto è troppo lungo: restringilo e riprova.',
  DATES_REQUIRED: 'Scegli almeno un giorno su cui copiare.',
  TOO_MANY_DATES: 'Troppi giorni in una volta: copia su meno giorni, e ripeti per gli altri.',
  OBLIGATIONS_READ_NOT_ALLOWED: 'Non puoi vedere le scadenze di questa persona: le vedono lei, il suo responsabile e l’ufficio del personale.',
  OBLIGATION_RULE_INVALID: 'La regola della scadenza non è completa o ha un valore fuori misura: controlla i campi segnalati e salva di nuovo.',

  /* ── Portale clienti ── */
  CLIENT_PORTAL_ONLY: 'Questa pagina è riservata agli accessi del portale clienti: entra con l’accesso che ti ha dato il fornitore.',
  CLIENT_PORTAL_ACCOUNT_INACTIVE: 'Il tuo accesso al portale è sospeso o revocato: chiedi al tuo referente, o al fornitore, di riattivarlo.',
  CLIENT_PORTAL_ROUTE_FORBIDDEN: 'Questa funzione non è disponibile dal portale clienti.',
  CLIENT_PORTAL_ACCOUNT_DELETE_FORBIDDEN: 'Un accesso al portale non si elimina, si revoca: così resta la storia di chi poteva entrare.',
  CLIENT_PORTAL_INVALID_SECTION: 'Una delle sezioni del portale scelte non esiste più: ricarica la pagina e scegli di nuovo.',
  CLIENT_PORTAL_DOC_TITLE_REQUIRED: 'Dai un titolo al documento: è quello che il cliente legge nell’elenco.',
  CLIENT_PORTAL_DOC_FILE_REQUIRED: 'Manca il file: caricalo prima di pubblicare il documento.',
  CLIENT_PORTAL_DOC_FILE_NOT_FOUND: 'Il file caricato non si trova più: caricalo di nuovo.',
  CLIENT_PORTAL_DOC_CLIENT_NOT_IN_PORTFOLIO: 'Il cliente scelto non è fra i clienti di questa azienda: scegline un altro.',
  CLIENT_PORTAL_DOC_IMMUTABLE: 'Cliente e file di un documento pubblicato non si cambiano: spegni questo documento e pubblicane uno nuovo.',
  PORTAL_ACCESS_SELF_REVOKE: 'Non puoi revocare il tuo stesso accesso: chiedilo a un altro referente della tua azienda, o al fornitore.',
  PORTAL_CONTACT_NAME_REQUIRED: 'Indica il nome del referente.',
  PORTAL_UPLOAD_EMPTY: 'Non è arrivato nessun file: sceglilo di nuovo e riprova.',
  PORTAL_UPLOAD_TOO_LARGE: 'Il file supera i 10 MB ammessi: comprimilo o dividilo.',
  PORTAL_UPLOAD_TYPE_NOT_ALLOWED: 'Questo tipo di file non si può inviare: sono ammessi PDF, immagini JPG e PNG, documenti Word ed Excel, CSV, testo, ZIP, P7M e XML.',
  PORTAL_UPLOAD_NO_TENANT: 'Il tuo accesso non è collegato a un fornitore, quindi il documento non ha un destinatario: segnalalo al fornitore.',
  PORTAL_UPLOAD_QUOTA_REACHED: 'Avete raggiunto il numero massimo di documenti inviati: contattate il fornitore per liberarne.',
  PORTAL_ATTACHMENT_QUOTA_REACHED: 'Questa richiesta ha già il numero massimo di allegati (10): togline uno o aprine una nuova.',
  PORTAL_PAGE_NOT_ASSIGNED: 'Questa sezione del portale non è attiva per voi.',
  PORTAL_PAGE_KEY_INVALID: 'La chiave della pagina va scritta in minuscolo con i trattini (es. acme-spedizioni), al massimo 60 caratteri.',
  PORTAL_PAGE_KEY_LOOKS_LIKE_ID: 'La chiave della pagina non può essere un identificativo: usa il nome del modulo, uguale in tutti gli ambienti.',
  PORTAL_PAGE_LABEL_REQUIRED: 'Dai un nome alla pagina: è quello che si legge nel menu del portale.',
  PORTAL_PAGE_PATH_INVALID: 'Il percorso della pagina va scritto in minuscolo con i trattini, una sola parola, al massimo 60 caratteri.',
  PORTAL_PAGE_PATH_RESERVED: 'Questo percorso è già di una pagina del portale: sceglierne un altro, altrimenti la pagina su misura non si aprirebbe.',
  PORTAL_PAGE_PATH_DUPLICATE: 'Un’altra pagina usa già questo percorso per gli stessi clienti: scegline un altro.',
  PORTAL_PAGE_CONFIG_TOO_LARGE: 'La configurazione della pagina è troppo grande: alleggeriscila e riprova.',
  PORTAL_PAGE_CONFIG_NOT_JSON: 'La configurazione della pagina non è scritta in un formato valido: controlla parentesi e virgolette.',
  PORTAL_PAGE_CONFIG_BUDGET_EXCEEDED: 'Con questa pagina le configurazioni che un cliente riceve all’apertura del portale diventano troppo pesanti: alleggerisci questa o un’altra pagina dello stesso cliente.',
  PORTAL_PAGE_CLIENT_NOT_IN_PORTFOLIO: 'Il cliente scelto non è fra i clienti di questa azienda: scegline un altro.',
  SUPPORT_REQUEST_CLOSED: 'Questa richiesta è chiusa: per un seguito aprine una nuova.',
  SUPPORT_REQUEST_REJECTED: 'Questa richiesta non è stata accolta: per un seguito aprine una nuova.',
  SUPPORT_REQUEST_NOT_RESOLVED: 'Si chiude solo una richiesta già risolta: aspetta che il fornitore la segni come risolta.',
  SUPPORT_COMMENT_EMPTY: 'Scrivi il testo della risposta prima di inviarla.',
  SUPPORT_INTAKE_FIELD_TOO_LONG: 'Il testo è troppo lungo: accorcialo e riprova.',
  TENANT_REQUIRED: 'Manca l’azienda su cui lavorare: rientra scegliendo l’azienda e riprova.',
};

/* ------------------------------------------------------------------------- *
 *  Messaggi ricostruiti dal payload `meta`
 * ------------------------------------------------------------------------- */

/**
 * Molti errori applicativi del BE portano con sé i **numeri** che li spiegano: quanti
 * giorni hai chiesto e quanti ne hai, quale periodo è bloccato, quanto preavviso serve.
 * La tabella statica qui sopra li butta via e dice "Saldo insufficiente per questo tipo
 * di assenza", lasciando l'utente a indovinare di quanto ha sforato.
 *
 * Le funzioni qui sotto ricostruiscono il messaggio dai dati. Le chiavi non sono
 * inventate: sono quelle **garantite** dal contratto `FE-DIRECTIVES-TO-BE-LEAVE-ERROR-META-v1`,
 * accolto dal BE in v1.70.0 — per ogni code il BE si impegna a emettere tutte le chiavi
 * elencate (mai `undefined`; `null` se un valore non è calcolabile) e a non rinominarle
 * senza bump di versione. Restano comunque difensive: se `meta` arriva vuoto tornano
 * `null` e si ricade sulla tabella statica.
 *
 * Regola di scrittura: ogni messaggio dice **cosa fare**, non solo cosa è andato storto.
 * L'utente sta subendo l'errore nel mezzo di un'azione, non sta leggendo un log.
 */
type ErrorMeta = Record<string, unknown>;

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

/**
 * Quantità di giorni in italiano corrente: `0.5` → "mezza giornata", `1.5` → "1 giorno e
 * mezzo". Le mezze giornate sono la norma nelle assenze e "0,5 giorni" si legge male.
 */
export function fmtDays(v: unknown): string {
  const n = num(v);
  if (n === null) return String(v ?? '—');
  if (n === 0) return 'nessun giorno';
  if (n === 0.5) return 'mezza giornata';
  if (n === 1) return '1 giorno';
  if (Number.isInteger(n)) return `${n} giorni`;
  const whole = Math.floor(n);
  if (Math.abs(n - whole - 0.5) < 0.01) return whole === 1 ? '1 giorno e mezzo' : `${whole} giorni e mezzo`;
  return `${n.toFixed(2).replace(/\.?0+$/, '').replace('.', ',')} giorni`;
}

/** Come `fmtDays` ma per i tipi di assenza a ore. */
export function fmtHours(v: unknown): string {
  const n = num(v);
  if (n === null) return String(v ?? '—');
  if (n === 1) return '1 ora';
  if (Number.isInteger(n)) return `${n} ore`;
  return `${n.toFixed(2).replace(/\.?0+$/, '').replace('.', ',')} ore`;
}

/** Sceglie giorni od ore in base a `meta.unitOfMeasure`, quando il BE lo dichiara. */
const fmtQty = (v: unknown, unit: unknown): string => (unit === 'hours' ? fmtHours(v) : fmtDays(v));

/** `2026-08-14` → `14/08/2026`. Le date del BE sono DATE-ONLY, niente fusi di mezzo. */
function fmtDate(v: unknown): string | null {
  const s = str(v);
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
}

/** Elenco leggibile: `['a','b','c']` → "a, b o c". */
function fmtList(v: unknown): string | null {
  if (!Array.isArray(v) || v.length === 0) return null;
  const items = v.map(String);
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} o ${items[items.length - 1]}`;
}

const TRANSITION_LABELS: Record<string, string> = {
  submit: 'inviare',
  'manager-approve': 'approvare come responsabile',
  'admin-approve': 'approvare come HR',
  approve: 'approvare',
  reject: 'rifiutare',
  cancel: 'annullare',
  reopen: 'riportare in bozza',
  delete: 'eliminare',
};

/**
 * Compone il messaggio per i codici tecnici del BE: il suo `detail` (che porta i numeri
 * reali) più l'`hint` (che dice come uscirne), con un testo di riserva se il BE non ha
 * mandato nulla di leggibile.
 */
function withHint(meta: ErrorMeta, fallback: string): string {
  const detail = str(meta.detail);
  const hint = str(meta.hint);
  const base = detail ?? fallback;
  return hint && hint !== base ? `${base} ${hint}` : base;
}

/**
 * `meta.code` → costruttore del messaggio. Ritornare `null` significa "non ho abbastanza
 * dati": il chiamante ricade su `API_ERROR_MESSAGES` e poi sul `detail` del BE.
 */
/**
 * Nome utente dei tre campi ore soggetti alla griglia. Le ore di viaggio non ci sono
 * apposta: il BE le tiene fuori dalla regola.
 */
/**
 * «Qualcun altro l'ha già chiusa». Senza la data il messaggio resta utile — dice comunque
 * che non c'è più niente da fare — invece di stampare «undefined».
 */
function cancellationAlreadyHandled(m: ErrorMeta): string {
  const when = fmtDate(m.cancellationHandledAt);
  return when
    ? `Questa pratica è già stata chiusa il ${when}. Se serve rifarla, riaprila prima di scrivere una nota nuova: così non si sovrascrive quella di chi ci ha già lavorato.`
    : 'Questa pratica è già stata chiusa da qualcun altro. Se serve rifarla, riaprila prima di scrivere una nota nuova.';
}

const HOURS_FIELD: Record<string, string> = {
  ordinaryHours: 'le ore ordinarie',
  overtimeHours: 'gli straordinari',
  billingHours: 'le ore fatturabili',
};

/**
 * Messaggi degli **avvisi** — `meta.warnings` su una risposta riuscita, non errori.
 *
 * Stanno a parte dai messaggi d'errore perché il tono deve essere diverso: l'operazione è
 * andata a buon fine, quindi il testo non deve dire «non è stato possibile» ma «è successo
 * questo, e va sistemato altrove». Confonderli produrrebbe un salvataggio riuscito che
 * sembra fallito.
 */
export const API_WARNING_MESSAGES: Record<string, string> = {
  // Il BE lo emette alla transizione a `consumed`: la riga d'ordine è chiusa e resta chiusa,
  // ma nessuno è stato assegnato alla commessa successiva. Senza avviso il buco si scopre
  // quando qualcuno prova a rendicontare lì e non risulta nel team.
  ORDER_NEXT_PROJECT_TEAM_COPY_FAILED:
    'La riga d\'ordine è stata chiusa, ma il team non è stato copiato sul progetto successivo: vanno assegnate le persone a mano dalla scheda del progetto.',
  // Le rotte della scadenza efficace contano solo i primi 5000 task aperti del perimetro.
  TASK_EFFECTIVE_DUE_TRUNCATED:
    'I task aperti sono più di 5000: il conto si ferma ai primi 5000, quindi il numero può essere più basso del vero.',
  // Il formato Excel del rapportino non è supportato: il sistema ha prodotto un PDF al suo posto.
  SERVICE_REPORT_OUTPUT_FORMAT_FALLBACK:
    'Il formato Excel non è disponibile: il rapportino è stato preparato in PDF.',
};

const ENTRY_MISSING_LABELS: Record<string, string> = {
  project: 'il progetto', projectSubActivity: 'l’attività', description: 'la descrizione',
};

/** «Mancano il progetto e la descrizione.» Con meno di due campi resta il testo fisso. */
function entryMissingMessage(meta: ErrorMeta, sourceRow = false): string | null {
  const labels = (Array.isArray(meta.missing) ? meta.missing : [])
    .map((f) => ENTRY_MISSING_LABELS[String(f)])
    .filter(Boolean);
  if (labels.length < (sourceRow ? 1 : 2)) return null;
  const list = labels.length === 1 ? labels[0] : `${labels.slice(0, -1).join(', ')} e ${labels[labels.length - 1]}`;
  const verb = labels.length === 1 ? 'manca' : 'mancano';
  return sourceRow
    ? `Alla riga di partenza ${verb} ${list}: completala e ripeti.`
    : `Alla riga ${verb} ${list}: completala e salva di nuovo.`;
}

interface DependencyStep {
  id?: string; taskNumber?: number | null; title?: string | null;
  event?: string; waitsFor?: string; waitsForEvent?: string; via?: string;
}

function taskRef(n: { taskNumber?: number | null; title?: string | null } | undefined): string {
  if (!n) return 'un altro task';
  return n.taskNumber != null ? `#${n.taskNumber}` : `«${n.title ?? 'senza titolo'}»`;
}

/** «l’avvio di #12 aspetta la chiusura di #13; …», dal cammino del server. */
function deadlockText(m: ErrorMeta, byHierarchy: boolean, intro = 'Questo vincolo bloccherebbe i task per sempre'): string | null {
  const path = Array.isArray(m.path) ? m.path as DependencyStep[] : [];
  if (path.length === 0) return null;
  const byId = new Map(path.map((p) => [p.id, p]));
  const event = (e?: string) => (e === 'finish' ? 'la chiusura' : 'l’avvio');
  const chain = path.map((p) => (p.via === 'hierarchy'
    ? `${event(p.event)} di ${taskRef(p)} aspetta tutto il suo ramo, compreso ${taskRef(byId.get(p.waitsFor))}`
    : `${event(p.event)} di ${taskRef(p)} aspetta ${event(p.waitsForEvent)} di ${taskRef(byId.get(p.waitsFor))}`));
  return `${intro}: ${chain.join('; ')}.`
    + (byHierarchy ? ' Un task con la regola di chiusura «bloccata» non si chiude finché il suo ramo è aperto.' : '');
}

function dependencyUnsatisfiedText(m: ErrorMeta): string | null {
  const blockers = Array.isArray(m.blockedBy) ? m.blockedBy as Array<{ taskNumber?: number | null; title?: string | null }> : [];
  if (blockers.length === 0) return null;
  const what = m.gate === 'finish' ? 'chiudere' : 'avviare';
  const remedy = m.canOverride === true
    ? 'Puoi forzarlo scrivendo il motivo.'
    : 'Lo può forzare chi gestisce il progetto o il gruppo.';
  return `Non si può ancora ${what}: vincolo non soddisfatto con ${blockers.map(taskRef).join(', ')}. ${remedy}`;
}

export const API_ERROR_MESSAGE_BUILDERS: Record<string, (meta: ErrorMeta) => string | null> = {
  /* Vincoli fra task: il messaggio nomina i task, che sono la cosa da sapere. */
  TASK_DEPENDENCY_UNSATISFIED: (m) => dependencyUnsatisfiedText(m),
  TASK_DEPENDENCY_CYCLE: (m) => deadlockText(m, false),
  TASK_DEPENDENCY_HIERARCHY_DEADLOCK: (m) => deadlockText(m, true),
  TASK_CLOSURE_POLICY_DEADLOCK: (m) => deadlockText(m, true, 'Con la chiusura «bloccata» il task non si chiuderebbe mai'),
  SETTING_CLOSURE_POLICY_DEADLOCK: (m) => deadlockText(m, true, 'Con questa regola di chiusura alcuni task non si chiuderebbero mai'),
  /* L'ambito di una circolare: il motivo dice cosa cambiare. `missingClaim` è un permesso, gli altri
     tre sono la posizione dell'azienda nel gruppo. */
  CIRCULAR_SCOPE_DENIED: (m) => {
    switch (str(m.reason)) {
      case 'noSubsidiaries': return 'Questa azienda non ha controllate: scegli «Solo questa azienda» o «Tutto il gruppo».';
      case 'groupNotAllowed': return 'La capogruppo non permette a questa azienda di scrivere a tutto il gruppo: scegli un ambito più stretto, o chiedi alla capogruppo.';
      case 'platformMasterOnly': return 'Si scrive a tutta la piattaforma solo dall’azienda che la gestisce.';
      case 'missingClaim': return 'Non hai il permesso per questo passaggio: chiedilo a chi amministra l’azienda.';
      default: return null;
    }
  },
  /* Lo stesso codice fiscale o la stessa email due volte nella stessa azienda. `employeeIsActive`
     dice se la scheda che c'è già è cessata: allora il rimedio è riaprirla, non crearne un'altra.
     Senza `meta` (due salvataggi incrociati, li ferma l'indice) si dice solo il fatto. */
  EMPLOYEE_TAX_CODE_TAKEN: (m) => (m.employeeIsActive === false
    ? 'Questo codice fiscale è già di un dipendente cessato di questa azienda: riapri la sua scheda invece di crearne un’altra.'
    : 'Questo codice fiscale è già di un dipendente di questa azienda: apri la sua scheda invece di crearne un’altra.'),
  EMPLOYEE_EMAIL_TAKEN: (m) => (m.employeeIsActive === false
    ? 'Questa email aziendale è già di un dipendente cessato di questa azienda: riapri la sua scheda, o usa un’altra email.'
    : 'Questa email aziendale è già di un dipendente di questa azienda: apri la sua scheda, o usa un’altra email.'),
  /* Più progetti scelti: il rifiuto non dice quale, il messaggio lo ammette. */
  TASK_PROJECT_PERIMETER_DENIED: (m) => (Array.isArray(m.projectIds) && m.projectIds.length > 1
    ? 'Fra i progetti scelti ce n’è almeno uno di cui non sei responsabile né nel team, quindi non ne vedi tutti i task. Togli dal filtro «Progetto» quelli che non ti riguardano, o chiedi a chi li gestisce di aggiungerti al team.'
    : null),
  /* ── Il cancello dei moduli (BE-CONTRACT-TENANT-MODULES-v1 §1) ──
     È un 403 che **non** è un permesso: il super-admin non lo scavalca, e chiedere un claim non
     lo risolve. Il `detail` del BE nomina il modulo con l'etichetta del catalogo («Il modulo
     'Flotta: modulo abilitato' non è attivo per questa azienda»), ma quel `detail` è **fuori** dal
     `meta` e ai costruttori arriva solo il `meta`: il nome lo si ricava da `meta.module` con il
     catalogo di `shared/lib/tenantModules`. Quello che il BE non dice è dove si accende, ed è la
     parte che manda la persona giusta nel posto giusto. */
  EXTERNAL_SYNC_LOCKED: (m) => {
    const fields = Array.isArray(m.lockedFields) ? (m.lockedFields as unknown[]).map(String).filter(Boolean) : [];
    const one = str(m.lockedField);
    const list = fields.length ? fields : one ? [one] : [];
    const source = str(m.syncSource);
    const who = source ? `da ${systemName(source)}` : 'dal gestionale collegato';
    if (str(m.operation) === 'delete') {
      return `Questa riga arriva ${who} e qui non si elimina: va tolta lì, e sparisce da sola al prossimo allineamento.`;
    }
    const what = list.length === 1 ? `Il campo «${list[0]}» arriva` : list.length > 1 ? `I campi ${list.map((f) => `«${f}»`).join(', ')} arrivano` : 'Questo campo arriva';
    return `${what} ${who} e qui non si modifica: la correzione va fatta lì, e arriva da sola al prossimo allineamento.`;
  },
  MODULE_DISABLED: (m) => {
    const modulo = str(m.module);
    const base = modulo
      ? `Il modulo «${moduleName(modulo)}» non è attivo per questa azienda.`
      : 'Questo modulo non è attivo per questa azienda.';
    /* Dal 26-10-2026 non lo riaccende chi amministra l'azienda: o lo attiva la piattaforma, o lo
       ha spento la capogruppo. «Moduli attivi» dice quale dei due, e a chi chiedere. */
    return `${base} Non è un permesso: i moduli li attiva la piattaforma, o li spegne la capogruppo. In Amministrazione → Moduli attivi trovi quale dei due, e a chi chiedere.`;
  },
  MODULE_UNKNOWN: (m) => {
    const codes = Array.isArray(m.moduleCodes) ? (m.moduleCodes as unknown[]).map(String).filter(Boolean) : [];
    return codes.length
      ? `Il sistema non conosce ${codes.length === 1 ? 'il modulo' : 'i moduli'} ${codes.map((c) => `«${moduleName(c)}»`).join(', ')}. Aggiorna la pagina e riprova; se succede ancora, segnalalo.`
      : 'Il sistema non conosce uno dei moduli scelti. Aggiorna la pagina e riprova; se succede ancora, segnalalo.';
  },
  SUPPORT_MODULE_DISABLED: (m) => withHint(
    m,
    'Questa azienda non ha attivo il modulo Assistenza: la richiesta non può essere aperta. Contattala per un altro canale.',
  ),
  /*
   * ⚠️ **Il nome di chi ha deciso è l'unica cosa che rende utile questo errore.** «Non si può
   * cambiare» manda a cercare un permesso che c'è già: il divieto è una decisione di un'altra
   * azienda, e il rimedio è chiedere a lei. Il `meta` porta `lockedByTenantName`, `areaLabel` e —
   * per una riga di `tenant-attributes` — `lockedAttributeKey`.
   *
   * ⚠️ Mancava del tutto fino al 05-10-2026: tre file lo nominavano nei commenti e nessuno lo
   * gestiva, quindi arrivava il messaggio generico. L'ha trovato chi lavora su Lares, dove lo
   * stesso punto diceva «Riprova» — che su un divieto permanente è peggio di non dire niente.
   */
  /**
   * I destinatari di un avviso di scadenza, con una voce che non esiste — BE task 1875 §3.
   *
   * ⚠️ **Questo errore è una buona notizia, e il messaggio deve dirlo.** Fino al 05-10-2026 una
   * voce sbagliata passava in silenzio: non corrispondeva a nessun dipendente, l'avviso non
   * partiva e nessuno lo scopriva — un guasto che non lascia traccia. Ora si rifiuta la
   * scrittura, e le voci respinte arrivano in `meta.invalidEntries`.
   */
  NOTIFICATION_RECIPIENTS_INVALID: (m) => {
    const bad = fmtList(m.invalidEntries);
    const kinds = fmtList(m.allowedKinds);
    /* Le voci sono guid: elencarle serve a chi guarda il valore salvato, non a riconoscere una
       persona. Quindi si dice **quante** sono, e si elencano solo se sono poche. */
    const count = Array.isArray(m.invalidEntries) ? m.invalidEntries.length : 0;
    const which = count > 0 && count <= 3 && bad ? ` (${bad})` : '';
    const allowed = kinds ? ` Si possono indicare: ${kinds}.` : '';
    return count > 1
      ? `${count} destinatari non esistono in quest’azienda${which}: toglili e riprova.${allowed}`
      : `Un destinatario non esiste in quest’azienda${which}: toglilo e riprova.${allowed}`;
  },
  /* Task 1921: la data da cui si potrà, con `reason: seniority`; con `denied` nessuna data. */
  /*
   * Il periodo chiuso dalla scadenza (`overdueBehaviour: lock`, BE 09-10-2026). Il `detail` del server
   * è già in italiano; qui si ricompone dalle date per non dipendere dalla sua formulazione.
   */
  OBLIGATION_PERIOD_LOCKED: (m) => {
    const dmy = (iso: string | null) => { const [y, mo, d] = (iso ?? '').slice(0, 10).split('-'); return y && mo && d ? `${d}/${mo}` : ''; };
    const from = dmy(str(m.periodFrom)), to = dmy(str(m.periodTo)), since = dmy(str(m.lockedSince));
    const what = str(m.kind) === 'smart_working' ? 'non si chiedono più giorni' : 'non si pianifica più';
    const period = from && to ? `Il periodo dal ${from} al ${to}` : 'Questo periodo';
    return `${period} si è chiuso${since ? ` il ${since}` : ''}: ${what}. Chiedi al tuo responsabile, che può farlo per te.`;
  },
  SMART_WORKING_NOT_ELIGIBLE: (m) => {
    const from = str(m.eligibleFrom);
    if (str(m.reason) === 'denied' || !from) {
      return 'Per te lo smart working non è previsto: le richieste di giorni da remoto non si possono inviare. Se pensi sia un errore, parlane con il tuo responsabile.';
    }
    const [y, mo, d] = from.slice(0, 10).split('-');
    return `Lo smart working si può chiedere dal ${d}/${mo}/${y}: lo dice la regola dell’azienda sull’anzianità. Prima può concedertelo il tuo responsabile.`;
  },
  /* Spostare un'azienda vuol dire amministrare la madre che lascia (`side: from`) e quella nuova
     (`side: to`): una figlia non si stacca da sola. */
  TENANT_PARENT_NOT_ADMINISTRABLE: (m) => (str(m.side) === 'from'
    ? 'Un’azienda non si stacca da sola dal gruppo: lo decide chi le sta sopra.'
    : 'Non puoi mettere l’azienda sotto un gruppo che non gestisci. Scegli una capogruppo del tuo ramo.'),
  /* Quota, tipo di legame, «solo holding» e protezione dalla cancellazione: li cambia chi sta sopra. */
  TENANT_GROUP_POSITION_NOT_WRITABLE: (m) => {
    const labels: Record<string, string> = {
      parentShare: 'la quota', relationshipType: 'il tipo di legame', isHoldingOnly: '«solo holding»',
      isErasable: 'la protezione dalla cancellazione',
    };
    const fields = Array.isArray(m.fields) ? m.fields.map(String).map((f) => labels[f] ?? f) : [];
    const what = fields.length ? `${fields.join(', ')}: ` : '';
    return `La posizione di un’azienda nel gruppo la decide chi le sta sopra, non l’azienda stessa (${what}va chiesto alla capogruppo). Gli altri dati si salvano lasciando questi com’erano.`;
  },
  /* Eliminare un'azienda porta via il suo ramo: si ferma se sotto ce n'è una protetta. */
  TENANT_SUBTREE_NOT_ERASABLE: (m) => {
    const names = Array.isArray(m.blockingTenants)
      ? (m.blockingTenants as unknown[]).map((t) => str((t as { name?: unknown } | null)?.name)).filter(Boolean) as string[]
      : [];
    const who = names.length === 1 ? `c’è «${names[0]}», che è protetta`
      : names.length > 1 ? `ci sono aziende protette: ${names.map((n) => `«${n}»`).join(', ')}`
      : 'ci sono aziende protette dalla cancellazione';
    return `Non si elimina: sotto quest’azienda ${who}. Spostale sotto un’altra capogruppo, o togli prima la protezione. Non è stato cancellato niente.`;
  },
  TENANT_AREA_LOCKED: (m) => {
    const area = str(m.areaLabel) || str(m.area);
    const who = str(m.lockedByTenantName);
    const key = str(m.lockedAttributeKey);
    const what = area ? `«${area}»` : 'Questa configurazione';
    const dove = key ? ` La voce è «${key}».` : '';
    /*
     * ⚠️ **Due divieti diversi, e il rimedio cambia.** Dal 05-10-2026 (BE task 1795) un'area può
     * essere chiusa perché **così nascono le controllate**, senza che nessuno l'abbia deciso:
     * `meta.lockLevel = 'systemDefault'` e `lockedByTenantId` a `null`. Dirlo come un divieto di
     * qualcuno manderebbe a chiedere a un'azienda che non ha fatto niente; tacerlo lascerebbe
     * «non si può» senza un seguito.
     */
    if (str(m.lockLevel) === 'systemDefault') {
      return `Di norma le controllate non cambiano ${what}.${dove} Non è un permesso che ti manca: se ti serve, chiedi alla tua capogruppo di aprirla.`;
    }
    const chi = who
      ? `l’ha bloccata ${who}`
      : 'l’ha bloccata un’azienda del gruppo sopra questa';
    return `${what} non si cambia da questa azienda: ${chi}.${dove} Non è un permesso che ti manca: se ti serve, chiedi a loro di aprirla.`;
  },
  /* ── Contratto tecnico dei controller custom (BE API-ERROR-ENRICHMENT-v1) ──
     Su questi il `detail` del BE è già scritto per un utente e porta i numeri reali
     (nome del file, dimensione, tetto): lo si preferisce al testo fisso, e si aggiunge
     l'`hint` quando c'è, perché è la parte che dice come uscirne. */
  /* ── Accessi al portale clienti (BE v1.100) ──
     I tre rifiuti hanno lo stesso codice ma tre cause diverse, e il `detail` del BE dice
     quale: lo si preferisce al testo fisso perché «l'utente è interno» senza dire *perché*
     manda a cercare la condizione sbagliata delle tre. */
  CLIENT_PORTAL_USER_IS_INTERNAL: (m) => withHint(
    m,
    'Questo utente è una persona interna e non può avere un accesso al portale: l’accesso lo declasserebbe a utente-cliente su tutte le sue richieste.',
  ),
  /* `CLIENT_PORTAL_DUPLICATE` («un utente, un accesso») non esiste più: dal P3 l'unicità è per
     coppia (cliente, utente) e il rifiuto è `PORTAL_ACCESS_ALREADY_EXISTS`, fra i messaggi fissi. */
  CLIENT_PORTAL_MANAGE_FORBIDDEN: (m) => withHint(
    m,
    'Per gestire gli accessi al portale serve un permesso dedicato. Chiedilo a un amministratore.',
  ),
  CLIENT_PORTAL_MISSING_LINK: (m) => withHint(m, 'Servono sia l’utente sia il cliente per creare un accesso.'),
  CLIENT_PORTAL_LINK_IMMUTABLE: (m) => withHint(
    m,
    'Utente e cliente di un accesso non si cambiano: revoca questo accesso e creane uno nuovo, così resta la storia di chi poteva entrare.',
  ),
  CLIENT_PORTAL_INVALID_SCOPE: (m) => withHint(m, 'Il livello di visibilità può essere solo «tutto», «amministrativo» o «tecnico».'),
  CLIENT_PORTAL_INVALID_STATUS: (m) => withHint(m, 'Lo stato di un accesso può essere solo attivo, sospeso o revocato.'),

  /* ── Download del portale (BE v1.109.x) ──
     Prima erano un 404 solo, e da fuori i tre casi erano indistinguibili: ci è costato un
     doc di diagnosi su un bug già chiuso. Ora il codice dice quale dei tre è, e i tre
     rimedi sono davvero diversi — riprovare, chiedere il file a noi, oppure segnalarlo
     perché il contenuto è corrotto e nessuno se ne accorgerebbe altrimenti. */
  /* ── Consultazione dei log (BE v1.117.0) ──
     Il `detail` del server è già scritto per una persona: qui si aggiunge solo il rimedio,
     che è diverso per ognuno — restringere il periodo, togliere un filtro, o accorgersi che
     il problema non è la propria richiesta. */
  LOG_INVALID_RANGE: (m) => withHint(m, 'La data di fine precede quella di inizio: invertile e riprova.'),
  /* Questi due sono nati da una nostra segnalazione (v1.119.0): prima un filtro non valido
     veniva **ignorato** e la risposta arrivava con l'insieme completo — un «falso pieno», che
     fa sospettare il sistema invece del filtro. Ora il server rifiuta, e il messaggio dice
     quali valori accetta. */
  LOG_INVALID_STATUS_CLASS: (m) => withHint(
    m,
    'Le classi ammesse sono 2xx, 3xx, 4xx, 5xx, oppure «unknown» per le righe senza esito registrato.',
  ),
  LOG_INVALID_METHOD: (m) => withHint(
    m,
    'Il metodo va scritto per esteso e senza cifre: GET, POST, PUT, PATCH, DELETE. Il sistema verifica la forma, non un elenco chiuso, così un verbo legittimo ma inusuale non viene rifiutato.',
  ),
  LOG_RANGE_TOO_WIDE: (m) => withHint(
    m,
    'Il periodo interrogabile in una volta è di 92 giorni: restringilo, oppure spostalo indietro a blocchi.',
  ),
  LOG_TENANT_OUT_OF_SCOPE: (m) => withHint(
    m,
    'L’azienda richiesta è fuori dal tuo perimetro: togli il filtro per tornare a vedere il tuo e i suoi discendenti. Il sistema rifiuta invece di restituire un elenco vuoto, che ti farebbe credere di guardare i log di quell’azienda.',
  ),
  LOG_TRACE_ID_REQUIRED: (m) => withHint(m, 'Serve l’identificativo della richiesta da ricostruire.'),
  LOG_TRACE_NOT_FOUND: (m) => withHint(
    m,
    'Nessuna riga con questo identificativo **nel perimetro che puoi vedere**: non significa che non esista. Considera anche che i log vengono consolidati periodicamente, quindi una richiesta recente può non essere ancora archiviata.',
  ),
  LOG_TENANT_UNRESOLVED: (m) => withHint(m, 'L’azienda non è stata riconosciuta dalla sessione: rientra e riprova.'),
  LOG_STORE_UNAVAILABLE: (m) => withHint(
    m,
    'Non significa che non ci siano log: l’archivio che li conserva non è raggiungibile in questo momento. È un problema di infrastruttura, non della tua richiesta — riprova fra poco e, se persiste, segnalalo.',
  ),

  /* ── Schedulazione dei job (BE v1.110) ──
     Il `detail` del BE è già scritto bene e porta l'esempio del formato: gli si aggiunge
     solo il perché il server valida in scrittura invece di accettare e scartare dopo. */
  JOB_SCHEDULE_INVALID_CRON: (m) => withHint(
    m,
    'Il cron viene verificato adesso e non al prossimo avvio: un’espressione sbagliata salvata in silenzio farebbe dichiarare al pannello un orario che lo scheduler non userebbe.',
  ),
  JOB_SCHEDULE_NOT_CRON_BASED: (m) => withHint(
    m,
    'Questo job gira a intervallo, non a orario fisso: non ha un cron da impostare. Si può però sospendere.',
  ),
  /* Questi due si incontrano quando la pagina è più vecchia del server — un job rimosso dal
     codice, o un override tolto da qualcun altro nel frattempo. Il rimedio è lo stesso e non è
     ovvio da un codice grezzo: ricaricare, perché l'elenco a schermo non è più quello vero. */
  JOB_SCHEDULE_UNKNOWN_JOB: (m) => withHint(
    m,
    'L’elenco a schermo non corrisponde più ai job presenti sul sistema: ricarica la pagina per aggiornarlo.',
  ),
  JOB_SCHEDULE_OVERRIDE_NOT_FOUND: (m) => withHint(
    m,
    'Questa personalizzazione non c’è più: può averla rimossa qualcun altro. Ricarica per vedere la schedulazione attuale.',
  ),

  MEDIA_NOT_FOUND: (m) => withHint(
    m,
    'Il documento non risulta più disponibile: potrebbe essere stato ritirato. Chiedetecelo pure e ve lo rimandiamo.',
  ),
  MEDIA_CONTENT_EMPTY: (m) => withHint(
    m,
    'Il documento risulta registrato ma il file allegato non è raggiungibile. È un problema nostro, non vostro: segnalatecelo e lo ripubblichiamo.',
  ),
  MEDIA_CONTENT_UNREADABLE: (m) => withHint(
    m,
    'Il file allegato risulta danneggiato e non si riesce ad aprirlo. Segnalatecelo: dobbiamo ricaricarlo noi.',
  ),
  /* 503 dal BE, e a differenza degli altri tre il rimedio e' **riprovare**: il magazzino dei
     documenti non risponde, il file non e' ne' perso ne' rotto. Confonderlo con
     `MEDIA_NOT_FOUND` manderebbe a chiedere un rinvio di un file che c'e'. */
  MEDIA_STORAGE_UNAVAILABLE: (m) => withHint(
    m,
    'L’archivio dei documenti non risponde in questo momento: il file non è perso. Riprova fra poco.',
  ),

  /* ── Annullamenti già lavorati (BE v1.99.0) ──
     Il BE ha scelto il 422 invece dell'idempotenza, e la ragione va riportata a schermo:
     una seconda marcatura silenziosa sovrascriverebbe la nota di chi ci ha già lavorato
     senza che nessuna delle due persone lo sappia. Il `meta` porta chi ha chiuso e quando,
     che è l'informazione che serve per non rifare una telefonata già fatta. */
  FLEET_BOOKING_CANCELLATION_ALREADY_HANDLED: (m) => cancellationAlreadyHandled(m),
  TRAVEL_BOOKING_CANCELLATION_ALREADY_HANDLED: (m) => cancellationAlreadyHandled(m),

  /* ── Griglia di rendicontazione (BE v1.89.0) ──
     Il passo è configurazione di tenant e il vocabolario è chiuso. Il `meta` porta
     `attribute`, `value` e `reportingStepMinutes`: abbastanza per dire quale campo, quale
     valore e — soprattutto — quale valore accettare, senza far rileggere la policy. */
  REPORTING_STEP_VIOLATION: (m) => {
    const step = num(m.reportingStepMinutes);
    const value = num(m.value);
    const campo = HOURS_FIELD[str(m.attribute) ?? ''] ?? 'le ore';
    if (step === null) return 'Le ore non rispettano il passo di registrazione previsto. Correggile e riprova.';
    const near = value === null ? null : Math.round((Math.round((value * 60) / step) * step) / 60 * 100) / 100;
    return near === null
      ? `Le ore si registrano a scatti di ${step} minuti. Correggi ${campo} e riprova.`
      : `${campo === 'le ore' ? 'Le ore' : campo.replace(/^le |^gli /, (x) => x.toUpperCase())} si registrano a scatti di ${step} minuti: ${String(value).replace('.', ',')} non è ammesso, il valore più vicino è ${String(near).replace('.', ',')}.`;
  },
  INVALID_REPORTING_STEP: (m) =>
    withHint(m, 'Il passo di registrazione non è fra quelli ammessi. Valori possibili: 1, 5, 6, 10, 15, 20, 30 o 60 minuti — non vengono arrotondati.'),
  INVALID_REPORTING_MODE: (m) =>
    withHint(m, 'La modalità di inserimento può essere solo «durata» o «inizio e fine». Scegline una delle due.'),
  INVALID_BREAK_MINUTES: (m) =>
    withHint(m, 'La pausa non può essere negativa. Lascia il campo vuoto se non la vuoi dichiarare: è diverso da zero.'),

  FILE_TOO_LARGE: (m) => withHint(m, 'Il file supera la dimensione massima consentita.'),
  ATTACHMENT_TOTAL_LIMIT_REACHED: (m) => withHint(m, 'Gli allegati di questo elemento hanno raggiunto lo spazio massimo. Elimina qualche allegato o carica file più leggeri.'),
  ATTACHMENT_LIMIT_REACHED: (m) => withHint(m, 'Questo elemento ha raggiunto il numero massimo di allegati. Eliminane qualcuno prima di aggiungerne altri.'),
  IMAGE_UNREADABLE: (m) => withHint(m, 'L’immagine non si può leggere. Sono ammesse immagini PNG, JPEG, GIF e WebP integre.'),
  DOCUMENT_TOO_LARGE: (m) => withHint(m, 'Il file è troppo grande per essere archiviato. Caricane una versione compressa o più leggera.'),
  REQUEST_TOO_LARGE: (m) => withHint(m, 'La richiesta supera la dimensione massima accettata dal sistema.'),
  /* Invio della pianificazione, tutto o niente (BE v1.272.0): ogni giorno bloccato porta il codice e
     il `meta` che darebbe la richiesta singola, quindi il motivo si dice con le stesse parole. Dove
     c'è spazio — la bacheca della settimana — i giorni si elencano uno per uno e si possono togliere. */
  SMART_WORKING_PLANNING_ROWS_BLOCKED: (m) => {
    const rows = planningBlockedRows(m);
    if (rows.length === 0) return null;
    const summary = (m.summary ?? {}) as ErrorMeta;
    const total = typeof summary.total === 'number' ? summary.total : null;
    const head = rows.length === 1
      ? `Un giorno${total ? ` su ${total}` : ''} non rispetta le regole dello smart working`
      : `${rows.length} giorni${total ? ` su ${total}` : ''} non rispettano le regole dello smart working`;
    const list = rows.map((r) => `${r.dateLabel}: ${r.message}`).join(' · ');
    return `${head}, e la pianificazione non è partita. ${list}. Togli o cambia quei giorni e inviala di nuovo.`;
  },
  /* Il valore di un campo personalizzato si controlla sul suo tipo (BE v1.272.0): `meta.expectedType`
     dice cosa ci si aspettava. */
  TIMESHEET_ATTRIBUTE_VALUE_INVALID: (m) => {
    const expected: Record<string, string> = {
      int: 'un numero intero',
      decimal: 'un numero (i decimali col punto, per esempio 1250.50)',
      bool: 'sì o no',
      date: 'una data',
      datetime: 'una data con l’ora',
      enum: 'una delle scelte previste',
    };
    const kind = expected[str(m.expectedType) ?? ''];
    return kind ? `Il valore di questo campo personalizzato deve essere ${kind}.` : null;
  },
  /* Sale riunioni (MR-01, BE del 28-10-2026). Sulla conferma il 409 porta la prenotazione che occupa
     già la fascia: la richiesta resta in attesa, e chi l'ha fatta può spostarla o annullarla. */
  MEETING_ROOM_BOOKING_CONFLICT: (m) => str(m.conflictingBookingId)
    ? 'Questa richiesta non si può confermare: nel frattempo la sala è stata confermata a un’altra prenotazione sulla stessa fascia. Resta in attesa finché chi l’ha chiesta non la sposta o la annulla.'
    : null,
  /* `meta.effectiveStatus` è lo stato calcolato dall'orario: dice perché non si può più. */
  MEETING_ROOM_BOOKING_INVALID_STATUS: (m) => {
    switch (str(m.effectiveStatus)) {
      case 'in_progress': return 'La riunione è già iniziata: non si può più annullare né cambiare.';
      case 'completed': return 'La riunione è già finita: non si può più annullare né cambiare.';
      case 'cancelled': return 'La prenotazione è già stata annullata.';
      case 'expired': return 'La richiesta è scaduta senza essere confermata: l’orario è passato.';
      default: return null;
    }
  },
  FK_VIOLATION: (m) => withHint(m, 'Riferimento non valido: l’elemento collegato non esiste, oppure questo record è ancora usato da altri dati e non si può eliminare.'),
  DB_TIMEOUT: (m) => withHint(m, 'Il sistema ha superato il tempo massimo per rispondere. Restringi il periodo o i filtri e riprova.'),
  DB_RESOURCE_LIMIT: (m) => withHint(m, 'Il database ha esaurito le risorse disponibili. Non dipende dai dati inseriti: riprova fra qualche minuto e, se persiste, segnalalo.'),
  MALFORMED_JSON: (m) => withHint(m, 'La richiesta inviata non è valida. Ricarica la pagina e riprova.'),
  INVALID_SIZE: (m) => withHint(m, 'Formato immagine richiesto non valido: sono ammessi solo sm, md e lg.'),
  INTERNAL_ERROR: (m) => withHint(m, 'Errore interno del sistema. Non dipende da quello che hai inserito.'),

  LEAVE_INSUFFICIENT_BALANCE: (m) => {
    const available = m.available, requested = m.requested;
    if (num(available) === null || num(requested) === null) return null;
    return `Saldo insufficiente: chiedi ${fmtQty(requested, m.unitOfMeasure)} ma ne hai ${fmtQty(available, m.unitOfMeasure)}. Riduci il periodo o scegli un altro tipo di assenza.`;
  },

  LEAVE_EVENT_LIMIT_EXCEEDED: (m) => {
    const max = num(m.maxEventsPerYear) ?? num(m.maxEvents);
    if (max === null) return null;
    const used = num(m.currentEventCount);
    const year = num(m.year);
    const usedPart = used === null ? '' : ` (ne hai già usati ${used})`;
    const yearPart = year === null ? "nell'anno" : `nel ${year}`;
    return `Hai raggiunto il limite di ${max} eventi ${yearPart} per questo tipo di assenza${usedPart}. Scegli un altro tipo o attendi il rinnovo annuale.`;
  },

  LEAVE_ADVANCE_NOTICE_INSUFFICIENT: (m) => {
    const min = num(m.minAdvanceDays);
    if (min === null) return null;
    const actual = num(m.actualAdvanceDays);
    const earliest = fmtDate(m.earliestAllowedFromDate);
    const actualPart = actual === null ? '' : ` (ne stai dando ${actual})`;
    const earliestPart = earliest ? ` La prima data richiedibile è il ${earliest}.` : '';
    return `Servono almeno ${min} giorni di preavviso per questo tipo di assenza${actualPart}.${earliestPart} Sposta le date oppure contrassegna la richiesta come urgente.`;
  },

  LEAVE_RETROACTIVE_DENIED: (m) => {
    const limit = num(m.retroactiveLimitDays);
    if (limit === null) return null;
    const latest = fmtDate(m.latestAllowedFromDate);
    if (limit === 0) {
      return `Questo tipo di assenza non ammette richieste retroattive.${latest ? ` La data più indietro consentita è il ${latest}.` : ''} Scegli una data da oggi in avanti.`;
    }
    return `Puoi tornare indietro al massimo di ${limit} giorni.${latest ? ` La data più indietro consentita è il ${latest}.` : ''} Sposta l'inizio del periodo.`;
  },

  LEAVE_BLACKOUT_PERIOD: (m) => {
    const name = str(m.blackoutName) ?? str(m.blockingPeriodName);
    const from = fmtDate(m.blackoutFromDate), to = fmtDate(m.blackoutToDate);
    const window = from && to ? ` (${from} – ${to})` : '';
    const escape = m.allowEmergencies === true
      ? ' Scegli date fuori dal periodo, oppure contrassegna la richiesta come urgente: per questo blocco le urgenze sono ammesse.'
      : ' Scegli date fuori dal periodo.';
    if (!name && !window) return null;
    return `Le date ricadono nel periodo bloccato${name ? ` «${name}»` : ''}${window}.${escape}`;
  },

  LEAVE_DOCUMENT_REQUIRED: (m) => {
    const alternatives = fmtList(m.acceptedAlternatives);
    const mode = str(m.documentSubmissionMode);
    if (mode === 'both') return 'Per questo tipo di assenza servono sia il codice del documento sia l\'allegato. Compila entrambi e riprova.';
    if (alternatives) return `Serve il documento giustificativo: puoi fornire ${alternatives}.`;
    if (mode === 'code') return 'Serve il codice del documento giustificativo (es. protocollo INPS). Inseriscilo e riprova.';
    if (mode === 'attachment') return 'Serve l\'allegato del documento giustificativo. Caricalo e riprova.';
    return null;
  },

  LEAVE_INVALID_CERTIFICATE_CODE_FORMAT: (m) => {
    const pattern = str(m.pattern);
    return pattern
      ? `Il codice certificato non rispetta il formato atteso (${pattern}). Controlla il codice sul documento e riscrivilo.`
      : null;
  },

  LEAVE_SUBSTITUTE_REQUIRED: (m) => {
    const after = num(m.requiredAfterDays);
    return after === null
      ? 'Per questa richiesta serve indicare un sostituto. Selezionalo dal campo «Sostituto».'
      : `Serve un sostituto per le assenze di ${after} giorni o più. Selezionalo dal campo «Sostituto».`;
  },

  LEAVE_SUBSTITUTE_TENANT_MISMATCH: (m) =>
    m.groupPoolEnabled === true
      ? 'Il collega scelto appartiene a un\'altra azienda del gruppo e non è selezionabile per questa richiesta. Scegli un sostituto della tua azienda.'
      : 'Il sostituto deve appartenere alla tua stessa azienda. Scegli un collega dall\'elenco proposto.',

  /* ── Flussi di lavorazione (BE v1.150.0) ──
     `WORKFLOW_STATE_UNKNOWN` arriva **con l'elenco degli stati ammessi nel meta**, e il BE l'ha
     fatto apposta: «è l'elenco vero di quel flusso, non una costante da tenere allineata». Un
     messaggio che dice solo «stato non valido» costringerebbe a indovinare, o a ricopiare qui un
     vocabolario che ogni azienda cambia. Lo stesso codice e lo stesso meta valgono in creazione e
     in transizione: chi sbaglia stato nei due casi ha lo stesso problema. */
  WORKFLOW_STATE_UNKNOWN: (m) => {
    const statuses = Array.isArray(m.allowedStates)
      ? fmtList((m.allowedStates as Array<Record<string, unknown>>)
        .map((x) => str(x.name) ?? str(x.code))
        .filter(Boolean))
      : null;
    const flow = str(m.workflowName);
    if (!statuses) {
      return flow
        ? `Questo stato non è previsto dal flusso «${flow}». Apri il task e guarda la fascia «Stato», in cima alla scheda, per vedere quelli ammessi.`
        : null;
    }
    return flow
      ? `Il flusso «${flow}» non prevede questo stato. Quelli ammessi sono: ${statuses}.`
      : `Stato non previsto dal flusso. Quelli ammessi sono: ${statuses}.`;
  },

  // `meta.allowedFor` dice chi può: è il rimedio.
  WORKFLOW_TRANSITION_FORBIDDEN_FOR_USER: (m) => {
    switch (str(m.allowedFor)) {
      case 'assignee': return 'Questo passaggio lo fa solo chi ha il task in carico: chiediglielo, o fatti assegnare il task.';
      case 'queue_owner': return 'Questo passaggio lo fa solo il responsabile della coda del task: chiediglielo.';
      case 'queue_member': return 'Questo passaggio lo fa solo chi lavora nella coda del task: chiedilo a un collega di quella coda.';
      case 'claim': return 'Questo passaggio richiede un permesso che il tuo profilo non ha: chiedilo all’amministratore.';
      default: return null;
    }
  },

  WORKFLOW_TRANSITION_REQUIRES_FIELDS: (m) => {
    const fields = Array.isArray(m.fields) ? m.fields.map(String).filter(Boolean).map(taskFieldLabel) : [];
    if (fields.length === 0) return null;
    // Vanno compilati tutti: «e», non la «o» di `fmtList`.
    const list = fields.length === 1 ? fields[0] : `${fields.slice(0, -1).join(', ')} e ${fields[fields.length - 1]}`;
    return `Prima di questo passaggio vanno compilati: ${list}. Aprili nella scheda del task.`;
  },

  LEAVE_INVALID_STATUS_TRANSITION: (m) => {
    const attempted = str(m.attemptedTransition);
    const allowed = Array.isArray(m.allowedTransitions)
      ? fmtList(m.allowedTransitions.map((t) => TRANSITION_LABELS[String(t)] ?? String(t)))
      : null;
    if (!attempted) return null;
    const verb = TRANSITION_LABELS[attempted] ?? attempted;
    return allowed
      ? `Non puoi ${verb} la richiesta nello stato attuale. Da qui puoi solo: ${allowed}.`
      : `Non puoi ${verb} la richiesta nello stato attuale. Ricarica la pagina: qualcuno potrebbe averla già lavorata.`;
  },

  LEAVE_EMPLOYEE_TERMINATED: (m) => {
    const end = fmtDate(m.contractEndDate);
    return end
      ? `Il rapporto di lavoro risulta chiuso al ${end}: non è possibile registrare assenze oltre quella data.`
      : 'Il rapporto di lavoro del dipendente risulta chiuso: non è possibile registrare assenze.';
  },

  LEAVE_INVALID_DATE_RANGE: (m) => {
    const from = fmtDate(m.fromDate), to = fmtDate(m.toDate);
    return from && to
      ? `Intervallo non valido: la fine (${to}) precede l'inizio (${from}). Inverti le due date.`
      : null;
  },

  LEAVE_INVALID_PART_OF_DAY: (m) =>
    m.contextHint === 'hours-required-with-part-of-day-half'
      ? 'La mezza giornata vale solo su un giorno singolo e su tipi di assenza a giornate. Per un periodo più lungo scegli «Intera», per un\'assenza parziale indica gli orari.'
      : null,

  LEAVE_INVALID_UNIT_OF_MEASURE: (m) => {
    const allowed = fmtList(m.allowedValues);
    return allowed ? `Unità di misura non ammessa per questo tipo di assenza: usa ${allowed}.` : null;
  },

  LEAVE_ATTACHMENT_TYPE_MISMATCH: (m) => {
    const allowed = fmtList(m.allowedValues);
    return allowed ? `Tipo di allegato non ammesso per questa richiesta: sono accettati ${allowed}.` : null;
  },

  LEAVE_EVENT_DURATION_EXCEEDED: (m) => {
    const cap = num(m.entitledDaysPerEvent) ?? num(m.maxDaysPerEvent);
    if (cap === null) return null;
    const requested = num(m.requested);
    return `Su questo tipo di assenza ogni evento può durare al massimo ${fmtDays(cap)}${requested === null ? '' : `, ne stai chiedendo ${fmtDays(requested)}`}. Accorcia il periodo o spezza la richiesta in più eventi.`;
  },

  /* ── Quale delle tre porte di SCOPE_PROJECT_MISMATCH ──
     Il server non lo scrive nel testo ma nelle chiavi del `meta`: dalla rendicontazione arrivano
     `projectTaskId` e `nodeProjectId` (l'attività è nella WBS di un altro progetto), dall'albero —
     dei task o dei nodi WBS, stessa regola — arriva `parentProjectId`, anche quando vale `null`
     perché il padre non ha progetto. Senza nessuna delle due si resta sul testo neutro. */
  SCOPE_PROJECT_MISMATCH: (m) => {
    if ('nodeProjectId' in m) {
      return 'L’attività scelta è di un altro progetto: scegline una del progetto della riga, oppure cambia progetto.';
    }
    if ('parentProjectId' in m) {
      return 'Padre e figlio devono stare nello stesso progetto: tutti e due in nessuno, o tutti e due nello stesso.';
    }
    return null;
  },

  /* ── I campi obbligatori della riga: il server risponde il primo, `meta.missing` li porta tutti ── */
  PROJECT_REQUIRED: (m) => entryMissingMessage(m),
  SUB_ACTIVITY_REQUIRED: (m) => entryMissingMessage(m),
  DESCRIPTION_REQUIRED: (m) => entryMissingMessage(m),
  ENTRY_INCOMPLETE: (m) => entryMissingMessage(m, true),

  /** Non è del contratto leave, ma il payload quota è lo stesso in tutto il BE. */
  QUOTA_EXCEEDED: (m) => {
    const limit = num(m.limit) ?? num(m.max);
    const current = num(m.current) ?? num(m.used);
    if (limit === null) return null;
    return `Hai raggiunto il limite di ${limit} previsto dalla tua licenza${current === null ? '' : ` (in uso: ${current})`}. Aggiorna il piano o libera un elemento esistente.`;
  },

  /* ── Posta in uscita: `meta.field` dice quale casella è sbagliata ──
     Dal 19-10-2026 il rifiuto riguarda anche `deliveryMode` («spedisci» o «solo registrare»), che
     non è un indirizzo: un messaggio unico sugli indirizzi mandava a cercare l'errore nel posto
     sbagliato. */
  EMAIL_ROUTING_INVALID: (m) => {
    switch (str(m.field)) {
      case 'redirectTo': return 'L’indirizzo a cui dirottare la posta non è valido: col dirottamento acceso serve un indirizzo e-mail completo.';
      case 'allowedRecipients': return 'Uno dei destinatari ammessi non è valido, o l’elenco è troppo lungo: scrivi indirizzi completi o domini con la @ davanti (es. @maestrale.it).';
      case 'deliveryMode': return 'La modalità della posta non è valida: scegli fra «Spedisci» e «Solo registrare».';
      case 'recipient': return 'L’indirizzo a cui mandare la prova non è valido: scrivi un indirizzo e-mail completo.';
      default: return null;
    }
  },

  /* ── Pianificazione delle attività: lo stato del piano dice perché l'azione non passa ── */
  PLANNING_INVALID_STATE_TRANSITION: (m) => {
    const state = PLANNING_STATE_LABELS[str(m.currentStatus) ?? ''];
    const action = PLANNING_ACTION_LABELS[str(m.action) ?? ''];
    if (!state || !action) return null;
    return `Un piano ${state} non si può ${action}: ricarica la pagina per vedere com’è adesso.`;
  },
  PLANNING_ENTRY_NOT_CONSUMABLE: (m) => {
    const state = PLANNING_STATE_LABELS[str(m.status) ?? ''];
    if (!state) return null;
    return `Il piano scelto è ${state}: una registrazione di ore si collega solo a un piano confermato o già in parte rendicontato. Togli il collegamento, oppure conferma prima il piano.`;
  },
  TIMESHEET_MONTH_LOCKED: (m) => {
    const month = num(m.month);
    const year = num(m.year);
    if (month === null || year === null) return null;
    return `La rendicontazione di ${String(month).padStart(2, '0')}/${year} è stata chiusa: va riaperta prima di modificarla.`;
  },
  PLANNING_SERIES_HAS_LIVE_OCCURRENCES: (m) => {
    const n = num(m.liveCount);
    if (n === null) return null;
    return `La serie ha ancora ${n === 1 ? 'una giornata attiva' : `${n} giornate attive`}: annulla la serie prima di eliminarla.`;
  },
  PLANNING_SERIES_RANGE_TOO_LONG: (m) => {
    const days = num(m.days);
    if (days === null) return null;
    return `La serie coprirebbe ${days} giorni, e al massimo ne copre 366: accorcia il periodo, o dividilo in due serie.`;
  },
  TOO_MANY_DATES: (m) => {
    const max = num(m.maxDates);
    const asked = num(m.dates);
    if (max === null) return null;
    return `Si copia su al massimo ${max} giorni per volta${asked === null ? '' : ` (ne hai scelti ${asked})`}: scegline meno, e ripeti per gli altri.`;
  },
  /* Trasferte e prenotazioni dei mezzi: quello che serve non è «riprova più tardi», è quando. */
  REMINDER_TOO_SOON: (m) => {
    const next = str(m.nextAllowedAt);
    const when = next ? formatLockEnd(next) : null;
    return when ? `Il responsabile è già stato sollecitato da poco: il prossimo sollecito si può mandare dal ${when}.` : null;
  },

  /* ── Avvisi che arrivano su una risposta riuscita, e che qualcuno legge con `messageForCode` ──
     Stanno qui e non in `API_WARNING_MESSAGE_BUILDERS` perché le assenze (`leaveWarningText`)
     risolvono gli avvisi con `messageForCode`, che di builder legge solo questi. */
  LEAVE_CROSS_YEAR_HOURS_FALLBACK: (m) => {
    const year = num(m.registeredOnYear);
    if (year === null) return null;
    return `Il permesso scavalca la fine dell’anno: le ore sono state contate tutte sul saldo del ${year}, non divise fra i due anni. Se vanno divise, fai due richieste, una per anno.`;
  },
};

/** Stati del piano (`PlanningEntryStatus`) come si leggono dopo «un piano…». */
const PLANNING_STATE_LABELS: Record<string, string> = {
  draft: 'in bozza',
  confirmed: 'confermato',
  partially_consumed: 'già in parte rendicontato',
  consumed: 'già rendicontato',
  cancelled: 'annullato',
  expired: 'scaduto',
};

/** Le azioni di `PlanningWorkflowController` come verbo dopo «non si può…». */
const PLANNING_ACTION_LABELS: Record<string, string> = {
  confirm: 'confermare',
  cancel: 'annullare',
};

/* ------------------------------------------------------------------------- *
 *  Avvisi su risposta riuscita (`meta.warnings`)
 * ------------------------------------------------------------------------- */

/**
 * Forma di un avviso così come arriva: `{ code, message?, params? }`. ⚠️ Non tutti i controller
 * mettono i dati in `params`: rapportini e prenotazioni dei mezzi li scrivono **accanto** al
 * codice (`{ code, outputFormat }`, `{ code, policyEndDate }`). Si leggono tutti e due.
 */
export interface ApiWarningLike {
  code: string;
  message?: string;
  params?: Record<string, unknown>;
}

/** `params` più i campi scritti accanto al codice: il dato del caso, da qualunque parte arrivi. */
function warningData(w: ApiWarningLike): ErrorMeta {
  const { code: _c, message: _m, params, ...rest } = w as ApiWarningLike & Record<string, unknown>;
  return { ...rest, ...(params && typeof params === 'object' ? params : {}) };
}

const plural = (n: number, one: string, many: string) => (n === 1 ? `1 ${one}` : `${n} ${many}`);

/**
 * Avvisi costruiti dai dati del caso. Hanno la precedenza anche sul testo del sistema: nessuno
 * dei controller che li manda scrive un `message`, e quando lo scrive (`TASK_EFFECTIVE_DUE_TRUNCATED`)
 * nomina i parametri della richiesta, non quello che l'utente vede.
 *
 * ⚠️ Il tono è quello di un'operazione **riuscita**: «è passata, e c'è questo da sapere». Alcuni
 * codici (`SMART_WORKING_BLACKOUT_PERIOD`) sono anche errori, con un testo da rifiuto: per questo
 * l'avviso ha il suo builder e non riusa quello dell'errore.
 */
export const API_WARNING_MESSAGE_BUILDERS: Record<string, (data: ErrorMeta) => string | null> = {
  /* Il presidio «avvisa» (`isHardBlock = false`): uno per giorno sotto il minimo. */
  SMART_WORKING_QUORUM_WARNING: (d) => {
    const day = fmtDate(d.date);
    const present = num(d.presentCount);
    const min = num(d.minRequired);
    const name = str(d.quorumName);
    if (!day || present === null || min === null) return null;
    return `Il ${day} in sede resterebbero ${plural(Math.max(0, present), 'persona', 'persone')}, sotto il minimo di ${min}${name ? ` del presidio «${name}»` : ''}. La richiesta è passata lo stesso: conviene accordarsi con i colleghi su quel giorno.`;
  },
  SMART_WORKING_BLACKOUT_PERIOD: (d) => {
    const name = str(d.blackoutName);
    return `Le date cadono nel periodo ${name ? `«${name}»` : 'di blocco'}, in cui lo smart working è sconsigliato. La richiesta è passata lo stesso: chi approva lo vedrà.`;
  },
  SERVICE_REPORT_OUTPUT_FORMAT_FALLBACK: (d) => {
    const fmt = str(d.outputFormat);
    const label = fmt ? (OUTPUT_FORMAT_LABELS[fmt] ?? fmt.toUpperCase()) : null;
    return `Il rapportino è partito in PDF: il formato ${label ? `${label} ` : ''}scelto nella configurazione di invio non è ancora disponibile. Per non vedere più questo avviso, scegli PDF nella configurazione.`;
  },
  SERVICE_REPORT_PLACEHOLDER_UNRESOLVED: (d) => {
    const ph = str(d.placeholder);
    return `Nel modello del rapportino ${ph ? `il segnaposto «{${ph}}» non corrisponde` : 'un segnaposto non corrisponde'} a nessun dato ed è rimasto scritto così: correggi il modello nella configurazione di invio.`;
  },
  FLEET_INSURANCE_EXPIRING_DURING_BOOKING: (d) => {
    const day = fmtDate(d.policyEndDate);
    return `La polizza del mezzo scade${day ? ` il ${day}` : ''}, durante la prenotazione: avvisa chi gestisce la flotta perché la rinnovi in tempo.`;
  },
  FLEET_INSPECTION_EXPIRING_DURING_BOOKING: (d) => {
    const day = fmtDate(d.inspectionExpiry);
    return `La revisione del mezzo scade${day ? ` il ${day}` : ''}, durante la prenotazione: avvisa chi gestisce la flotta.`;
  },
  FLEET_BOOKING_ODOMETER_INCONSISTENT: (d) => {
    const start = num(d.odometerStart);
    const known = num(d.vehicleOdometer);
    if (start === null || known === null) return null;
    return `I chilometri di partenza (${start}) sono meno di quelli già registrati sul mezzo (${known}): la partenza è salvata, ma controlla di averli letti giusti.`;
  },
  FLEET_BOOKING_OVERLAP_OVERRIDDEN: () =>
    'La prenotazione si sovrappone a un’altra sullo stesso mezzo ed è stata confermata lo stesso: avvisa chi ha l’altra prenotazione.',
  FLEET_TRAFFIC_VIOLATION_MULTIPLE_BOOKINGS_MATCH: (d) => {
    const n = Array.isArray(d.candidateBookingIds) ? d.candidateBookingIds.length : null;
    return `All’ora della multa il mezzo risulta in ${n ? `${n} prenotazioni` : 'più prenotazioni'}: la multa resta senza responsabile, scegli tu a chi attribuirla.`;
  },
};

/** I formati di uscita dei rapportini (`customer-acknowledgment-configs.outputFormat`). */
const OUTPUT_FORMAT_LABELS: Record<string, string> = { pdf: 'PDF', html: 'HTML', xlsx: 'Excel' };

export class JsonApiHttpError extends Error {
  readonly status: number;
  readonly errors: JsonApiError[];

  constructor(status: number, errors: JsonApiError[], message?: string) {
    super(message ?? errors[0]?.title ?? errors[0]?.detail ?? `HTTP ${status}`);
    this.name = 'JsonApiHttpError';
    this.status = status;
    this.errors = errors;
  }

  /** Returns a map of "field.path" -> first matching error.detail, for form binding. */
  toFieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const err of this.errors) {
      const ptr = err.source?.pointer ?? err.source?.parameter;
      if (!ptr) continue;
      const field = ptr.replace(/^\/data\/attributes\//, '').replace(/^\/data\/relationships\//, '').replace(/\//g, '.');
      if (!out[field]) out[field] = err.detail ?? err.title ?? 'Errore di validazione';
    }
    return out;
  }

  /**
   * Primo errore che porta un code, con il **suo** `meta` accanto. Tenerli insieme è il
   * punto: prendere il code da `errors[2]` e il meta da `errors[0]` produrrebbe messaggi
   * con i numeri di un altro errore, che è peggio di nessun numero.
   */
  private findCoded(): { code: ApiErrorCode; meta: Record<string, unknown> } | null {
    for (const err of this.errors) {
      const meta = (err.meta ?? {}) as Record<string, unknown>;
      const code = (typeof meta.code === 'string' ? meta.code : undefined) ?? err.code;
      if (code) return { code: code as ApiErrorCode, meta };
    }
    return null;
  }

  /** Restituisce il primo `meta.code` o `code` noto, se presente. */
  getApiCode(): ApiErrorCode | null {
    return this.findCoded()?.code ?? null;
  }

  /** Payload strutturato dell'errore che ha dato il code — per UI che vogliono i dati grezzi. */
  getApiMeta(): Record<string, unknown> | null {
    return this.findCoded()?.meta ?? null;
  }

  /**
   * Identificativo di richiesta del BE (`API-ERROR-ENRICHMENT-v1`): sempre presente sui
   * 5xx dei controller custom. È l'unico modo per ritrovare la riga di log completa senza
   * riprodurre il caso, quindi va mostrato — un utente che riporta «errore interno» non dà
   * niente su cui indagare, uno che riporta un traceId sì.
   */
  getTraceId(): string | null {
    for (const err of this.errors) {
      const t = err.meta?.traceId;
      if (typeof t === 'string' && t) return t;
    }
    return null;
  }

  /** Suggerimento del BE: perché è andata così e cosa si può fare. */
  getHint(): string | null {
    for (const err of this.errors) {
      const h = err.meta?.hint;
      if (typeof h === 'string' && h) return h;
    }
    return null;
  }

  /**
   * Messaggio user-facing, in ordine di specificità:
   *   1. costruito dai dati in `meta` (dice i numeri e cosa fare),
   *   2. testo fisso della tabella per quel code,
   *   3. `detail` del BE — che però è pensato per audit/log e può contenere suffissi
   *      tecnici, quindi resta l'ultima spiaggia.
   */
  getUserMessage(): string {
    const found = this.findCoded();
    if (found) {
      const built = API_ERROR_MESSAGE_BUILDERS[found.code]?.(found.meta);
      if (built) return built;
      if (API_ERROR_MESSAGES[found.code]) return API_ERROR_MESSAGES[found.code];
    }

    /**
     * I 500 di JsonApiDotNetCore hanno `detail` scritti per chi legge un log, non per chi
     * sta usando l'applicativo: «An unhandled error occurred while processing this request»
     * e «Failed to persist changes in the underlying data store» non dicono all'utente né
     * cosa è andato storto né cosa fare — e soprattutto non dicono la cosa più utile, che
     * **non dipende da quello che ha inserito** e riprovare non serve.
     *
     * Caso reale (31-07-2026, allegati ai task): il salvataggio falliva per un vincolo di
     * integrità del database, e l'utente leggeva «unhandled error» dopo un upload andato a
     * buon fine — non poteva sapere se il file c'era o no.
     */
    const raw = this.errors[0]?.detail ?? this.errors[0]?.title ?? this.message;
    if (this.status >= 500) {
      // Il traceId rende la segnalazione diagnosticabile senza riprodurre il caso.
      const trace = this.getTraceId();
      const suffix = trace ? ` (rif. ${trace})` : '';
      if (/persist changes|underlying data store/i.test(raw)) {
        return `Il sistema non è riuscito a salvare: è un problema di configurazione del database, non dei dati che hai inserito. Riprovare non aiuta — segnalalo indicando cosa stavi facendo${suffix}.`;
      }
      if (/unhandled error/i.test(raw)) {
        return `Errore interno del sistema durante l’operazione. Non dipende da quello che hai inserito: se si ripete, segnalalo indicando cosa stavi facendo${suffix}.`;
      }
    }
    return raw;
  }

  isAuthError(): boolean {
    return this.status === 401;
  }

  isForbiddenError(): boolean {
    return this.status === 403;
  }

  isValidationError(): boolean {
    return this.status === 422 || this.status === 400;
  }

  isRateLimitError(): boolean {
    return this.status === 429;
  }

  /** 423 Locked: account bloccato/bannato o senza tenant assegnabile (login/change-tenant). */
  isLockError(): boolean {
    return this.status === 423;
  }

  /** Dati grezzi del lock (LockEnd/CrossTenantBanned/TenantId), se il BE li ha forniti. */
  getLockInfo(): LockInfo | null {
    for (const err of this.errors) {
      const lock = err.meta?.lock;
      if (lock && typeof lock === 'object') return lock as LockInfo;
    }
    return null;
  }

  isLicenseError(): boolean {
    const c = this.getApiCode();
    return c != null && (c.startsWith('LICENSE_') || c === 'MISSING_PREREQUISITE' || c === 'QUOTA_EXCEEDED');
  }
}

/**
 * Shape piatta del lock restituita da /account/login e /refresh-token/change-tenant
 * con status 423 (BE v1.8.2+). I campi arrivano in PascalCase sul wire.
 */
export interface LockInfo {
  lockEnd: string | null;
  crossTenantBanned: boolean;
  tenantId: string | null;
}

function formatLockEnd(iso: string): string | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  try {
    return new Date(t).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return new Date(t).toLocaleString();
  }
}

function lockMessage(lock: LockInfo): string {
  if (lock.crossTenantBanned) {
    return 'Il tuo accesso è stato sospeso dall\'amministratore. Contatta il supporto per riattivarla.';
  }
  const until = lock.lockEnd ? formatLockEnd(lock.lockEnd) : null;
  if (until) {
    return `Accesso temporaneamente bloccato fino al ${until}. Riprova più tardi o contatta l'amministratore.`;
  }
  return 'Accesso non disponibile: il tuo account non ha aziende attive associate. Contatta l\'amministratore.';
}

/**
 * Endpoint che rifiutano con un **testo e nessun `code`**: qui il testo diventa il codice, così il
 * messaggio per l'utente esce da `API_ERROR_MESSAGES` come per ogni altro errore.
 *
 * Si aggiunge una riga qui solo quando il testo del server è una **sentinella stabile** — scritta
 * nel controller, non composta a runtime — e quando lasciarlo passare significherebbe mostrare
 * all'utente una frase tecnica in inglese. Fuori da questi due casi vale la regola generale: il
 * `code` lo dichiara il BE.
 */
const CODES_FROM_TEXT: ReadonlyArray<readonly [RegExp, string]> = [
  // `RefreshTokenController` (change-tenant): `{ "Error": "Association with this tenant not existing." }`.
  [/association with this tenant not existing/i, 'TENANT_ENTRY_DENIED'],
];

function codeFromText(msg: string): string | undefined {
  return CODES_FROM_TEXT.find(([re]) => re.test(msg))?.[1];
}

/** Un giorno che l'invio di una pianificazione di smart working ha bloccato. */
export interface PlanningBlockedRow {
  requestId: string;
  /** `AAAA-MM-GG`. */
  date: string;
  /** Il giorno detto come lo si legge: «mer 4/11». */
  dateLabel: string;
  /** Il motivo, con le parole della richiesta singola. */
  message: string;
}

/**
 * I giorni bloccati di `SMART_WORKING_PLANNING_ROWS_BLOCKED`, dal `meta` dell'errore. Il codice di
 * ogni giorno è quello della richiesta singola, quindi il motivo è il messaggio che c'è già; se non
 * lo conosciamo resta il testo del sistema, e solo in fondo una frase che non lo tace.
 */
export function planningBlockedRows(meta: Record<string, unknown> | null | undefined): PlanningBlockedRow[] {
  const results = Array.isArray(meta?.results) ? (meta.results as ErrorMeta[]) : [];
  return results
    .filter((r) => r.outcome === 'blocked')
    .map((r) => {
      const date = str(r.date) ?? '';
      const code = str(r.code);
      const rowMeta = (r.meta && typeof r.meta === 'object' ? r.meta : {}) as ErrorMeta;
      const parsed = /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(`${date}T12:00:00`) : null;
      return {
        requestId: str(r.requestId) ?? '',
        date,
        dateLabel: parsed
          ? parsed.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'numeric' })
          : date,
        message: (code ? messageForCode(code, rowMeta) : null)
          ?? str(r.detail)
          ?? 'non rispetta le regole dello smart working',
      };
    });
}

/** Il testo di un codice senza un errore in mano: serve a spiegare prima del rifiuto. */
export function messageForCode(code: string, meta: Record<string, unknown> = {}): string | null {
  return API_ERROR_MESSAGE_BUILDERS[code]?.(meta) ?? API_ERROR_MESSAGES[code] ?? null;
}

/**
 * Il testo di un **avviso** (`meta.warnings` su una risposta riuscita): la porta unica per tutti
 * i punti che lo mostrano. Fino al 26-10-2026 se ne occupava ogni schermata con
 * `w.message ?? w.code`, e siccome quasi nessun controller scrive `message` — presidio dello
 * smart working, assenze a cavallo d'anno, formato dei rapportini — a schermo finiva il codice.
 *
 * Ordine: il builder dell'avviso coi dati del caso → il testo del sistema → il catalogo degli
 * avvisi → il messaggio dell'errore omonimo → una frase che non lo tace. Un avviso che non
 * sappiamo descrivere si mostra comunque, con il codice come riferimento per chi lo segnala:
 * tacere un segnale che il sistema ha ritenuto di mandare è peggio che mostrarlo grezzo.
 */
export function messageForWarning(w: ApiWarningLike): string {
  const data = warningData(w);
  return API_WARNING_MESSAGE_BUILDERS[w.code]?.(data)
    || w.message?.trim()
    || API_WARNING_MESSAGES[w.code]
    || messageForCode(w.code, data)
    || `L’operazione è riuscita, ma il sistema ha segnalato un’avvertenza che questa schermata non sa ancora descrivere (rif. ${w.code}).`;
}

export function parseErrorsFromResponse(status: number, body: unknown): JsonApiHttpError {
  /**
   * Corpo di **solo codice**: `{ code: "MEDIA_NOT_FOUND" }`.
   *
   * ⚠️ Non e' un'ipotesi: e' la forma che `MediaFilesCustomController.DownloadMediaFile`
   * restituisce per tutti e quattro i suoi esiti negativi (`MEDIA_NOT_FOUND`,
   * `MEDIA_CONTENT_EMPTY`, `MEDIA_CONTENT_UNREADABLE`, `MEDIA_STORAGE_UNAVAILABLE`) —
   * `return NotFound(new { code = "…" })`, senza `error`, senza `message`, senza `title`.
   *
   * Senza questa riga quel corpo **non corrisponde a nessun ramo** e finisce nel ripiego per
   * status: l'utente leggeva «Risorsa non trovata» e i tre messaggi che il catalogo aveva
   * gia' scritto per quei codici non venivano raggiunti da nessuno. E' il modo in cui un
   * catalogo d'errori sembra completo e non parla: le voci ci sono, la strada che le raggiunge
   * no.
   */
  const codeOnly = body && typeof body === 'object' && !Array.isArray(body)
    && typeof (body as Record<string, unknown>).code === 'string'
    && (body as Record<string, unknown>).code
    ? String((body as Record<string, unknown>).code)
    : null;

  if (body && typeof body === 'object') {
    const o = body as Record<string, unknown>;

    // JSON:API standard: { errors: [{ title, detail, ... }] }
    if (Array.isArray(o.errors)) {
      return new JsonApiHttpError(status, o.errors as JsonApiError[]);
    }

    // ASP.NET Core ProblemDetails: { errors: { field: ["msg1", "msg2"] }, title }
    if (o.errors && typeof o.errors === 'object') {
      const flat: JsonApiError[] = [];
      for (const [field, msgs] of Object.entries(o.errors as Record<string, unknown>)) {
        const arr = Array.isArray(msgs) ? (msgs as string[]) : [String(msgs)];
        for (const m of arr) {
          flat.push({ status: String(status), title: 'Errore di validazione', detail: m, source: { pointer: `/data/attributes/${field}` } });
        }
      }
      if (flat.length > 0) return new JsonApiHttpError(status, flat);
    }

    /**
     * Contratto arricchito dei **controller custom** (BE `API-ERROR-ENRICHMENT-v1`):
     * `{ error: { code, detail, hint, traceId } }`. Nota che `error` qui è un **oggetto**,
     * non la stringa della forma storica gestita subito sotto — senza questo ramo cadrebbe
     * nel generico e perderemmo code, hint e traceId, cioè tutto quello che rende l'errore
     * diagnosticabile.
     *
     * `traceId` è sempre presente sui 5xx ed è l'unico aggancio per ritrovare la riga di log
     * completa: lo conserviamo in `meta` e lo mostriamo nei messaggi 5xx.
     */
    const errObj = (o.error && typeof o.error === 'object' && !Array.isArray(o.error))
      ? (o.error as Record<string, unknown>) : null;
    if (errObj && (typeof errObj.code === 'string' || typeof errObj.detail === 'string')) {
      const detail = (typeof errObj.detail === 'string' && errObj.detail) || undefined;
      const hint = (typeof errObj.hint === 'string' && errObj.hint) || undefined;
      const traceId = (typeof errObj.traceId === 'string' && errObj.traceId) || undefined;
      const code = (typeof errObj.code === 'string' && errObj.code) || undefined;
      return new JsonApiHttpError(status, [{
        status: String(status),
        title: detail ?? code ?? `HTTP ${status}`,
        detail,
        code,
        // `detail` anche in meta: i builder tecnici lo compongono con l'hint.
        meta: { code, hint, traceId, detail },
      }]);
    }

    // Custom controller shape: { error: "messaggio" } o { message: "..." }.
    // Alcuni endpoint (es. change-password 409 "password riusata") usano PascalCase.
    // Se sono presenti ENTRAMBI error+message (es. passkey { error: "attestation_failed",
    // message: "..." }), `error` è un codice → il testo umano sta in `message`.
    const errorField = (typeof o.error === 'string' && o.error) || (typeof o.Error === 'string' && o.Error) || null;
    const messageField = (typeof o.message === 'string' && o.message) || (typeof o.Message === 'string' && o.Message) || null;
    const msg = (errorField && messageField ? messageField : errorField)
      || messageField
      || (typeof o.title === 'string' && o.title);
    if (msg) {
      // Codice applicativo parallelo ({ error, code } — es. ErpSite DenyCode) o l'error
      // stesso quando funge da codice: allegato all'errore così getUserMessage() può
      // mappare API_ERROR_MESSAGES → messaggio italiano.
      const code = (typeof o.code === 'string' && o.code)
        || (errorField && messageField ? errorField : undefined)
        || codeFromText(msg)
        || undefined;
      return new JsonApiHttpError(status, [{ status: String(status), title: msg, detail: msg, code }]);
    }

    // BE 401 da /account/login: { AttemptsRemaining, AccountLocked, LockExpiresIn }.
    // Sintetizza un messaggio con il contatore tentativi / la scadenza del lockout.
    if (status === 401 && ('AttemptsRemaining' in o || 'AccountLocked' in o)) {
      const attempts = typeof o.AttemptsRemaining === 'number' ? o.AttemptsRemaining : null;
      const locked = o.AccountLocked === true;
      const lockExpires = typeof o.LockExpiresIn === 'string' && o.LockExpiresIn ? o.LockExpiresIn : null;
      let detail: string;
      if (locked) {
        const until = lockExpires ? formatLockEnd(lockExpires) : null;
        detail = until
          ? `Account bloccato per troppi tentativi falliti. Riprova dopo le ${until}.`
          : 'Account bloccato per troppi tentativi falliti. Riprova più tardi.';
      } else {
        detail = attempts != null && attempts > 0
          ? `Credenziali non valide. Tentativi rimanenti: ${attempts}.`
          : 'Credenziali non valide.';
      }
      return new JsonApiHttpError(status, [{
        status: String(status), title: 'Accesso negato', detail,
        meta: { attemptsRemaining: attempts, accountLocked: locked, lockExpiresIn: lockExpires },
      }]);
    }

    // BE 423 lock shape (login/change-tenant): { LockEnd, CrossTenantBanned, TenantId }.
    // Body senza error/message → sintetizziamo un messaggio leggibile e conserviamo
    // i campi grezzi in meta.lock per eventuale UI dedicata (getLockInfo()).
    if (status === 423 && ('LockEnd' in o || 'CrossTenantBanned' in o || 'lockEnd' in o || 'crossTenantBanned' in o)) {
      const lock: LockInfo = {
        lockEnd: (typeof o.LockEnd === 'string' && o.LockEnd) || (typeof o.lockEnd === 'string' && o.lockEnd) || null,
        crossTenantBanned: o.CrossTenantBanned === true || o.crossTenantBanned === true,
        tenantId: (typeof o.TenantId === 'string' && o.TenantId) || (typeof o.tenantId === 'string' && o.tenantId) || null,
      };
      const detail = lockMessage(lock);
      return new JsonApiHttpError(status, [{ status: String(status), title: 'Accesso bloccato', detail, meta: { lock } }]);
    }
  }
  // Body stringa semplice (es. NotFound("User not found")): usala come dettaglio.
  if (typeof body === 'string' && body.trim()) {
    return new JsonApiHttpError(status, [{ status: String(status), title: body.trim() }]);
  }
  // Fallback senza body leggibile: per gli status più comuni del BE v1.9 (authz
  // per-dipendente sui moduli verticali, approvazioni idempotenti) un messaggio sensato.
  const FALLBACK_BY_STATUS: Record<number, string> = {
    400: 'Richiesta non valida o già processata.',
    401: 'Credenziali non valide o sessione scaduta.',
    403: 'Non hai i permessi per questa operazione.',
    404: 'Risorsa non trovata.',
    // Workflow custom account/user-profiles (BE): status usati come flag semantici a body vuoto.
    406: 'Operazione non accettata nello stato corrente.',
    409: 'Operazione in conflitto con lo stato attuale della risorsa.',
    411: 'Dati richiesti mancanti.',
    412: 'Precondizione non soddisfatta per questa operazione.',
    416: 'Valore fuori dall\'intervallo consentito.',
    417: 'Operazione non riuscita: verifica i dati inseriti.',
    // BE: /account/login → 418 = email non ancora confermata (solo endpoint account).
    418: 'Email non ancora confermata. Controlla la tua casella di posta e completa la registrazione.',
    422: 'Dati non validi: impossibile elaborare la richiesta.',
    423: 'Accesso bloccato. Riprova più tardi o contatta l\'amministratore.',
    // BE (post-v1.9): workflow POST /api/user-profiles → 424 = uno dei ruoli passati
    // nell'header `roles` non è risolvibile (GUID inesistente o di un altro tenant).
    424: 'Uno dei ruoli indicati non è valido o non appartiene all’azienda selezionata.',
    428: 'Manca un passaggio preliminare per completare l\'operazione.',
    429: 'Troppe richieste: attendi qualche secondo e riprova.',
    451: 'Operazione non disponibile: consensi o termini legali non accettati.',
  };
  const fallback = FALLBACK_BY_STATUS[status]
    ?? (status >= 500 ? 'Errore del sistema. Riprova tra qualche istante.' : `HTTP ${status}`);
  /* Il `title` resta il testo per status, che e' cio' che si legge quando il codice non e' in
     catalogo: un `MEDIA_CONTENT_UNREADABLE` a schermo non aiuta nessuno. Il codice viaggia
     accanto, ed e' quello che `getUserMessage()` cerca per primo. */
  return new JsonApiHttpError(status, [{
    status: String(status),
    title: fallback,
    code: codeOnly ?? undefined,
    meta: codeOnly ? { code: codeOnly } : undefined,
  }]);
}
